/* ============================================================================
   TurnOrder — MOTORUL COMUN AL CONVERSAȚIILOR (chat club + mesaje private)
   ----------------------------------------------------------------------------
   RO: Modulele de chat v9 (8.13, decizia D1: un motor comun + două adaptoare,
       club și DM). Script CLASIC — fără import/export; partajează scopul global
       cu restul aplicației. Se încarcă DUPĂ `block.js` (deci și după
       `supabase-integration.js` și `social.js`), ca orice cod de chat mutat aici
       să rămână versiunea care rulează (§4.A, §5) și să găsească gata API-urile
       pe care le folosește (`sendMessage`, `isBlockedPair`, `openDM`).
       Codul NOU de chat se scrie aici; codul VECHI se mută doar în batch-uri de
       MUTARE dedicate, separate de modificări.

       Conține (actualizat 28 oct. 2026, 8.13-3): câmpul de scriere (`composer`),
       meniul mesajului, „înapoi" pe straturi, zilele și necititele, desenarea rând
       cu rând (`patchRows`, club și DM), paginarea, căutarea, trimiterea cu stare
       (`send`), urmele șterse, încărcătorul fișierelor la cerere (`ensureModule`)
       și punțile spre ele (emoji, reacții, redirecționare, linkuri, aspect…).
       Inventarul complet: KB §74.1 și secțiunile sesiunilor (§78–§81).
       ⚠ OBLIGATORIU din 8.13-3 (decizia lui Alex): căile vechi de trimitere și de
       ștergere fără el au fost scoase.

   EN: Shared conversation engine (club chat + DMs). Classic script, loaded after
       block.js so any chat code moved here stays the running version. Holds the
       composer, message menu, back-stack, row patching, sending, deleted traces and
       the lazy-module loader. Required since 8.13-3 (old fallback paths removed).
   ============================================================================ */
(function () {
    'use strict';
    /* RO: gardă — dacă scriptul ar fi inclus de două ori, a doua încărcare nu
           șterge ce a înregistrat prima.
       EN: guard — a second inclusion must not wipe what the first registered. */
    if (window.TO_CHAT) return;
    var TO_CHAT = window.TO_CHAT = {};

    /* ========================================================================
       COMPOSER — câmpul de scriere care crește (8.1bis)
       ------------------------------------------------------------------------
       RO: Pe un singur rând, într-un câmp îngust, un mesaj mai lung era greu de
           scris și de recitit. Câmpul crește acum odată cu textul, ca în
           WhatsApp, până la o limită, apoi derulează în interior.
           • Limita: 6 rânduri pe dispozitivele de atingere, 10 pe desktop, dar
             NICIODATĂ peste 40% din înălțimea VIZIBILĂ (pe un telefon mic, cu
             tastatura deschisă, rămân ~250 px ⇒ ~100 px pentru câmp).
           • Creșterea: nativ (`field-sizing: content` — Chrome/Edge 123+, Firefox
             152+, Safari 26.2+); pe browserele mai vechi (ex. iPhone cu iOS sub
             26.2) o măsurăm noi, o singură dată pe cadru (`requestAnimationFrame`).
           • Pe un singur rând câmpul are EXACT înălțimea vechiului `<input>`
             (măsurat: 55,2 px la 375 px, 46,4 px pe desktop): păstrăm
             `line-height: normal`, iar alinierea butoanelor jos se schimbă doar
             de la al doilea rând (clasa `ci-multi`).
       EN: The message field grows with the text up to 6 rows (touch) / 10 rows
           (desktop), capped at 40% of the visible height, then scrolls inside.
           Native `field-sizing: content` where supported, a one-per-frame JS
           measurement elsewhere. One row stays pixel-identical to the old input.
       ======================================================================== */
    var ROWS_TOUCH = 6;
    var ROWS_DESK = 10;
    var MAX_VISIBLE = 0.4;
    /* Space Mono cu `line-height: normal`: 23,2 px la 16 px (măsurat) ⇒ 1,45.
       Folosit doar până când rândul real e măsurat (prima afișare a câmpului). */
    var LH_RATIO = 1.45;
    /* „Ești la ultimul mesaj" = la mai puțin de atâția pixeli de capăt. */
    var PIN_SLACK = 24;

    var supportsFieldSizing = false;
    try { supportsFieldSizing = !!(window.CSS && CSS.supports && CSS.supports('field-sizing', 'content')); } catch (e) { /* noop */ }

    /* D14: dispozitivele al căror mod PRINCIPAL de intrare e atingerea (telefoane,
       tablete) ⇒ Enter = rând nou. Un laptop cu ecran tactil raportează `fine`
       (mouse/touchpad) ⇒ Enter trimite (§34: un laptop cu touchscreen nu e telefon).
       Detecție de capabilitate, nu de browser. */
    var mqTouch = null;
    try { mqTouch = window.matchMedia ? window.matchMedia('(pointer: coarse)') : null; } catch (e) { /* noop */ }
    function isTouchPrimary() { return !!(mqTouch && mqTouch.matches); }

    var TA_VALUE = null;
    try { TA_VALUE = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value'); } catch (e) { /* noop */ }

    var attached = [];

    function logErr(where, e) {
        try { if (typeof window.logError === 'function') window.logError(where, e); } catch (e2) { /* noop */ }
    }
    function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
    function padV(cs) { return num(cs.paddingTop) + num(cs.paddingBottom); }
    function borderV(cs) { return num(cs.borderTopWidth) + num(cs.borderBottomWidth); }
    function visibleHeight() {
        var vv = window.visualViewport;
        return (vv && vv.height) || window.innerHeight || 0;
    }

    /* Înălțimea unui rând de text. Măsurată la prima afișare cu câmpul gol; până
       atunci (sau după ce se schimbă fontul — ex. fereastra trece sub 768 px și
       fontul urcă de la 15 la 16 px) o estimăm din font. */
    function lineHeight(st, cs) {
        var fpx = num(cs.fontSize);
        if (fpx !== st.fontPx) { st.fontPx = fpx; st.lineH = 0; }
        return st.lineH || fpx * LH_RATIO || 22;
    }

    /* Câmp gol și vizibil = exact un rând ⇒ înălțimea REALĂ a rândului (pe ecranele
       cu densitate fracționară se rotunjește la pixeli de dispozitiv: 22,4 px, nu
       21,75 cât dă estimarea). Întoarce 0 dacă nu se poate măsura acum. */
    function measureLine(st, cs, h) {
        var el = st.el;
        cs = cs || getComputedStyle(el);
        lineHeight(st, cs);                       /* resetează măsura dacă s-a schimbat fontul */
        if (st.lineH) return st.lineH;
        if (el.value) return 0;
        h = h || el.getBoundingClientRect().height;
        var inner = h - padV(cs) - borderV(cs);
        if (!(inner > 0)) return 0;
        st.lineH = inner;
        applyLimits(st);
        return inner;
    }

    /* Limita de înălțime + tasta Enter de pe tastatura de pe ecran. Rulează la
       atașare și când se schimbă spațiul vizibil (tastatura, rotirea ecranului). */
    function applyLimits(st) {
        var el = st.el;
        var cs = getComputedStyle(el);
        var lh = lineHeight(st, cs);
        var one = lh + padV(cs) + borderV(cs);                 /* înălțimea unui rând */
        var rows = isTouchPrimary() ? ROWS_TOUCH : ROWS_DESK;
        var byRows = rows * lh + padV(cs) + borderV(cs);
        var byScreen = Math.floor(visibleHeight() * MAX_VISIBLE) || byRows;
        /* Niciodată sub un rând: pe un ecran foarte mic limita de 40% ar putea
           coborî sub înălțimea unui singur rând. */
        el.style.maxHeight = Math.round(Math.max(one, Math.min(byRows, byScreen))) + 'px';
        /* Pentru alinierea butoanelor pe ultimul rând (chat.css, `.ci-multi`). */
        var host = st.area || st.wrap;
        if (host) host.style.setProperty('--ci-h1', one.toFixed(1) + 'px');
        el.setAttribute('enterkeyhint', isTouchPrimary() ? 'enter' : 'send');
    }

    /* Varianta JS (fără `field-sizing`): înălțimea = conținutul, plafonată.
       ⚠ `height = auto` urmat de citirea `scrollHeight` e o măsurare forțată, de
         aceea rulează o singură dată pe cadru (vezi `schedule`), nu la fiecare tastă. */
    function fitJs(st) {
        var el = st.el;
        el.style.height = 'auto';
        /* Încape pe un rând ⇒ lăsăm înălțimea naturală (identică cu vechiul
           `<input>`); `scrollHeight` e rotunjit la pixeli întregi (55 vs 55,2). */
        if (el.scrollHeight <= el.clientHeight) { el.style.height = ''; return; }
        var cs = getComputedStyle(el);
        var want = el.scrollHeight + borderV(cs);   /* `* { box-sizing: border-box }` (style.css) */
        var max = num(el.style.maxHeight) || want;
        el.style.height = Math.min(want, max) + 'px';
    }
    function schedule(st) {
        if (st.fs || st.raf) return;
        st.raf = requestAnimationFrame(function () {
            st.raf = 0;
            try { fitJs(st); } catch (e) { logErr('TO_CHAT.fit', e); }
        });
    }

    /* Varianta JS: valoarea pusă din COD (golirea după trimitere, textul pus la loc
       după o eroare, emoji-ul adăugat) nu produce evenimentul `input`, deci câmpul
       ar rămâne la înălțimea veche. Interceptăm scrierea valorii DOAR pe acest
       câmp, fără să atingem `sendMessage` sau selectorul de emoji.
       EN: programmatic value writes fire no `input` event; re-measure on them. */
    function hookValue(st) {
        if (st.hooked || !TA_VALUE || !TA_VALUE.set) return;
        st.hooked = true;
        Object.defineProperty(st.el, 'value', {
            configurable: true,
            get: function () { return TA_VALUE.get.call(this); },
            set: function (v) { TA_VALUE.set.call(this, v); schedule(st); liveUpdate(st); draftNote(st); }
        });
    }

    /* Rândurile goale consecutive se reduc la UNUL (cel mult o linie goală între
       paragrafe), ca un mesaj să nu poată ocupa un ecran întreg cu spațiu gol.
       Spațiile de la capete le taie deja funcția de trimitere. */
    function normalize(el) {
        var v = el.value;
        var n = v.replace(/(?:[ \t]*\n){3,}/g, '\n\n');
        if (n !== v) el.value = n;
    }
    function doSend(st) {
        normalize(st.el);
        try { st.send(); } catch (e) { logErr('TO_CHAT.send', e); }
    }

    /* D14 — tasta Enter:
         • telefon / tabletă: Enter = rând nou; trimiterea, din butonul ➤;
         • desktop / laptop: Enter = trimite, Shift+Enter = rând nou;
         • Ctrl/Cmd+Enter trimite peste tot (scăparea pentru tableta cu tastatură
           fizică, care nu poate fi detectată sigur).
       ⛔ În timpul compunerii (autocorectare, diacritice, limbi asiatice) Enter
          confirmă cuvântul și NU trimite — altfel pleacă mesaje pe jumătate.
          `keyCode 229` acoperă browserele care nu setează `isComposing`. */
    function onKeydown(st, e) {
        if (e.key !== 'Enter') return;
        if (e.isComposing || e.keyCode === 229) return;
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); doSend(st); return; }
        if (e.shiftKey || e.altKey) return;
        if (isTouchPrimary()) return;
        e.preventDefault();
        doSend(st);
    }

    /* Creșterea câmpului micșorează lista de mesaje. Dacă erai la ultimul mesaj,
       rămâi la el; dacă citeai mai sus, poziția nu se schimbă (browserul păstrează
       derularea de sus). Tot aici se marchează trecerea pe mai multe rânduri. */
    function onResize(st) {
        var el = st.el;
        /* `getBoundingClientRect`, nu `offsetHeight`: al doilea rotunjește la pixeli
           întregi (55 în loc de 55,2), iar eroarea se înmulțea cu numărul de rânduri. */
        var h = el.getBoundingClientRect().height;
        if (st.liveOn) { try { liveGeom(st); } catch (e) { logErr('TO_CHAT.live', e); } }   /* 8.1-g: oglinda urmează câmpul */
        if (!h || Math.abs(h - st.lastH) < 0.5) return;
        var prevH = st.lastH;
        st.lastH = h;
        var cs = getComputedStyle(el);
        var inner = h - padV(cs) - borderV(cs);
        var lh = measureLine(st, cs, h) || lineHeight(st, cs);
        var multi = Math.round(inner / lh) >= 2;
        if (st.wrap) st.wrap.classList.toggle('ci-multi', multi);
        if (st.area) st.area.classList.toggle('ci-multi', multi);
        /* Lista s-a micșorat exact cu cât a crescut câmpul, iar derularea de sus a
           rămas pe loc. Deci distanța până la capăt de DINAINTE = distanța de acum
           minus creșterea. ⚠ NU o memorăm din evenimentul `scroll`: acela sosește
           abia la cadrul următor, DUPĂ creștere, și ar raporta „nu ești la capăt"
           (prins la test: lista rămânea cu 69 px deasupra ultimului mesaj). */
        if (st.list && prevH) {
            var l = st.list;
            var gapNow = l.scrollHeight - l.scrollTop - l.clientHeight;
            if (gapNow - (h - prevH) < PIN_SLACK) l.scrollTop = l.scrollHeight;
        }
    }

    function attach(el, opts) {
        if (!el || el.__toComposer || el.tagName !== 'TEXTAREA') return;
        opts = opts || {};
        var st = {
            el: el,
            send: opts.send || function () {},
            list: opts.list || null,
            area: el.closest ? el.closest('.chat-input-area') : null,
            wrap: el.closest ? el.closest('.chat-input-wrap') : null,
            fs: supportsFieldSizing,
            raf: 0, lastH: 0, lineH: 0, fontPx: 0, hooked: false
        };
        el.__toComposer = st;
        if (st.fs) el.classList.add('ci-fs');
        /* 8.1-h: bula „Răspunde lui…" intră ÎN capsula câmpului, deasupra textului, ca
           în WhatsApp (înainte era o bară separată, pe toată lățimea chatului). Doar o
           mutăm: funcțiile care o desenează o caută după id, deci nu se schimbă nimic
           altundeva. Fără chat-core.js rămâne ca înainte. EN: the reply preview moves
           inside the composer pill; renderers find it by id, so nothing else changes. */
        try {
            var rb = (st.wrap && st.area && st.area.parentNode) ? st.area.parentNode.querySelector(':scope > .chat-reply-bar') : null;
            if (rb) st.wrap.insertBefore(rb, st.wrap.firstChild);
        } catch (e) { logErr('TO_CHAT.replyBar', e); }
        /* 8.1-g: interceptarea valorii puse din cod rulează acum peste tot (nu doar la
           varianta JS de creștere): golirea după trimitere, emoji-ul sau textul pus la loc
           trebuie să actualizeze și formatarea vizibilă din câmp. */
        hookValue(st);

        /* 8.1-f: întâi lista @ (Enter / Tab / ↑ ↓ / Esc cât e deschisă), apoi
           scurtăturile de formatare, apoi Enter-ul obișnuit (D14). */
        el.addEventListener('keydown', function (e) {
            try {
                if (st.mentions && mentionKey(e)) return;
                if (fmtKey(st, e)) return;
            } catch (e2) { logErr('TO_CHAT.keys', e2); }
            onKeydown(st, e);
        });
        /* La atingerea câmpului (înainte de prima literă) măsurăm rândul real, ca
           limita de 6/10 rânduri să fie exactă din primul mesaj. */
        el.addEventListener('focus', function () {
            try { measureLine(st); } catch (e) { logErr('TO_CHAT.focus', e); }
        });
        el.addEventListener('input', function (e) {
            schedule(st);
            liveUpdate(st);                                   /* 8.1-g: formatarea din câmp */
            draftNote(st);                                    /* 8.1-h: ciorna conversației */
            linkNote(st);                                     /* 8.6: cardul linkului (chat-link.js, la cerere) */
            /* În timpul compunerii (tastaturile de telefon) așteptăm `compositionend`. */
            if (st.mentions && !(e && e.isComposing)) mentionUpdate(st);
        });
        if (opts.mentions) {
            st.mentions = true;
            el.addEventListener('compositionend', function () { mentionUpdate(st); });
            el.addEventListener('click', function () { mentionUpdate(st); });
            el.addEventListener('keyup', function (e) { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') mentionUpdate(st); });
            el.addEventListener('blur', function () { mentionClose(); });
        }
        /* Butonul ➤ trimite prin delegare, pe faza de bubbling (club: `chat-send` pe card,
           8.13-1; DM: `dm-send` pe fereastră, 8.13-2); normalizăm textul pe faza de CAPTURĂ
           a zonei, deci ÎNAINTE de trimitere. */
        if (st.area) {
            st.area.addEventListener('click', function (e) {
                var b = e.target && e.target.closest ? e.target.closest('.chat-send-btn') : null;
                if (b) normalize(el);
            }, true);
        }
        if (window.ResizeObserver) {
            try {
                var ro = new ResizeObserver(function () { onResize(st); });
                ro.observe(el);
                /* 8.1-h: bula de răspuns din capsulă mută câmpul în jos fără să-i schimbe
                   mărimea ⇒ observăm și capsula, ca oglinda formatării (8.1-g) să-l urmeze. */
                if (st.wrap) ro.observe(st.wrap);
            } catch (e) { logErr('TO_CHAT.attach', e); }
        }
        /* După observatorul câmpului (ordinea contează: rulează în ordinea creării). */
        if (st.list) bindAnchor(st.list);
        if (st.list) bindScrollDown(st.list);
        if (st.list) bindDayFloat(st.list);     /* 8.1-e: data plutitoare la derulare */
        if (st.list) bindSwipe(st.list);        /* 8.1-g: tragi de mesaj ⇒ răspuns */
        if (st.list) bindNoTouchSelect(st.list); /* 8.8-c: apăsarea lungă nu selectează text */
        if (st.list) bindLinkCards(st.list);    /* 8.6: atingerea cardului din bulă deschide linkul */
        applyLimits(st);
        schedule(st);
        attached.push(st);
        draftSwap(st);                          /* 8.1-h: ciorna acestei conversații */
        if (el.id === 'dmInput') { cprefApply(); cprefHold(openDmModal()); cprefSyncSoon(); }   /* 8.8: aspectul DM-ului (8.3-c: + așteptarea scurtă) */
    }

    /* ── Lista rămâne „ancorată" jos când își schimbă înălțimea (8.1-b3) ──────
       RO: Simptom (Samsung): la deschiderea tastaturii, câmpul și ➤ se vedeau,
           dar ultimele mesaje rămâneau ACOPERITE ⇒ trebuia derulat până jos. Cauza:
           lista se micșora, iar derularea de sus (`scrollTop`) rămânea pe loc.
           Ca în WhatsApp, păstrăm distanța până la CAPĂTUL listei: ce era lipit jos
           rămâne lipit jos (tastatură, rotire, ecran complet), iar dacă citeai mai
           sus, rămâi la același mesaj de la marginea de jos.
           • Distanța de DINAINTE se calculează cu înălțimea veche (`lastH`): la
             micșorare (tastatura se deschide) `scrollTop` e încă cel vechi.
           • La MĂRIRE (tastatura se închide) browserul taie `scrollTop` la noul
             maxim dacă erai la mai puțin de „creștere" de capăt; atunci `scrollTop`
             nu mai spune unde erai. ⚠ 8.1-b3 calcula și aici din `scrollTop` și
             obținea „creșterea" în loc de 0 ⇒ la închiderea tastaturii lista urca
             exact cu înălțimea ei (raportat de Alex pe Samsung). Acum, în acest
             caz, folosim ULTIMA distanță cunoscută (`lastGap`, actualizată la
             fiecare derulare): 0 ⇒ rămâi la ultimul mesaj; altfel rămâi la mesajele
             la care derulaseși (prins la test: 300 px de capăt, tastatura 332 px).
           • `lastGap` se actualizează doar când înălțimea e cea cunoscută: un
             eveniment `scroll` produs chiar de tăiere (înaintea observatorului) nu
             are voie s-o suprascrie.
           • Lista ascunsă (înălțime 0, ex. alt tab) nu schimbă nimic.
           • Fără ResizeObserver (browsere foarte vechi): comportamentul de azi.
       EN: keep the list anchored to its bottom when its height changes. */
    /* ── Butonul ↓ „la ultimele mesaje" (8.1-c2a, club + DM) ───────────────────
       RO: Ca în WhatsApp: mic, rotund, cu fundal semitransparent, deasupra
           butonului ➤ (centrat pe aceeași verticală). Apare DOAR când ai derulat
           în sus (mai mult de 160 px de capăt) și te duce direct la ultimul mesaj,
           FĂRĂ animație de derulare (cerință).
           • E pus în părintele listei (`.chat-container` / panoul DM), nu în listă:
             lista se redesenează (`innerHTML`) și l-ar șterge.
           • Înălțimea lui (`bottom`) urmează marginea de jos a listei, deci stă
             mereu chiar deasupra zonei de scriere, oricât crește câmpul sau bara
             de răspuns.
           • Starea se recalculează la derulare și la redimensionare, o dată pe
             cadru (rAF), cu ascultători pasivi (§54.4).
       EN: small translucent "jump to latest" button above the send button. */
    var DOWN_MIN_GAP = 160;
    function bindScrollDown(list) {
        if (!list || list.__toDown || !list.parentNode) return;
        list.__toDown = true;
        var host = list.parentNode;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chat-down';
        btn.hidden = true;
        var lbl = tr('chat.toLatest', 'Go to latest messages');
        btn.title = lbl;
        btn.setAttribute('aria-label', lbl);
        btn.innerHTML = '<i class="fas fa-chevron-down" aria-hidden="true"></i><span class="chat-down-badge" hidden></span>';
        host.appendChild(btn);
        var badge = btn.querySelector('.chat-down-badge');
        /* 8.1-c2b: câte mesaje noi (de la alții) au sosit cât citeai mai sus — ca în
           WhatsApp, numărul stă pe buton; se golește când ajungi la capăt. */
        var fresh = 0;
        var paint = function () {
            var has = fresh > 0;
            if (badge.hidden === has) badge.hidden = !has;
            if (has) badge.textContent = fresh > 99 ? '99+' : String(fresh);
            var l2 = has ? lbl + ' (' + newLabel(fresh) + ')' : lbl;
            btn.title = l2;
            btn.setAttribute('aria-label', l2);
        };
        var raf = 0;
        var sync = function () {
            raf = 0;
            if (!list.isConnected) return;
            var gap = list.scrollHeight - list.scrollTop - list.clientHeight;
            var atEnd = gap < 40;
            if (atEnd && fresh) { fresh = 0; paint(); }
            /* 8.1-c2b: bara „N mesaje necitite" dispare după ce ai derulat prin ele
               până la capăt (cerința lui Alex). Doar dacă, de la apariția ei, ai fost
               măcar o dată departe de capăt: dacă necititele încăpeau pe ecran, nu
               s-a derulat nimic și bara rămâne până ieși din conversație. */
            var bar = list.querySelector('.chat-unread');
            if (bar) {
                if (!atEnd) list.__toUnreadArmed = true;
                else if (list.__toUnreadArmed) {
                    list.__toUnreadArmed = false;
                    if (bar.parentNode) bar.parentNode.removeChild(bar);
                    if (typeof list.__toUnreadSeen === 'function') { try { list.__toUnreadSeen(); } catch (e) { logErr('TO_CHAT.unreadSeen', e); } }
                }
            } else list.__toUnreadArmed = false;
            var show = list.clientHeight > 0 && (gap > DOWN_MIN_GAP || (fresh > 0 && !atEnd));
            if (show) {
                var hb = host.getBoundingClientRect().bottom, lb = list.getBoundingClientRect().bottom;
                btn.style.bottom = Math.max(8, Math.round(hb - lb + 10)) + 'px';
            }
            if (btn.hidden === show) btn.hidden = !show;
        };
        var later = function () { if (!raf) raf = requestAnimationFrame(sync); };
        list.addEventListener('scroll', later, { passive: true });
        if (window.ResizeObserver) { try { new ResizeObserver(later).observe(list); } catch (e) { logErr('TO_CHAT.down', e); } }
        btn.addEventListener('click', function () {
            list.scrollTop = list.scrollHeight;     /* direct, fără `smooth` */
            btn.hidden = true;
            fresh = 0; paint();
        });
        list.__toDownApi = { add: function (n) { fresh += n; paint(); later(); } };
    }

    /* ── Data „plutitoare" la derulare (8.1-e, E5; club + DM) ────────────────────
       RO: Ca în WhatsApp: cât derulezi, ziua mesajelor din capul listei („Azi",
           „Ieri", „12 octombrie") apare într-o capsulă sus, chiar dacă separatorul
           ei a ieșit din ecran; dispare la 1,5 s după ce te oprești. Capsula stă în
           afara listei (ca „Se încarcă mesajele mai vechi…"), deci nu mută rândurile
           și nu încurcă `trimTop` / `prependTop`. Ziua o citim din separatoarele
           deja desenate (`.chat-day`), deci e mereu aceeași cu a lor.
           • Se ascunde când separatorul real urmează să-i ia locul (sub 34 px).
           • O dată pe cadru (rAF), ascultător pasiv; doar citiri, apoi scrieri.
       EN: floating day pill while scrolling, read from the rendered separators. */
    var DAY_FLOAT_MS = 1500;
    function bindDayFloat(list) {
        if (!list || list.__toDayFloat || !list.parentNode) return;
        list.__toDayFloat = true;
        var host = list.parentNode;
        var pill = document.createElement('div');
        pill.className = 'chat-day-float';
        pill.setAttribute('aria-hidden', 'true');
        pill.innerHTML = '<span></span>';
        host.appendChild(pill);
        var txt = pill.firstChild, raf = 0, timer = 0, on = false;
        var hide = function () { timer = 0; if (on) { on = false; pill.classList.remove('is-on'); } };
        var sync = function () {
            raf = 0;
            try {
                if (!list.isConnected || !list.clientHeight) return;
                var top = list.getBoundingClientRect().top, cur = null, next = null;
                var seps = list.querySelectorAll(':scope > .chat-day');
                for (var i = 0; i < seps.length; i++) {
                    var r = seps[i].getBoundingClientRect();
                    if (r.top < top) cur = seps[i]; else { next = r; break; }
                }
                var label = cur ? cur.textContent : '';
                var show = !!label && !(next && next.top - top < 34);
                var y = list.offsetTop + 8;
                if (show) {
                    if (txt.textContent !== label) txt.textContent = label;
                    pill.style.top = y + 'px';
                    if (!on) { on = true; pill.classList.add('is-on'); }
                } else hide();
                if (timer) clearTimeout(timer);
                timer = setTimeout(hide, DAY_FLOAT_MS);
            } catch (e) { logErr('TO_CHAT.dayFloat', e); }
        };
        list.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true });
    }

    function newLabel(n) {
        n = +n || 0;
        var ro = (typeof window.appLangCode === 'function') && window.appLangCode() === 'ro';
        var key = n === 1 ? 'chat.new1' : ((ro && (n % 100 === 0 || n % 100 >= 20)) ? 'chat.newNde' : 'chat.newN');
        return tr(key, n === 1 ? '1 new message' : '{n} new messages').replace('{n}', String(n));
    }

    function bindAnchor(list) {
        if (!list || list.__toAnchor || !window.ResizeObserver) return;
        list.__toAnchor = true;
        var lastH = list.clientHeight;
        var lastGap = 0;
        var gapNow = function () { return Math.max(0, list.scrollHeight - list.scrollTop - list.clientHeight); };
        list.addEventListener('scroll', function () {
            if (list.clientHeight === lastH) lastGap = gapNow();
        }, { passive: true });
        try {
            new ResizeObserver(function () {
                var h = list.clientHeight;
                if (!h) return;
                if (!lastH || h === lastH) { lastH = h; lastGap = gapNow(); return; }
                var clipped = h > lastH && list.scrollHeight - list.scrollTop - h < 1;   /* mărire tăiată */
                var gap = clipped ? lastGap : Math.max(0, list.scrollHeight - list.scrollTop - lastH);
                lastH = h;
                list.scrollTop = list.scrollHeight - h - gap;
                lastGap = gapNow();
            }).observe(list);
        } catch (e) { logErr('TO_CHAT.anchor', e); }
    }

    /* Spațiul vizibil se schimbă la deschiderea tastaturii și la rotire ⇒ limita
       de 40% se recalculează. Câmpurile scoase din pagină (ex. conversația privată
       închisă) ies din listă. */
    var refreshRaf = 0;
    function refreshAll() {
        if (refreshRaf) return;
        refreshRaf = requestAnimationFrame(function () {
            refreshRaf = 0;
            attached = attached.filter(function (st) { return st.el.isConnected; });
            attached.forEach(function (st) {
                try { applyLimits(st); schedule(st); } catch (e) { logErr('TO_CHAT.refresh', e); }
            });
        });
    }
    try {
        if (window.visualViewport) window.visualViewport.addEventListener('resize', refreshAll);
        window.addEventListener('resize', refreshAll);
        if (mqTouch) {
            if (mqTouch.addEventListener) mqTouch.addEventListener('change', refreshAll);
            else if (mqTouch.addListener) mqTouch.addListener(refreshAll);
        }
    } catch (e) { logErr('TO_CHAT.init', e); }

    TO_CHAT.composer = {
        attach: attach,
        /* Recalculează limita și înălțimea unui câmp atașat. */
        fit: function (el) {
            var st = el && el.__toComposer;
            if (st) { applyLimits(st); schedule(st); }
        },
        isTouchPrimary: isTouchPrimary,
        /* Pentru verificare: forțează varianta JS pe un câmp (ce rulează azi pe
           iPhone cu iOS sub 26.2 și pe Firefox sub 152), ca s-o putem testa și
           într-un browser care are `field-sizing`.
           EN: test hook — switch one field to the JS sizing path. */
        useJsSizing: function (el) {
            var st = el && el.__toComposer;
            if (!st) return;
            st.fs = false;
            el.classList.remove('ci-fs');
            hookValue(st);
            schedule(st);
        }
    };

    /* ========================================================================
       „CITEȘTE TOT" — mesajele lungi apar scurtate (8.1bis-A2)
       ------------------------------------------------------------------------
       RO: Cu câmpul pe mai multe rânduri, un singur mesaj lung ar ocupa tot
           ecranul. Bula arată CEL MULT 12 RÂNDURI, numărate cum apar pe ecran
           (inclusiv cele rupte de lățimea bulei), cu „…" pus de browser, apoi
           butonul „Citește tot", care deschide mesajul PE LOC. Rămâne deschis
           până la reîncărcarea paginii (ținem minte doar id-ul, nu textul).
           ⚠ De ce rânduri, nu caractere: prima variantă (600 de caractere) dădea
             pe telefon o bulă de 707 px — dublul listei de mesaje (337 px), fiindcă
             fontul e monospațiat și bula îngustă (~23 de caractere pe rând).
           • Textul întreg rămâne în pagină, doar ascuns de CSS
             (`-webkit-line-clamp`) ⇒ nicio tăietură în mijlocul unui cuvânt,
             link sau emoji, iar „Copiază" și reply-ul folosesc textul întreg.
           • Butonul apare DOAR dacă textul chiar depășește 12 rânduri. Verifică
             un `ResizeObserver`, doar pe mesajele lungi; se reface singur când se
             schimbă lățimea (rotirea ecranului) sau când lista devine vizibilă.
           • Escaparea rămâne `linkifyEscaped(escHtml(x))`, în această ordine (§57.4).
       EN: long messages are clamped to 12 visible lines; "Read more" shows only
           when the text really overflows and expands the message in place.
       ======================================================================== */
    var MORE_LINES = 12;
    /* Sub atâtea caractere (și cu puține rânduri scrise) un mesaj nu ajunge la 12
       rânduri nici pe cel mai îngust ecran (320 px ⇒ ~17 caractere pe rând), nici
       cu fontul mărit din setările telefonului ⇒ nu-l mai verificăm. */
    var MORE_MIN_CHARS = 80;
    var expanded = new Set();

    /* Un singur observator pentru toate bulele lungi. Fără `ResizeObserver`
       (browsere foarte vechi) butonul se afișează mereu la mesajele lungi, ca
       textul să nu rămână niciodată ascuns fără cale de deschidere. */
    var clampRO = null;
    try {
        if (window.ResizeObserver) {
            clampRO = new ResizeObserver(function (entries) {
                try { checkClamps(entries); } catch (e) { logErr('TO_CHAT.clamp', e); }
            });
        }
    } catch (e) { clampRO = null; }

    function newlineCount(s) {
        var n = 0, i = -1;
        while ((i = s.indexOf('\n', i + 1)) !== -1) n++;
        return n;
    }

    /* HTML-ul textului unei bule (conținutul lui `.chat-text`). `key` = id-ul
       mesajului, ca deschiderea să supraviețuiască redesenării listei.
       8.1-f: + formatarea ca în WhatsApp și @mențiunile (`opts.mentions`, club). */
    function textHtml(text, key, opts) {
        var s = (text == null) ? '' : String(text);
        var full = fmtHtml(s, opts && opts.mentions);
        /* 8.3: un steag în text ⇒ testul de afișare (o dată); doar 1–3 emoji ⇒ mari. */
        try { if (emoRe && emoRe.ri.test(s)) flagCheck(); } catch (e) { /* noop */ }
        var jn = jumboN(s);
        if (jn) return '<span class="chat-jumbo j' + jn + '">' + full + '</span>';
        if (key == null || expanded.has(String(key))) return full;
        if (s.length <= MORE_MIN_CHARS && newlineCount(s) < MORE_LINES - 1) return full;
        var k = window.escHtml(String(key));
        var label = (typeof window.t === 'function') ? window.t('common.readMore') : 'Read more';
        /* ⚠ Fără spații între etichete: `.chat-text` are `pre-wrap`, deci orice
             spațiu de aici s-ar vedea în bulă. */
        return '<div class="chat-clamp" data-key="' + k + '">' + full + '</div>'
            + '<button type="button" class="chat-more" data-action="chat-more" data-key="' + k + '"'
            + (clampRO ? ' hidden' : '') + '>' + window.escHtml(label) + '</button>';
    }

    /* Depășește textul cele 12 rânduri? Rulează după așezarea în pagină și la
       fiecare schimbare de mărime. `clientHeight` 0 = încă ascuns ⇒ așteptăm. */
    function checkClamps(entries) {
        entries.forEach(function (en) {
            var el = en.target;
            if (!el.isConnected || el.classList.contains('is-open')) { clampRO.unobserve(el); return; }
            if (!el.clientHeight) return;
            var b = el.nextElementSibling;
            if (b && b.classList.contains('chat-more')) b.hidden = !(el.scrollHeight > el.clientHeight + 1);
        });
    }
    function eachClamp(node, fn) {
        if (!node || node.nodeType !== 1) return;
        if (node.classList.contains('chat-clamp')) fn(node);
        node.querySelectorAll('.chat-clamp').forEach(fn);
    }

    /* Legată O SINGURĂ dată pe lista de mesaje: delegare pe faza de CAPTURĂ
       (§65.6) pentru buton + un `MutationObserver` care dă observatorului bulele
       lungi nou desenate (lista se redesenează din mai multe locuri). */
    function bindMore(container) {
        if (!container || container.__toMore) return;
        container.__toMore = true;
        container.addEventListener('click', function (e) {
            var b = e.target && e.target.closest ? e.target.closest('[data-action="chat-more"]') : null;
            if (!b || !container.contains(b)) return;
            try {
                expanded.add(String(b.getAttribute('data-key')));
                var box = b.previousElementSibling;
                var hadFocus = (document.activeElement === b);
                if (box && box.classList.contains('chat-clamp')) {
                    box.classList.add('is-open');
                    if (clampRO) clampRO.unobserve(box);
                }
                b.remove();
                /* Butonul dispare: focusul (tastatură) trece pe text, nu se pierde. */
                if (hadFocus && box) {
                    box.setAttribute('tabindex', '-1');
                    try { box.focus({ preventScroll: true }); } catch (e2) { box.focus(); }
                }
            } catch (e3) { logErr('TO_CHAT.more', e3); }
        }, true);
        if (clampRO && window.MutationObserver) {
            var obs = function (el) { if (!el.classList.contains('is-open')) clampRO.observe(el); };
            var unobs = function (el) { clampRO.unobserve(el); };
            try {
                new MutationObserver(function (muts) {
                    muts.forEach(function (m) {
                        var i;
                        for (i = 0; i < m.removedNodes.length; i++) eachClamp(m.removedNodes[i], unobs);
                        for (i = 0; i < m.addedNodes.length; i++) eachClamp(m.addedNodes[i], obs);
                    });
                }).observe(container, { childList: true, subtree: true });
                eachClamp(container, obs);
            } catch (e) { logErr('TO_CHAT.bindMore', e); }
        }
    }

    TO_CHAT.textHtml = textHtml;
    TO_CHAT.bindMore = bindMore;

    /* ========================================================================
       8.1-f — TEXTUL MESAJELOR CA ÎN WHATSAPP (club + DM)
       ------------------------------------------------------------------------
       RO: Formatarea: *îngroșat*, _cursiv_, ~tăiat~, ```monospațiat```, `cod`,
           liste („* ” / „- ” → •, „1. ”) și „> citat”. Regulile WhatsApp: markerul
           e lipit de text și stă la granița unui cuvânt ⇒ `2*3*4`, `nume_fisier_x`
           sau un e-mail rămân cum sunt.
           ⚠ SIGURANȚĂ: linkurile, codul și mențiunile se scot ÎNAINTE (jetoane din
             zona privată Unicode U+E000…), apoi textul se escapează (`escHtml`), apoi
             se pun DOAR etichetele noastre (<b>, <i>, <s>, <span class>); jetoanele
             revin la final. Un `_` dintr-o adresă nu poate strica linkul, iar textul
             utilizatorului nu ajunge niciodată HTML. Caracterele din zona privată
             scrise de utilizator devin și ele jetoane (nu pot imita unul).
           • Orice eroare ⇒ randarea de dinainte (`linkifyEscaped(escHtml(x))`).
       EN: WhatsApp-style formatting over escaped text; links / code / mentions are
           tokenised first, so formatting can never touch them. */
    var PUA0 = 0xE000, PUA_MAX = 6000;
    var W_UNI = (function () { try { new RegExp('\\p{L}', 'u'); return true; } catch (e) { return false; } })();
    var W_CLS = W_UNI ? '\\p{L}\\p{N}' : 'A-Za-z0-9\\u00C0-\\u024F\\u0370-\\u03FF\\u0400-\\u04FF';
    var W_ONE = new RegExp('[' + W_CLS + ']', W_UNI ? 'u' : '');
    /* *x* / _x_ / ~x~: deschidere la început sau după o ne-literă, conținut fără
       spații la capete și fără rând nou, închidere înainte de final sau de o ne-literă. */
    var MARKS = [['*', 'b'], ['_', 'i'], ['~', 's']].map(function (m) {
        var c = m[0] === '*' ? '\\*' : m[0];
        return { tag: m[1], re: new RegExp('(^|[^' + W_CLS + c + '])' + c + '(?=[^\\s' + c + '])([^' + c + '\\n]*?[^\\s' + c + '])' + c + '(?=$|[^' + W_CLS + c + '])', W_UNI ? 'gu' : 'g') };
    });
    /* Aceeași familie de caractere ca `linkifyEscaped` (app-core.js), pe text brut. */
    var URL_RAW = /\bhttps?:\/\/[A-Za-z0-9\-._~:\/?#\[\]@!$()*+,;=%&]+/g;

    function myId() { return (typeof state !== 'undefined' && state && state.currentUser && state.currentUser.id) || null; }
    function nameById(id) {
        try { return (typeof window.displayNameById === 'function' && window.displayNameById(id)) || ''; } catch (e) { return ''; }
    }
    function validMention(m) { return !!(m && m.id && typeof m.n === 'string' && m.n.length > 0 && m.n.length <= 80); }
    /* „@Nume" ca jeton, doar la granița unui cuvânt (nu în „x@Nume" sau „@Numele"). */
    function replaceMention(s, needle, tok) {
        var out = '', i = 0, j;
        while ((j = s.indexOf(needle, i)) !== -1) {
            var pv = j > 0 ? s.charAt(j - 1) : '', nx = s.charAt(j + needle.length);
            if ((pv && W_ONE.test(pv)) || (nx && W_ONE.test(nx))) { out += s.slice(i, j + 1); i = j + 1; continue; }
            out += s.slice(i, j) + tok;
            i = j + needle.length;
        }
        return out + s.slice(i);
    }
    function mentionHtml(m, me) {
        /* Numele ACTUAL al persoanei (după id); rezervă: numele scris la trimitere. */
        var nm = nameById(m.id) || m.n;
        return '<span class="chat-mention' + (me && String(m.id) === String(me) ? ' is-me' : '') + '" data-action="chat-profile" data-uid="'
            + esc(String(m.id)) + '">@' + esc(nm) + '</span>';
    }
    function inlineMarks(t) {
        for (var i = 0; i < MARKS.length; i++) t = t.replace(MARKS[i].re, '$1<' + MARKS[i].tag + '>$2</' + MARKS[i].tag + '>');
        return t;
    }
    function fmtLine(ln) {
        var m;
        if ((m = /^&gt; (.*)$/.exec(ln))) return '<span class="chat-fq">' + inlineMarks(m[1]) + '</span>';
        if ((m = /^[*-] (.+)$/.exec(ln))) return '<span class="chat-li">• ' + inlineMarks(m[1]) + '</span>';
        if ((m = /^(\d{1,3})\. (.+)$/.exec(ln))) return '<span class="chat-li">' + m[1] + '. ' + inlineMarks(m[2]) + '</span>';
        return inlineMarks(ln);
    }
    function fmtHtml(raw, mentions) {
        var src = (raw == null) ? '' : String(raw);
        try {
            var toks = [], s = src;
            var ph = function (html) { toks.push(html); return String.fromCharCode(PUA0 + toks.length - 1); };
            s = s.replace(/[-]/g, function (c) { return ph(esc(c)); });
            s = s.replace(/```([\s\S]+?)```/g, function (m0, c) { return ph('<span class="chat-pre">' + esc(c) + '</span>'); });
            s = s.replace(/`([^`\n]+)`/g, function (m0, c) { return ph('<code class="chat-code">' + esc(c) + '</code>'); });
            s = s.replace(URL_RAW, function (u) { return ph(window.linkifyEscaped(esc(u))); });
            if (Array.isArray(mentions) && mentions.length) {
                var me = myId();
                mentions.filter(validMention).slice().sort(function (a, b) { return b.n.length - a.n.length; }).forEach(function (m) {
                    s = replaceMention(s, '@' + m.n, ph(mentionHtml(m, me)));
                });
            }
            if (toks.length > PUA_MAX) return window.linkifyEscaped(esc(src));
            var out = esc(s).split('\n').map(fmtLine).join('\n');
            return out.replace(/[-]/g, function (c) { var h = toks[c.charCodeAt(0) - PUA0]; return h == null ? '' : h; });
        } catch (e) {
            logErr('TO_CHAT.fmt', e);
            return window.linkifyEscaped(esc(src));
        }
    }
    /* Textul fără markere (citate, bara „Răspunde", meniul mesajului). */
    function plainText(raw) {
        var s = (raw == null) ? '' : String(raw);
        try {
            s = s.replace(/```([\s\S]+?)```/g, '$1').replace(/`([^`\n]+)`/g, '$1');
            for (var i = 0; i < MARKS.length; i++) s = s.replace(MARKS[i].re, '$1$2');
        } catch (e) { logErr('TO_CHAT.plain', e); }
        return s;
    }
    /* Ora „în linie" (8.1-f): un loc invizibil la finalul textului, cât ora (+ bifele),
       ca ora să stea în colțul ultimului rând când încape (chat.css, `.meta-in`). */
    function metaSpacer(timeTxt, tick) {
        return '<span class="chat-meta-sp' + (tick ? ' has-tick' : '') + '" aria-hidden="true">' + esc(String(timeTxt || '')) + '</span>';
    }
    TO_CHAT.fmt = { html: fmtHtml, plain: plainText };
    TO_CHAT.meta = { spacer: metaSpacer };

    /* ── 8.1-f: numele din citate = numele ACTUAL, „Tu" pentru mesajele proprii ──
       RO: Citatul salva numele de la momentul răspunsului (`reply_sender`) ⇒ după o
           redenumire același om apărea cu trei nume. Acum: după id-ul mesajului citat
           (în memorie) aflăm expeditorul și îi arătăm numele de acum. Dacă mesajul
           citat nu e încărcat, îl cerem o singură dată (id + expeditor; club: filtrat
           pe club; DM: doar mesajele mele; ≤ 50 de id-uri; doar cu conversația
           deschisă), apoi conversația se redesenează (`names.ver` intră în semnătură).
           Memoria se golește singură la schimbarea contului.
       EN: quoted names resolved from the quoted message's sender id. */
    var qs = { uid: null, club: {}, dm: {}, ph: { club: {}, dm: {} }, want: { club: {}, dm: {} }, timer: 0, ver: 0 };
    function qReset() {
        var me = myId();
        if (qs.uid !== me) { qs.uid = me; qs.club = {}; qs.dm = {}; qs.ph = { club: {}, dm: {} }; qs.want = { club: {}, dm: {} }; }
    }
    /* 8.1-g: prima poză a mesajului citat (miniatura din citat), din memorie sau din
       aceeași cerere leneșă ca numele. Calea se semnează ca orice poză (`data-sp`). */
    function firstPhoto(list) { return (Array.isArray(list) && list.length && typeof list[0] === 'string') ? list[0] : null; }
    function quotePhoto(kind, id) {
        try {
            if (id == null || id === '') return null;
            var row = kind === 'club'
                ? ((typeof window.chatMsgById === 'function') ? window.chatMsgById(id) : null)
                : ((typeof window._dmMsgById === 'function') ? window._dmMsgById(id) : null);
            if (row) return (row.deletedAt || row.deleted_at) ? null : firstPhoto(row.photos);
            qReset();
            quotedSender(kind, id);   /* pornește (o dată) căutarea leneșă, dacă e cazul */
            return qs.ph[kind][String(id)] || null;
        } catch (e) { logErr('TO_CHAT.quotePhoto', e); return null; }
    }
    function quotedSender(kind, id) {
        qReset();
        id = String(id);
        if (Object.prototype.hasOwnProperty.call(qs[kind], id)) return qs[kind][id] || null;
        if (!/^\d+$/.test(id) || (typeof isDemoMode === 'function' && isDemoMode())) return null;
        qs.want[kind][id] = 1;
        if (!qs.timer) qs.timer = setTimeout(runQuoted, 60);
        return null;
    }
    async function runQuoted() {
        qs.timer = 0;
        var me = myId();
        if (!me || typeof sb === 'undefined' || !sb) return;
        var changed = false;
        try {
            var club = (typeof currentClubId !== 'undefined') ? currentClubId : null;
            var cIds = Object.keys(qs.want.club).slice(0, 50);
            if (cIds.length && club && typeof currentTab !== 'undefined' && currentTab === 'chat') {
                qs.want.club = {};
                cIds.forEach(function (x) { qs.club[x] = ''; });
                var r = await sb.from('messages').select('id, sender_id, photos').eq('club_id', club).in('id', cIds).limit(50);
                if (!r.error && Array.isArray(r.data)) r.data.forEach(function (x) {
                    if (x.sender_id) { qs.club[String(x.id)] = x.sender_id; changed = true; }
                    var p = firstPhoto(x.photos); if (p) { qs.ph.club[String(x.id)] = p; changed = true; }
                });
            }
            var dIds = Object.keys(qs.want.dm).slice(0, 50);
            if (dIds.length && window._dmOpenWith) {
                qs.want.dm = {};
                dIds.forEach(function (x) { qs.dm[x] = ''; });
                var d = await sb.from('direct_messages').select('id, sender_id, photos').in('id', dIds)
                    .or('sender_id.eq.' + me + ',recipient_id.eq.' + me).limit(50);
                if (!d.error && Array.isArray(d.data)) d.data.forEach(function (x) {
                    if (x.sender_id) { qs.dm[String(x.id)] = x.sender_id; changed = true; }
                    var p = firstPhoto(x.photos); if (p) { qs.ph.dm[String(x.id)] = p; changed = true; }
                });
            }
        } catch (e) { logErr('TO_CHAT.names', e); }
        if (!changed || myId() !== me) return;
        qs.ver++;
        try { if (typeof currentTab !== 'undefined' && currentTab === 'chat' && typeof loadChat === 'function') loadChat(); } catch (e) { logErr('TO_CHAT.names', e); }
        try { if (window._dmOpenWith && typeof window.renderDMThread === 'function') window.renderDMThread(); } catch (e) { logErr('TO_CHAT.names', e); }
    }
    /* Numele afișat în citat. `stored` = numele salvat la răspuns (rezervă). */
    function quoteName(kind, id, stored) {
        try {
            if (id == null || id === '') return stored || '';
            var me = myId(), sid = null, row = null;
            if (kind === 'club') {
                row = (typeof window.chatMsgById === 'function') ? window.chatMsgById(id) : null;
                if (row) {
                    sid = row.senderId || null;
                    if (!sid) {
                        var em = state && state.currentUser && state.currentUser.email;
                        return (em && row.senderEmail === em) ? tr('dm.you', 'You') : (row.sender || stored || '');
                    }
                }
            } else {
                row = (typeof window._dmMsgById === 'function') ? window._dmMsgById(id) : null;
                if (row) sid = row.sender_id || null;
            }
            if (!sid) sid = quotedSender(kind, id);
            if (!sid) return stored || '';
            if (me && sid === me) return tr('dm.you', 'You');
            var nm = (kind === 'dm' && window._dmNames && window._dmNames[sid]) || nameById(sid);
            return nm || stored || '';
        } catch (e) { logErr('TO_CHAT.quoteName', e); return stored || ''; }
    }
    /* Miniatura pentru citat (bula + bara „Răspunde"): HTML gata, cu `data-sp`. */
    function thumbHtml(p, cls) {
        /* 8.5-B: `data-spt` ⇒ miniatura, dacă există (supabase-integration.js, semnarea) */
        return p ? '<img class="' + (cls || 'chat-quote-ph') + '" data-sp="' + esc(p) + '" data-spt="1" src="' + esc(window._SP_PH || '') + '" alt="" aria-hidden="true">' : '';
    }
    function quoteThumb(kind, id, cls) { return thumbHtml(quotePhoto(kind, id), cls); }
    /* RP (31 oct. 2026): „Răspunde privat" la o poză din chatul clubului — în DM citatul n-are id de
       DM, ci calea pozei (`reply_photo`, verificată de server). Doar căi din dosarul unui club; o vede
       doar cine e membru (regula de citire a pozelor). EN: club-photo thumbnail for private replies. */
    function clubThumb(p, cls) { return (typeof p === 'string' && /^club\//.test(p)) ? thumbHtml(p, cls) : ''; }
    TO_CHAT.names = { quote: quoteName, ver: function () { return qs.ver; }, photo: quotePhoto, thumb: quoteThumb, clubThumb: clubThumb };

    /* ── 8.1-f: @mențiuni la scriere (chatul de club) ──────────────────────────
       RO: Scrii „@" la început sau după un spațiu ⇒ deasupra câmpului apare lista
           membrilor clubului (fără tine), filtrată pe măsură ce scrii (fără să
           conteze diacriticele; fiecare cuvânt scris = începutul unui cuvânt din
           nume). Atingere / clic sau ↑ ↓ + Enter / Tab aleg, Esc închide. Alegerea
           pune „@Nume " în text și ține minte {id, nume}; la trimitere pleacă doar
           mențiunile al căror „@Nume" a rămas în text (max. 20) — serverul le
           verifică din nou (V9_81F_MENTIONS.sql: doar membri activi ai clubului).
           ⚠ Datele vin din memorie (membrii clubului deja încărcați): zero cereri.
           ⚠ Elementele din listă nu primesc clicuri pe copii (CSS) ⇒ poza de profil
             nu deschide galeria; apăsarea nu ia focusul câmpului (tastatura rămâne).
       EN: @-mention picker over the club composer; members from memory only. */
    var MENTION_MAX = 20;
    var mp = { st: null, box: null, items: [], sel: 0, at: -1, picked: [] };
    function foldTxt(s) { return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase(); }
    function mentionMembers() {
        var me = myId(), out = [], byId = (typeof state !== 'undefined' && state && state.membersById) || {};
        Object.keys(byId).forEach(function (id) {
            var m = byId[id];
            if (!m || !id || id === me) return;
            var name = nameById(id) || m.displayName || m.name;
            if (name) out.push({ id: id, name: String(name), avatar: m.avatar || '' });
        });
        out.sort(function (a, b) { return a.name.localeCompare(b.name); });
        return out;
    }
    function mentionQuery(el) {
        var pos = el.selectionStart;
        if (pos == null || pos !== el.selectionEnd) return null;
        /* Rapid: fără „@" în ultimele 31 de caractere ⇒ nimic de căutat (la fiecare tastă). */
        var at = el.value.lastIndexOf('@', pos - 1);
        if (at === -1 || pos - at > 31) return null;
        var m = /(^|\s)@([^\s@][^\n@]{0,29}|)$/.exec(el.value.slice(Math.max(0, at - 1), pos));
        /* Spațiu la final ⇒ închis (și după o alegere, care pune „@Nume "). */
        if (!m || /\s$/.test(m[2])) return null;
        return { at: pos - m[2].length - 1, q: m[2] };
    }
    function mentionClose() {
        if (!mp.items.length && (!mp.box || mp.box.hidden)) return;
        if (mp.box) mp.box.hidden = true;
        if (mp.st) { mp.st.el.setAttribute('aria-expanded', 'false'); mp.st.el.removeAttribute('aria-activedescendant'); }
        mp.items = []; mp.at = -1;
    }
    function mentionPaint() {
        var html = mp.items.map(function (m, i) {
            var av = m.avatar && typeof window.avatarHtml === 'function' ? window.avatarHtml(m.avatar, 28) : (iniHtml(m.id, m.name, 28) || (typeof window.avatarHtml === 'function' ? window.avatarHtml('', 28) : ''));
            return '<div class="chat-mention-item" role="option" id="to-mn-' + i + '" data-i="' + i + '" aria-selected="' + (i === mp.sel ? 'true' : 'false') + '">'
                + '<span class="chat-mention-av">' + av + '</span><span class="chat-mention-nm">' + esc(m.name) + '</span></div>';
        }).join('');
        mp.box.innerHTML = html;
        mp.st.el.setAttribute('aria-activedescendant', 'to-mn-' + mp.sel);
        var cur = mp.box.querySelector('[aria-selected="true"]');
        if (cur && cur.scrollIntoView) { try { cur.scrollIntoView({ block: 'nearest' }); } catch (e) { /* noop */ } }
    }
    function mentionBox(st) {
        if (mp.box && mp.box.isConnected && mp.st === st) return mp.box;
        var host = st.list && st.list.parentNode;
        if (!host) return null;
        var box = document.createElement('div');
        box.className = 'chat-mention-box';
        box.id = 'chatMentionBox';
        box.setAttribute('role', 'listbox');
        box.setAttribute('aria-label', tr('chat.mentionList', 'Mention a member'));
        box.hidden = true;
        host.appendChild(box);
        /* `pointerdown` + `preventDefault`: câmpul nu pierde focusul (tastatura rămâne). */
        box.addEventListener('pointerdown', function (e) {
            var it = e.target && e.target.closest ? e.target.closest('.chat-mention-item') : null;
            if (!it) return;
            e.preventDefault();
            mentionPick(+it.getAttribute('data-i'));
        });
        st.el.setAttribute('aria-controls', 'chatMentionBox');
        mp.box = box; mp.st = st;
        return box;
    }
    function mentionUpdate(st) {
        try {
            var q = mentionQuery(st.el);
            if (!q) { mentionClose(); return; }
            var words = foldTxt(q.q).split(/\s+/).filter(Boolean);
            var list = mentionMembers().filter(function (m) {
                var nw = foldTxt(m.name).split(/\s+/);
                return words.every(function (w) { return nw.some(function (x) { return x.indexOf(w) === 0; }); });
            }).slice(0, 30);
            if (!list.length) { mentionClose(); return; }
            var box = mentionBox(st);
            if (!box) return;
            mp.items = list; mp.at = q.at;
            if (mp.sel >= list.length) mp.sel = 0;
            /* Deasupra zonei de scriere: citiri întâi, apoi scrieri (§54.4). */
            var hb = box.parentNode.getBoundingClientRect().bottom;
            var top = (st.area || st.el).getBoundingClientRect().top;
            box.style.bottom = Math.max(8, Math.round(hb - top + 6)) + 'px';
            box.hidden = false;
            st.el.setAttribute('aria-expanded', 'true');
            mentionPaint();
        } catch (e) { logErr('TO_CHAT.mention', e); }
    }
    function mentionPick(i) {
        var st = mp.st, m = mp.items[i];
        if (!st || !m || mp.at < 0) return;
        var el = st.el, pos = el.selectionStart, ins = '@' + m.name + ' ';
        el.focus();
        el.setSelectionRange(mp.at, pos);
        var okExec = false;
        try { okExec = document.execCommand && document.execCommand('insertText', false, ins); } catch (e) { okExec = false; }
        if (!okExec) {
            el.setRangeText(ins, mp.at, pos, 'end');
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (!mp.picked.some(function (x) { return x.id === m.id; })) mp.picked.push({ id: m.id, n: m.name });
        mentionClose();
        liveUpdate(st);                                       /* 8.1-g: „@Nume" colorat în câmp */
    }
    /* Tastele cât lista e deschisă. Întoarce true dacă a tratat tasta. */
    function mentionKey(e) {
        if (!mp.box || mp.box.hidden || !mp.items.length) return false;
        if (e.isComposing || e.keyCode === 229) return false;
        var k = e.key;
        if (k === 'ArrowDown' || k === 'ArrowUp') {
            mp.sel = (mp.sel + (k === 'ArrowDown' ? 1 : -1) + mp.items.length) % mp.items.length;
            mentionPaint();
        } else if (k === 'Enter' || k === 'Tab') {
            mentionPick(mp.sel);
        } else if (k === 'Escape') {
            mentionClose();
        } else return false;
        e.preventDefault();
        e.stopPropagation();
        return true;
    }
    /* La trimitere: mențiunile alese al căror „@Nume" a rămas în text, membri de acum. */
    function mentionTake(text) {
        var t = String(text || ''), byId = (typeof state !== 'undefined' && state && state.membersById) || {}, seen = {}, out = [];
        mp.picked.forEach(function (m) {
            if (out.length >= MENTION_MAX || seen[m.id] || !byId[m.id]) return;
            if (replaceMention(t, '@' + m.n, '￿') === t) return;
            seen[m.id] = 1;
            out.push({ id: m.id, n: m.n });
        });
        mp.picked = [];
        mentionClose();
        return out.length ? out : null;
    }

    /* ── 8.1-f: scurtături de formatare (tastatură fizică) ───────────────────────
       Ctrl/Cmd+B → *…*, Ctrl/Cmd+I → _…_, Ctrl/Cmd+Shift+X → ~…~ în jurul textului
       selectat (spațiile de la capete rămân în afară); fără selecție, perechea de
       markere cu cursorul între ele. `insertText` păstrează „Anulează" (Ctrl+Z). */
    function fmtKey(st, e) {
        if (!(e.ctrlKey || e.metaKey) || e.altKey) return false;
        var k = String(e.key || '').toLowerCase(), mk = null;
        if (k === 'b' && !e.shiftKey) mk = '*';
        else if (k === 'i' && !e.shiftKey) mk = '_';
        else if (k === 'x' && e.shiftKey) mk = '~';
        if (!mk) return false;
        var el = st.el, a = el.selectionStart, b = el.selectionEnd;
        if (a == null) return false;
        e.preventDefault();
        var picked = el.value.slice(a, b);
        var lead = picked.match(/^\s*/)[0], rest = picked.slice(lead.length), trail = rest.match(/\s*$/)[0], core = rest.slice(0, rest.length - trail.length);
        var ins = core ? lead + mk + core + mk + trail : mk + mk;
        var okExec = false;
        try { el.focus(); okExec = document.execCommand && document.execCommand('insertText', false, ins); } catch (e2) { okExec = false; }
        if (!okExec) { el.setRangeText(ins, a, b, 'end'); el.dispatchEvent(new Event('input', { bubbles: true })); }
        var p = core ? a + lead.length + 1 : a + 1;
        el.setSelectionRange(p, p + core.length);
        return true;
    }

    /* ========================================================================
       8.1-g — FORMATAREA VIZIBILĂ ÎN CÂMP, TRAS PENTRU RĂSPUNS, SELECȚIE MULTIPLĂ
       ======================================================================== */

    /* ── Formatarea vizibilă în timp ce scrii (club + DM) ───────────────────────
       RO: Un <textarea> nu poate arăta litere îngroșate. Tehnica „oglindă": sub câmp
           desenăm ACELAȘI text, formatat (markerele rămân, mai estompate), iar literele
           câmpului devin transparente; cursorul, selecția, tastatura, corectura și
           compunerea (IME) rămân ale câmpului real.
           ⚠ Cursorul trebuie să cadă exact pe litere ⇒ nimic din oglindă nu schimbă
             lățimea textului (măsurat, Segoe UI 16 px, 41 de caractere): îngroșatul real
             +6% ⇒ „desenat" cu contur (+0); cursivul +0,9% ⇒ compensat cu spațierea
             literelor, măsurată pe fiecare bucată; monospațiatul +28% ⇒ în câmp doar o
             nuanță de fundal (în bulă rămâne monospațiat).
           • Pornește DOAR când textul chiar are formatare (sau o @mențiune aleasă):
             un mesaj obișnuit se comportă exact ca înainte. Peste 4.000 de caractere,
             oprită (cost la fiecare tastă). Orice eroare ⇒ câmpul rămâne normal.
       EN: live formatting via a mirror under a transparent-text textarea; nothing in
           the mirror changes glyph widths, so the caret stays on the letters. */
    var LIVE_MAX = 4000;
    var LIVE_STYLE = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'wordSpacing',
        'textIndent', 'textTransform', 'tabSize', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'];
    function liveHtml(raw, picked) {
        var toks = [], s = String(raw);
        var ph = function (html) { toks.push(html); return String.fromCharCode(PUA0 + toks.length - 1); };
        s = s.replace(/[-]/g, function (c) { return ph(esc(c)); });
        s = s.replace(/```([\s\S]+?)```/g, function (m0) { return ph('<span class="fl-c">' + esc(m0) + '</span>'); });
        s = s.replace(/`([^`\n]+)`/g, function (m0) { return ph('<span class="fl-c">' + esc(m0) + '</span>'); });
        s = s.replace(URL_RAW, function (u) { return ph(esc(u)); });
        if (Array.isArray(picked) && picked.length) {
            picked.filter(validMention).slice().sort(function (a, b) { return b.n.length - a.n.length; }).forEach(function (m) {
                s = replaceMention(s, '@' + m.n, ph('<span class="fl-at">' + esc('@' + m.n) + '</span>'));
            });
        }
        if (toks.length > PUA_MAX) return '';
        s = esc(s);
        MARKS.forEach(function (mk) {
            var ch = mk.tag === 'b' ? '*' : (mk.tag === 'i' ? '_' : '~');
            s = s.replace(mk.re, function (all, pre, inner) {
                return pre + '<span class="fl-m">' + ch + '</span><span class="fl-' + mk.tag + '">' + inner + '</span><span class="fl-m">' + ch + '</span>';
            });
        });
        return s.replace(/[-]/g, function (c) { var h = toks[c.charCodeAt(0) - PUA0]; return h == null ? '' : h; });
    }
    function liveEnsure(st) {
        if (st.mir && st.mir.isConnected) return st.mir;
        var parent = st.el.parentNode;
        if (!parent) return null;
        var m = document.createElement('div');
        m.className = 'ci-mirror';
        m.setAttribute('aria-hidden', 'true');
        m.hidden = true;
        parent.insertBefore(m, st.el);
        st.mir = m;
        if (!st.__liveScroll) {
            st.__liveScroll = true;
            st.el.addEventListener('scroll', function () { if (st.liveOn && st.mir) st.mir.scrollTop = st.el.scrollTop; }, { passive: true });
        }
        return m;
    }
    /* Aceeași cutie și același text ca în câmp (citiri, apoi scrieri). */
    function liveGeom(st) {
        var el = st.el, m = st.mir;
        if (!m) return;
        var cs = getComputedStyle(el);
        var g = { l: el.offsetLeft, t: el.offsetTop, w: el.clientWidth, h: el.clientHeight, sc: el.scrollTop };
        var v = {};
        LIVE_STYLE.forEach(function (p) { v[p] = cs[p]; });
        m.style.left = g.l + 'px'; m.style.top = g.t + 'px';
        m.style.width = g.w + 'px'; m.style.height = g.h + 'px';
        LIVE_STYLE.forEach(function (p) { m.style[p] = v[p]; });
        m.scrollTop = g.sc;
    }
    /* Cursivul e cu ~0,9% mai lat ⇒ fiecare bucată cursivă primește spațierea care o
       aduce la lățimea textului drept (măsurat pe loc, cu o sondă ascunsă). */
    function liveItalic(m) {
        var its = m.querySelectorAll('.fl-i');
        if (!its.length) return;
        var probe = document.createElement('span');
        probe.className = 'ci-probe';
        m.appendChild(probe);
        var fix = [];
        for (var i = 0; i < its.length; i++) {
            var tx = its[i].textContent, n = Array.from(tx).length;
            if (!n) continue;
            probe.textContent = tx;
            probe.style.fontStyle = 'normal';
            var wN = probe.getBoundingClientRect().width;
            probe.style.fontStyle = 'italic';
            var wI = probe.getBoundingClientRect().width;
            fix.push([its[i], (wN - wI) / n]);
        }
        m.removeChild(probe);
        fix.forEach(function (f) { f[0].style.letterSpacing = f[1].toFixed(3) + 'px'; });
    }
    function liveOff(st) {
        if (!st.liveOn) return;
        st.liveOn = false;
        st.el.classList.remove('ci-live');
        if (st.mir) { st.mir.hidden = true; st.mir.textContent = ''; }
    }
    function liveUpdate(st) {
        try {
            var v = st.el.value || '';
            var html = (v && v.length <= LIVE_MAX && /[*_~`@]/.test(v)) ? liveHtml(v, st.mentions ? mp.picked : null) : '';
            if (!html || html.indexOf('<span') === -1) { liveOff(st); return; }
            var m = liveEnsure(st);
            if (!m) { liveOff(st); return; }
            /* Oglinda e gata (text, cutie, cursiv) ÎNAINTE ca literele câmpului să devină
               transparente ⇒ nicio clipă fără text vizibil. */
            m.innerHTML = html + '​';
            m.hidden = false;
            liveGeom(st);
            liveItalic(m);
            if (!st.liveOn) { st.liveOn = true; st.el.classList.add('ci-live'); }
        } catch (e) {
            logErr('TO_CHAT.live', e);
            try { liveOff(st); } catch (e2) { /* noop */ }
        }
    }

    /* ── Tragi de un mesaj spre dreapta ⇒ răspuns (club + DM, ecrane tactile) ─────
       RO: Ca în WhatsApp: bula urmează degetul (cel mult 72 px), apare săgeata de
           răspuns; ridici degetul după 56 px ⇒ „Răspunde" (aceeași funcție ca din
           meniu), cu o vibrație scurtă la prag unde există.
           • Evenimente „pointer" pasive + `touch-action: pan-y` pe rânduri (chat.css):
             browserul decide la început — mișcare verticală = derulare (gestul nostru
             se anulează), orizontală = a noastră. Fără ascultători care blochează
             derularea (§54.4).
           • Nu pornește din marginea stângă (24 px — gestul „înapoi" al telefonului),
             pe mesaje șterse / netrimise / de sistem, în DM blocat sau în selecție.
           • Apăsarea lungă (meniul) se anulează singură la mișcare (codul existent).
       EN: swipe right on a bubble to reply; passive pointer events + pan-y. */
    var SWIPE_MAX = 72, SWIPE_GO = 56, SWIPE_EDGE = 24;
    function reduceMotion() { try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }
    function rowSel(kind) { return kind === 'dm' ? '.dm-msg' : '.chat-row'; }
    function rowMid(kind, row) {
        if (kind === 'dm') return row.getAttribute('data-mid');
        var b = row.querySelector('.chat-message');
        return b ? b.getAttribute('data-mid') : null;
    }
    /* Același criteriu ca meniul: fără urme, rânduri provizorii sau mesaje de sistem. */
    function rowUsable(kind, row) {
        if (!row) return false;
        var id = rowMid(kind, row);
        if (!id || /^tmp_/.test(id)) return false;
        if (kind === 'dm') { var d = row.querySelector('.dm-bubble'); return !!d && !d.classList.contains('is-deleted'); }
        var b = row.querySelector('.chat-message');
        return !!b && b.getAttribute('data-system') !== '1' && !b.classList.contains('is-deleted');
    }
    /* 8.8-c — Apăsarea lungă pe telefon NU mai pornește selecția textului.
       RO: Alex: „uneori" se selecta pentru o clipă textul (cu opțiunile telefonului de
           copiere) înainte să apară meniul mesajului. Bulele erau deja neselectabile pe
           touch (chat.css), dar restul listei nu; iar momentul selecției telefonului
           depinde de setarea lui de „atingere lungă" (de aici „uneori"). Trei plase:
           toată lista neselectabilă pe touch (chat.css), `selectstart` anulat cât un
           deget e pe listă (aici) și selecția ștearsă la deschiderea meniului (openMenu).
           Cu mouse-ul (desktop) textul rămâne selectabil.
       EN: no text selection from a long press on touch. */
    function bindNoTouchSelect(list) {
        if (!list || list.__toNoSel) return;
        list.__toNoSel = true;
        var touching = false, t = 0;
        var on = function () { clearTimeout(t); touching = true; };
        var off = function () { clearTimeout(t); t = setTimeout(function () { touching = false; }, 400); };
        list.addEventListener('touchstart', on, { passive: true });
        list.addEventListener('touchend', off, { passive: true });
        list.addEventListener('touchcancel', off, { passive: true });
        list.addEventListener('selectstart', function (e) { if (touching) e.preventDefault(); });
    }
    function clearListSelection() {
        try {
            var sel = window.getSelection && window.getSelection();
            if (!sel || sel.isCollapsed || !sel.anchorNode) return;
            var n = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentNode;
            if (n && n.closest && n.closest('#chatMessages, #dmThread')) sel.removeAllRanges();
        } catch (e) { logErr('TO_CHAT.noSel', e); }
    }
    function bindSwipe(list) {
        if (!list || list.__toSwipe || !window.PointerEvent) return;
        list.__toSwipe = true;
        var kind = list.id === 'dmThread' ? 'dm' : 'club';
        var s = null, quietUntil = 0;
        var end = function (fire) {
            if (!s) return;
            var g = s;
            s = null;
            if (!g.on) return;
            var go = fire && g.dx >= SWIPE_GO, rm = reduceMotion();
            if (g.row.isConnected) {
                g.row.style.transition = rm ? '' : 'transform .18s ease';
                g.row.style.transform = '';
            }
            setTimeout(function () {
                g.row.style.transition = '';
                if (g.ic && g.ic.parentNode) g.ic.parentNode.removeChild(g.ic);
            }, rm ? 0 : 200);
            if (!go) return;
            quietUntil = Date.now() + 500;
            try {
                if (kind === 'dm') { if (typeof window.dmMsgReply === 'function') window.dmMsgReply(g.id); }
                else if (typeof window.msgReply === 'function') window.msgReply(g.id);
            } catch (e) { logErr('TO_CHAT.swipe', e); }
        };
        list.addEventListener('pointerdown', function (e) {
            s = null;
            if (e.pointerType !== 'touch' || !e.isPrimary || selActive() || e.clientX < SWIPE_EDGE) return;
            var row = e.target && e.target.closest ? e.target.closest(rowSel(kind)) : null;
            if (!row || !list.contains(row) || !rowUsable(kind, row)) return;
            if (kind === 'dm') {
                var area = document.querySelector('#dmModal .chat-input-area');
                if (area && area.hidden) return;                 /* relație blocată: fără răspuns */
            }
            s = { row: row, id: rowMid(kind, row), x0: e.clientX, y0: e.clientY, dx: 0, on: false, ic: null, buzz: false, pid: e.pointerId };
        }, { passive: true });
        list.addEventListener('pointermove', function (e) {
            if (!s || e.pointerId !== s.pid) return;
            var dx = e.clientX - s.x0, dy = e.clientY - s.y0;
            if (!s.on) {
                if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
                if (dx <= 0 || Math.abs(dx) < Math.abs(dy) * 1.4) { s = null; return; }
                if (!s.row.isConnected) { s = null; return; }
                s.on = true;
                s.ic = document.createElement('span');
                s.ic.className = 'chat-swipe-ic';
                s.ic.setAttribute('aria-hidden', 'true');
                s.ic.innerHTML = '<i class="fas fa-reply"></i>';
                s.row.appendChild(s.ic);
            }
            var d = Math.max(0, Math.min(dx - 10, SWIPE_MAX));
            s.dx = d;
            s.row.style.transform = 'translateX(' + d + 'px)';
            s.ic.style.opacity = String(Math.min(1, d / SWIPE_GO));
            if (d >= SWIPE_GO && !s.buzz) {
                s.buzz = true;
                if (navigator.vibrate) { try { navigator.vibrate(10); } catch (x) { /* noop */ } }
            } else if (d < SWIPE_GO) s.buzz = false;
        }, { passive: true });
        list.addEventListener('pointerup', function (e) { if (s && e.pointerId === s.pid) end(true); }, { passive: true });
        list.addEventListener('pointercancel', function () { end(false); }, { passive: true });
        /* Plasă: un clic imediat după tragere (dacă browserul îl trimite) nu deschide poza / linkul. */
        list.addEventListener('click', function (e) {
            if (Date.now() < quietUntil) { e.preventDefault(); e.stopPropagation(); }
        }, true);
    }

    /* ── Selecția mai multor mesaje (club + DM) ─────────────────────────────────
       RO: Meniul mesajului e o fereastră peste ecran, deci nu putem bifa „pe loc" ca
           în WhatsApp din meniu. Soluția: în meniu apare „Selectează"; apoi:
           • o bară sus, peste antet (DM) / deasupra mesajelor (club): ✕, „Selectate:
             N" și „Copiază";
           • atingerea / clicul pe un mesaj îl bifează sau îl debifează (nimic altceva
             nu se deschide: poze, linkuri, profil); apăsarea lungă / clic dreapta tot
             bifează (meniul nu se mai deschide cât e selecția activă);
           • ieșire: ✕, „înapoi" de pe telefon, Escape, „Copiază", 0 mesaje bifate,
             ieșirea din chat.
           • „Copiază": un mesaj ⇒ textul lui (ca „Copiază" din meniu); mai multe ⇒
             „[16.10.2026, 14:30] Nume: text", în ordinea timpului (ca WhatsApp).
           Bifarea supraviețuiește redesenărilor (aplicată odată cu gruparea).
       EN: multi-select via a menu entry, a top bar and tap-to-toggle; copy only. */
    var SEL_MAX = 200;
    var sel = { kind: null, list: null, ids: [], bar: null, key: null, last: null };
    function selActive(kind) {
        if (!sel.kind || !sel.list || !sel.list.isConnected) return false;
        return !kind || sel.kind === kind;
    }
    function selRowOf(kind, id) { return kind === 'dm' ? document.getElementById('dmmsg-' + id) : rowOf(id); }
    function selCount() {
        if (!sel.bar) return;
        var n = sel.bar.querySelector('.chat-sel-n');
        if (n) n.textContent = tr('chat.selectedN', 'Selected: {n}').replace('{n}', String(sel.ids.length));
        /* 8.4-d: 🗑 doar când TOATE mesajele bifate se pot șterge (cererea lui Alex) */
        var del = sel.bar.querySelector('[data-action="chat-sel-del"]');
        if (del) { var okDel = selDelOk(); if (del.hidden === okDel) del.hidden = !okDel; }
    }
    /* 8.4-d: aceleași reguli ca la un singur mesaj (app-modules-2.js `msgDelete`, social.js
       `dmMsgDelete`) și ca serverul (`delete_chat_message` / `delete_direct_message`):
       club — ale tale în primele 60 h, ale altora doar adminul (rang ≥ 3), nu într-un club
       doar-citire și nu cu restricție de scriere; DM — doar ale tale, în 60 h. Unul singur
       care nu se poate ⇒ butonul nu apare (selecție amestecată). EN: delete only if all can. */
    function selDelOk() {
        var kind = sel.kind, ids = sel.ids;
        if (!ids.length || (kind !== 'club' && kind !== 'dm')) return false;
        var me = myId(), em = (typeof state !== 'undefined' && state && state.currentUser && state.currentUser.email) || null, admin = false;
        try {
            if (kind === 'club') {
                if ((typeof isClubReadOnly === 'function' && isClubReadOnly()) || (typeof isMuted !== 'undefined' && isMuted)) return false;
                admin = canDeleteAny();
            }
            for (var i = 0; i < ids.length; i++) {
                var m = kind === 'dm' ? (typeof window._dmMsgById === 'function' ? window._dmMsgById(ids[i]) : null)
                    : (typeof window.chatMsgById === 'function' ? window.chatMsgById(ids[i]) : null);
                if (!m || m.pending || m.failed) return false;
                if (kind === 'dm') {
                    if (m.deleted_at || !me || String(m.sender_id) !== String(me) || !withinDelete(m.created_at)) return false;
                } else {
                    var own = !!em && m.senderEmail === em;        /* regula de azi a lui `msgDelete` (H6, după id, e separat) */
                    if (m.deletedAt || m.sender === 'Sistem' || !(own ? withinDelete(m.timestamp) : admin)) return false;
                }
            }
        } catch (e) { logErr('TO_CHAT.select', e); return false; }
        return true;
    }
    /* Ștergerea propriu-zisă: chat-delete.js, încărcat la cerere (confirmarea, serverul, urma). */
    function selDelete() {
        var kind = sel.kind, ids = sel.ids.slice();
        if (!selDelOk()) return;
        ensureModule('chat-delete.js', 'TO_DEL').then(function (M) { M.run(kind, ids); }, function (e) {
            if (e && e.isVersion) {
                if (typeof window.confirmModal === 'function') window.confirmModal(tr('lazy.reload', 'A new version of the app is available. Reload now?')).then(function (y) { if (y) location.reload(); });
                return;
            }
            logErr('TO_CHAT.select', e);
            if (typeof window.showNotification === 'function') window.showNotification(tr('lazy.loadErr', 'Could not load this part of the app. Check your connection.'), 'error');
        });
    }
    function selEnd(fromBack) {
        if (!sel.kind) return;
        var g = sel;
        sel = { kind: null, list: null, ids: [], bar: null, key: null, last: null };
        try {
            if (g.list) {
                g.list.classList.remove('is-selecting');
                var on = g.list.querySelectorAll('.is-sel');
                for (var i = 0; i < on.length; i++) on[i].classList.remove('is-sel');
            }
            if (g.bar && g.bar.parentNode) g.bar.parentNode.removeChild(g.bar);
            if (g.key) document.removeEventListener('keydown', g.key, true);
            if (!fromBack) backDone('chat-select');
        } catch (e) { logErr('TO_CHAT.select', e); }
    }
    function selToggle(id) {
        if (!selActive() || id == null) return;
        id = String(id);
        /* Aceeași bulă de două ori în 700 ms (apăsarea lungă + clic dreapta nativ / clicul
           de la ridicarea degetului) = o singură bifare. */
        var now = Date.now();
        if (sel.last && sel.last.id === id && now - sel.last.at < 700) return;
        sel.last = { id: id, at: now };
        var row = selRowOf(sel.kind, id);
        if (!row || !sel.list.contains(row) || !rowUsable(sel.kind, row)) return;
        var i = sel.ids.indexOf(id);
        if (i === -1) { if (sel.ids.length >= SEL_MAX) return; sel.ids.push(id); row.classList.add('is-sel'); }
        else { sel.ids.splice(i, 1); row.classList.remove('is-sel'); }
        if (!sel.ids.length) { selEnd(); return; }
        selCount();
    }
    function selCopyText() {
        var kind = sel.kind, rows = [];
        sel.ids.forEach(function (id) {
            var m = kind === 'dm'
                ? ((typeof window._dmMsgById === 'function') ? window._dmMsgById(id) : null)
                : ((typeof window.chatMsgById === 'function') ? window.chatMsgById(id) : null);
            if (m) rows.push(m);
        });
        var tsOf = function (m) { return new Date(kind === 'dm' ? m.created_at : m.timestamp).getTime() || 0; };
        rows.sort(function (a, b) { return tsOf(a) - tsOf(b); });
        var me = myId();
        var bodyOf = function (m) {
            var t = String((kind === 'dm' ? m.body : m.text) || '');
            return t.trim() ? t : ((Array.isArray(m.photos) && m.photos.length) ? tr('dm.photoOnly', '📷 Photo') : '');
        };
        if (rows.length === 1) return bodyOf(rows[0]);
        var loc = (typeof window.appTimeLocale === 'function') ? window.appTimeLocale() : undefined;
        return rows.map(function (m) {
            var d = new Date(tsOf(m)), when = '';
            try { when = d.toLocaleDateString(loc, { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' }); } catch (e) { when = d.toISOString(); }
            var sid = kind === 'dm' ? m.sender_id : m.senderId;
            var who = (sid && me && sid === me) ? ((state.currentUser && state.currentUser.name) || nameById(me))
                : ((kind === 'dm' && window._dmNames && window._dmNames[sid]) || nameById(sid) || m.sender || '');
            return '[' + when + '] ' + who + ': ' + bodyOf(m);
        }).join('\n');
    }
    function selCopy() {
        var many = sel.ids.length > 1, txt = selCopyText();
        var ok = function () { try { window.showNotification(tr(many ? 'chat.copiedMany' : 'chat.copied', many ? 'Messages copied!' : 'Message copied!'), 'success'); } catch (e) { /* noop */ } };
        var bad = function () { try { window.showNotification(tr('chat.copyErr', 'Copy failed'), 'error'); } catch (e) { /* noop */ } };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(txt).then(ok, bad);
            else {
                var ta = document.createElement('textarea');
                ta.value = txt; document.body.appendChild(ta); ta.select();
                var r = document.execCommand('copy'); ta.remove();
                if (r) ok(); else bad();
            }
        } catch (e) { logErr('TO_CHAT.select', e); bad(); }
        selEnd();
    }
    /* 8.4-b: mesajele bifate, redirecționate deodată (app-modules-2.js `msgForward` cu o
       listă: aceeași fereastră „Redirecționează către…", câte un mesaj, în ordinea timpului).
       Selecția se închide (ca în WhatsApp). EN: forward the selected club messages. */
    function selForward() {
        var kind = sel.kind, ids = sel.ids.slice();
        if (!ids.length) return;
        if (kind === 'club' ? typeof window.msgForward !== 'function' : kind !== 'dm') return;
        selEnd();
        /* 8.4-c: și în DM (cererea lui Alex) — chat-forward.js, încărcat la cerere */
        try { if (kind === 'club') window.msgForward(ids); else forwardMsgs('dm', ids); } catch (e) { logErr('TO_CHAT.select', e); }
    }
    function selBar(list) {
        var host = list.parentNode;
        var bar = document.createElement('div');
        bar.className = 'chat-sel-bar';
        bar.setAttribute('role', 'toolbar');
        var cl = esc(tr('common.close', 'Close')), cp = esc(tr('chat.copy', 'Copy')), fw = esc(tr('chat.forward', 'Forward')), dl = esc(tr('chat.delete', 'Delete'));
        /* 8.4-b: „Redirecționează" lângă „Copiază" (cererea lui Alex); 8.4-c: și în DM. */
        var canFwd = sel.kind === 'club' ? typeof window.msgForward === 'function' : sel.kind === 'dm';
        bar.innerHTML = '<button type="button" class="chat-sel-btn" data-action="chat-sel-close" title="' + cl + '" aria-label="' + cl + '"><i class="fas fa-times" aria-hidden="true"></i></button>'
            + '<span class="chat-sel-n" aria-live="polite"></span>'
            + '<button type="button" class="chat-sel-btn" data-action="chat-sel-copy" title="' + cp + '" aria-label="' + cp + '"><i class="fas fa-copy" aria-hidden="true"></i></button>'
            + (canFwd ? '<button type="button" class="chat-sel-btn" data-action="chat-sel-fwd" title="' + fw + '" aria-label="' + fw + '"><i class="fas fa-share" aria-hidden="true"></i></button>' : '')
            /* 8.4-d: 🗑 ascuns până când toate mesajele bifate se pot șterge (`selCount`) */
            + '<button type="button" class="chat-sel-btn is-del" data-action="chat-sel-del" hidden title="' + dl + '" aria-label="' + dl + '"><i class="fas fa-trash" aria-hidden="true"></i></button>';
        bar.addEventListener('click', function (e) {
            var b = e.target && e.target.closest ? e.target.closest('[data-action]') : null;
            if (!b) return;
            if (b.getAttribute('data-action') === 'chat-sel-close') selEnd();
            else if (b.getAttribute('data-action') === 'chat-sel-copy') selCopy();
            else if (b.getAttribute('data-action') === 'chat-sel-fwd') selForward();
            else if (b.getAttribute('data-action') === 'chat-sel-del') selDelete();
        });
        host.appendChild(bar);
        /* Atingerea unui mesaj bifează. Pe părintele listei, faza de CAPTURĂ: rulează
           înaintea ascultătorilor listei (profil, „Citește tot", poze) și îi oprește. */
        if (!host.__toSelClick) {
            host.__toSelClick = true;
            host.addEventListener('click', function (e) {
                if (!selActive() || !sel.list || sel.list.parentNode !== host) return;
                if (!sel.list.contains(e.target)) return;
                e.preventDefault();
                e.stopPropagation();
                var row = e.target.closest ? e.target.closest(rowSel(sel.kind)) : null;
                if (row) selToggle(rowMid(sel.kind, row));
            }, true);
        }
        return bar;
    }
    function selStart(kind, id) {
        try {
            var list = document.getElementById(kind === 'dm' ? 'dmThread' : 'chatMessages');
            if (!list || !list.parentNode) return;
            if (sel.kind) selEnd();
            sel.kind = kind; sel.list = list; sel.ids = []; sel.last = null;
            list.classList.add('is-selecting');
            sel.bar = selBar(list);
            sel.key = function (e) {
                if (e.key !== 'Escape' || !selActive()) return;
                e.preventDefault(); e.stopPropagation();
                selEnd();
            };
            document.addEventListener('keydown', sel.key, true);
            backOpen('chat-select', function () { selEnd(true); });
            selToggle(id);
            if (!sel.kind) return;
            var first = sel.bar.querySelector('[data-action="chat-sel-copy"]');
            if (first) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
        } catch (e) { logErr('TO_CHAT.select', e); selEnd(); }
    }
    /* Bifele revin după redesenare (chemat din `groupRows`). */
    function selPaint(container, plan, rowFn) {
        if (!selActive() || sel.list !== container) return;
        for (var i = 0; i < plan.length; i++) {
            var r = rowFn(plan[i].id);
            if (!r) continue;
            var on = sel.ids.indexOf(plan[i].id) !== -1;
            if (r.classList.contains('is-sel') !== on) r.classList.toggle('is-sel', on);
        }
    }
    TO_CHAT.select = { start: selStart, end: function () { selEnd(); }, active: selActive, toggle: selToggle };

    /* ========================================================================
       8.1-h — CIORNA PĂSTRATĂ (D24: pe telefon, doar pe durata sesiunii)
       ------------------------------------------------------------------------
       RO: Textul scris și netrimis rămâne, per conversație (fiecare club, fiecare
           DM), ca în WhatsApp: la schimbarea taburilor, la închiderea și
           redeschiderea DM-ului, la schimbarea clubului și, de obicei, la
           repornirea paginii de către telefon (aplicația ieșită din ecran).
           • Unde: în memorie + `sessionStorage` (decizia lui Alex, D24) — NU în
             localStorage (regula „nimic privat în localStorage"), NU pe server.
             sessionStorage trăiește doar cât fila / aplicația; se închide complet
             ⇒ se pierde.
           • Legată de contul care a scris-o (id): alt cont n-o vede și o aruncă.
             La ieșirea din cont (`SIGNED_OUT`, orice cale) se șterge tot, inclusiv
             textul rămas în câmpul de club. Demo: doar în memorie.
           • Doar textul (citatul „Răspunde" și pozele alese nu se păstrează).
           • Limite: 50 de conversații (cele mai vechi ies), textul ≤ limita câmpului.
       EN: per-conversation drafts in memory + sessionStorage (D24), bound to the
           account id, wiped on sign-out. */
    var DRAFT_SS = 'to-drafts', DRAFT_MAX = 50;
    var drafts = { uid: null, map: {}, loaded: false, timer: 0 };
    function draftUid() {
        if (typeof isDemoMode === 'function' && isDemoMode()) return 'demo';
        return myId();
    }
    function draftLoad() {
        var uid = draftUid();
        if (drafts.loaded && drafts.uid === uid) return;
        drafts.uid = uid; drafts.map = {}; drafts.loaded = true;
        if (!uid || uid === 'demo') return;
        try {
            var raw = window.sessionStorage.getItem(DRAFT_SS);
            if (!raw) return;
            var o = JSON.parse(raw);
            if (o && o.uid === uid && o.map && typeof o.map === 'object') drafts.map = o.map;
            else window.sessionStorage.removeItem(DRAFT_SS);       /* alt cont ⇒ aruncată */
        } catch (e) { /* stocare indisponibilă (mod privat): rămâne doar memoria */ }
    }
    function draftPersist() {
        drafts.timer = 0;
        if (!drafts.uid || drafts.uid === 'demo') return;
        try {
            if (!Object.keys(drafts.map).length) window.sessionStorage.removeItem(DRAFT_SS);
            else window.sessionStorage.setItem(DRAFT_SS, JSON.stringify({ uid: drafts.uid, map: drafts.map }));
        } catch (e) { /* plină / indisponibilă: rămâne doar memoria */ }
    }
    function draftSet(key, text) {
        if (!key) return;
        draftLoad();
        var t = String(text == null ? '' : text);
        delete drafts.map[key];
        if (t.trim()) {
            drafts.map[key] = t;                                   /* la final = cea mai recentă */
            var ks = Object.keys(drafts.map);
            while (ks.length > DRAFT_MAX) delete drafts.map[ks.shift()];
        }
        if (!drafts.timer) drafts.timer = setTimeout(draftPersist, 400);
    }
    function draftGet(key) { if (!key) return ''; draftLoad(); return drafts.map[key] || ''; }
    function draftFlush() { if (drafts.timer) { clearTimeout(drafts.timer); draftPersist(); } }
    /* Cheia conversației câmpului: clubul curent / persoana din DM-ul deschis. */
    function draftKeyFor(el) {
        if (!el) return null;
        if (el.id === 'chatInput') return (typeof currentClubId !== 'undefined' && currentClubId) ? 'club:' + currentClubId : null;
        if (el.id === 'dmInput') return window._dmOpenWith ? 'dm:' + window._dmOpenWith : null;
        return null;
    }
    /* Textul curent al câmpului, sub cheia conversației LUI (din input / scrieri din cod).
       Câmp încă nelegat (contul / clubul s-au încărcat după legare) ⇒ se leagă acum de
       conversația curentă, cu textul care e deja în el. Alt cont decât cel legat ⇒ nimic. */
    function draftNote(st) {
        try {
            if (!st || st.noDraft) return;
            var uid = draftUid();
            if (!st.dKey && uid) { st.dKey = draftKeyFor(st.el); st.dUid = uid; }
            if (!st.dKey || st.dUid !== uid) return;
            draftSet(st.dKey, st.el.value);
        } catch (e) { logErr('TO_CHAT.draft', e); }
    }
    /* Conversația câmpului s-a schimbat (alt club, alt cont) sau abia s-a aflat ⇒ ciorna
       veche rămâne sub cheia ei (doar dacă e a aceluiași cont), în câmp vine ciorna noii
       conversații. Fără cont / club încă (pornirea) ⇒ câmpul nu se atinge. */
    function draftSwap(st) {
        try {
            if (!st) return;
            var uid = draftUid(), k = uid ? draftKeyFor(st.el) : null;
            if (k === st.dKey && uid === st.dUid) return;
            if (!k && !st.dKey) return;
            if (st.dKey && st.dUid === uid) draftSet(st.dKey, st.el.value);
            st.dKey = k; st.dUid = uid;
            var d = k ? draftGet(k) : '';
            if (st.el.value !== d) { st.noDraft = true; st.el.value = d; st.noDraft = false; }
            linkNote(st);                       /* 8.6: cardul conversației noi (sau niciunul) */
        } catch (e) { logErr('TO_CHAT.draft', e); }
    }
    function draftsClear() {
        if (drafts.timer) { clearTimeout(drafts.timer); drafts.timer = 0; }
        drafts.map = {}; drafts.loaded = false; drafts.uid = null;
        try { window.sessionStorage.removeItem(DRAFT_SS); } catch (e) { /* noop */ }
        /* Și textul rămas în câmpuri (următorul cont de pe dispozitiv nu-l vede). */
        attached.forEach(function (st) {
            if (!st.dKey && st.el.id !== 'chatInput') return;
            st.noDraft = true; st.el.value = ''; st.noDraft = false;
            st.dKey = null; st.dUid = null;
        });
    }
    /* 8.7-C3b: la trecerea în fundal, copiile mici ale pozelor (cluburile mele, persoanele din DM)
       pentru iconița notificărilor. Fișierul (push-icons.js) se încarcă abia acum, doar pentru un
       cont logat cu notificările permise, cel mult o dată la 10 minute — nimic nou la pornire.
       EN: on going to background, lazily refresh the notification icon cache (≤ once / 10 min). */
    var piconsAt = 0;
    function piconsSync() {
        try {
            if (Date.now() - piconsAt < 600000) return;
            if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || !('caches' in window)) return;
            var st = (typeof state !== 'undefined') ? state : null;
            if (!st || !st.currentUser || st.currentUser.isDemo) return;
            piconsAt = Date.now();
            useModule('push-icons.js', 'TO_PICONS', function (m) { m.sync(); }, true);
        } catch (e) { logErr('TO_CHAT.picons', e); }
    }
    try {
        document.addEventListener('visibilitychange', function () { if (document.hidden) { draftFlush(); piconsSync(); } });
        window.addEventListener('pagehide', draftFlush);
        if (typeof sb !== 'undefined' && sb && sb.auth && typeof sb.auth.onAuthStateChange === 'function') {
            sb.auth.onAuthStateChange(function (ev) {
                if (ev === 'SIGNED_IN') { cprefWarm(1500); rxWarm(6000); return; }  /* 8.3-c: și după o autentificare (+ reacțiile, 29 oct.) */
                if (ev !== 'SIGNED_OUT') return;
                draftsClear(); cprefClear();                                   /* 8.8: + aspectul */
                try { if (window.TO_EMOJI && window.TO_EMOJI.clear) window.TO_EMOJI.clear(); } catch (e) { logErr('TO_CHAT.emoji', e); }   /* 8.3: recentele din memorie */
                try { if (window.TO_REACT && window.TO_REACT.clear) window.TO_REACT.clear(); } catch (e) { logErr('TO_CHAT.react', e); }   /* 8.4: reacțiile din memorie + canalele */
                try { if (window.TO_LINK && window.TO_LINK.clear) window.TO_LINK.clear(); } catch (e) { logErr('TO_CHAT.link', e); }     /* 8.6: cardurile din câmpuri */
                piconsAt = 0;                                                  /* 8.7-C3b: pozele pentru notificări */
                try { if (window.TO_PICONS) window.TO_PICONS.clear(); else if ('caches' in window) window.caches.delete('to-icons-v1'); } catch (e) { logErr('TO_CHAT.picons', e); }
                appBadge(0);                                                   /* 8.7-D8: cifra de pe iconiță */
            });
        }
    } catch (e) { logErr('TO_CHAT.draft', e); }
    TO_CHAT.drafts = { get: draftGet, clear: draftsClear, flush: draftFlush };

    /* ── „Văzut" doar cu aplicația ÎN FAȚĂ (29 oct. 2026, cererea lui Alex) ──────
       RO: Defect: cu conversația deschisă și aplicația în fundal / ecranul blocat, un mesaj
           sosit live era marcat „văzut" pe loc (club: `markChatRead`, DM: `markDMRead`), deși
           omul văzuse cel mult notificarea push. Acum, ca la WhatsApp, „văzut" pleacă doar cu
           aplicația chiar în față — aceeași regulă ca la notificările push (supabase-integration.js,
           `push-ping`): ecran tactil ⇒ vizibilă; desktop ⇒ vizibilă ȘI cu focus (fereastra poate
           sta vizibilă în spatele alteia). La revenirea în față (vizibilă / focus) se marchează
           atunci conversația de pe ecran. Decizie: e starea MEA de citire, nu un drept ⇒ fără prag.
       EN: read receipts only while the app is really in front (same rule as push-ping);
           the conversation on screen is marked when the app comes back to front. */
    function inFront() {
        try {
            return document.visibilityState === 'visible' && (document.hasFocus()
                || !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches));
        } catch (e) { return true; }
    }
    function readOnReturn() {
        if (!inFront()) return;
        try { if (window._dmOpenWith && typeof window.markDMRead === 'function') window.markDMRead(window._dmOpenWith); } catch (e) { logErr('TO_CHAT.inFront', e); }
        try { if (typeof currentTab !== 'undefined' && currentTab === 'chat' && typeof window.markChatRead === 'function') window.markChatRead(); } catch (e) { logErr('TO_CHAT.inFront', e); }
    }
    try {
        document.addEventListener('visibilitychange', readOnReturn);
        window.addEventListener('focus', readOnReturn);
    } catch (e) { logErr('TO_CHAT.inFront', e); }
    TO_CHAT.inFront = inFront;

    /* ========================================================================
       8.8 — ASPECTUL CONVERSAȚIILOR (club + DM), pe CONT
       ------------------------------------------------------------------------
       RO: Culoarea bulelor mele, fundalul conversației și mărimea textului, alese din
           fereastra „Aspect" (⋮ în club și în DM; codul ferestrei: chat-prefs.js,
           încărcat LA CERERE prin `ensureModule`).
           • Decizia lui Alex (D12): stocare SEPARATĂ, doar a ta (V9_88_PREFERINTE_CHAT:
             tabel fără acces direct, `my_chat_prefs` / `set_my_chat_prefs`); GLOBALE +
             per conversație: un rând `all` și, opțional, câte unul `club:<id>` / `dm:<id>`;
             pe fiecare câmp, conversația bate globalul, gol = implicit.
           • Doar IDENTIFICATORI din lista albă (aceeași ca pe server), niciodată CSS;
             ce nu e în listă se ignoră. Aplicarea = atribute pe rădăcina conversației
             (`data-cp-*`, chat.css), fără redesenare.
           • Fără cereri la pornire: o copie locală (`localStorage`, doar identificatori
             de aspect, legată de contul care i-a ales) aplică aspectul imediat; serverul
             se întreabă o dată pe sesiune, la PRIMA conversație deschisă (club sau DM) ⇒
             schimbările de pe alt dispozitiv ajung la următoarea deschidere. La ieșirea
             din cont copia se șterge. Demo: doar în memorie, zero trafic.
       EN: per-account chat appearance (own bubbles, background, text size): whitelisted
           ids → data attributes on the conversation root; local mirror + one server
           read per session on first chat open; the dialog is lazy-loaded. */
    /* 8.8-b: `def` = aspectul DE BAZĂ ales explicit pentru o conversație (bulă, fundal;
       mărimea are deja `m`). Înainte, „Implicit” ales într-un DM salva gol = „ca la
       toate conversațiile”, deci culoarea generală rămânea (defectul raportat de Alex).
       Pe rândul `all`, `def` = gol. Fundalurile luminoase pun și `data-cp-tone="light"`
       (chat.css: bulele celorlalți albe, text închis). Aspectul din copia locală se pune
       ÎNAINTE de autentificare (cprefEarly). EN: explicit base look per conversation,
       light variants, early apply from the local mirror. */
    var CP_BUB =['felt', 'wood', 'ocean', 'berry', 'ruby', 'slate', 'teal', 'plum', 'mint', 'sky', 'sand', 'lilac'];
    var CP_BG = ['felt', 'wood', 'dice', 'meeple', 'hex', 'cards', 'night', 'slate', 'score', 'parch', 'maple', 'mist'];
    var CP_LIGHT_BUB = ['mint', 'sky', 'sand', 'lilac'], CP_LIGHT_BG = ['score', 'parch', 'maple', 'mist'];
    var CP_DEF = 'def';                                         /* 8.8-b: aspectul de bază, ales explicit (doar per conversație) */
    var CP_SIZE = ['s', 'm', 'l', 'xl'];
    var CP_LS = 'to-cpref';
    var CP_SCOPE_RE = /^(club|dm):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
    var cpref = { uid: null, rows: {}, loaded: false, synced: null, busy: null, gen: 0, soon: false, early: false, lastClub: null };
    function cprefScopeOk(s) { return s === 'all' || CP_SCOPE_RE.test(String(s || '')); }
    /* `scope` = al cui e rândul: `def` contează doar pe o conversație (pe `all` = gol). */
    function cprefClean(r, scope) {
        if (!r || typeof r !== 'object') return null;
        var conv = scope !== 'all';
        var pick = function (v, list) { return list.indexOf(v) !== -1 ? v : ((conv && v === CP_DEF) ? CP_DEF : null); };
        var o = {
            bubble: pick(r.bubble, CP_BUB),
            bg: pick(r.bg, CP_BG),
            size: CP_SIZE.indexOf(r.size) !== -1 ? r.size : null
        };
        return (o.bubble || o.bg || o.size) ? o : null;
    }
    function cprefLoad() {
        var uid = draftUid();
        if (cpref.loaded && cpref.uid === uid) { cpref.early = false; return; }
        if (cpref.loaded && cpref.early && !uid) return;        /* contul încă neștiut: rămâne copia de la pornire */
        cpref.uid = uid; cpref.rows = {}; cpref.loaded = true; cpref.synced = null; cpref.early = false;
        if (!uid || uid === 'demo') return;
        try {
            var raw = window.localStorage.getItem(CP_LS);
            if (!raw) return;
            var o = JSON.parse(raw);
            if (o && o.uid === uid && o.rows && typeof o.rows === 'object') {
                Object.keys(o.rows).forEach(function (k) { var c = cprefClean(o.rows[k], k); if (c && cprefScopeOk(k)) cpref.rows[k] = c; });
                if (cprefScopeOk(o.club)) cpref.lastClub = o.club;
            } else window.localStorage.removeItem(CP_LS);          /* alt cont ⇒ aruncată */
        } catch (e) { /* stocare indisponibilă: rămâne implicitul până la server */ }
    }
    function cprefSave() {
        if (!cpref.uid || cpref.uid === 'demo') return;
        try {
            if (!Object.keys(cpref.rows).length) window.localStorage.removeItem(CP_LS);
            else window.localStorage.setItem(CP_LS, JSON.stringify({ uid: cpref.uid, rows: cpref.rows, club: cpref.lastClub }));
        } catch (e) { /* noop */ }
    }
    function cprefResolve(scope) {
        cprefLoad();
        var g = cpref.rows.all || {}, s = (scope && cpref.rows[scope]) || {};
        /* pe fiecare câmp: conversația bate globalul; `def` = aspectul de bază (nu globalul) */
        var f = function (k) { var v = s[k]; return v === CP_DEF ? null : (v || g[k] || null); };
        return { bubble: f('bubble'), bg: f('bg'), size: f('size') };
    }
    function cprefSetAttrs(root, r) {
        var tone = (r.bg && CP_LIGHT_BG.indexOf(r.bg) !== -1) ? 'light' : null;
        [['data-cp-bub', r.bubble], ['data-cp-bg', r.bg], ['data-cp-size', r.size], ['data-cp-tone', tone]].forEach(function (p) {
            if (p[1]) { if (root.getAttribute(p[0]) !== p[1]) root.setAttribute(p[0], p[1]); }
            else if (root.hasAttribute(p[0])) root.removeAttribute(p[0]);
        });
    }
    function cprefScopes() {
        var club = (typeof currentClubId !== 'undefined' && currentClubId) ? 'club:' + String(currentClubId).toLowerCase() : null;
        var dm = (openDmModal() && window._dmOpenWith) ? 'dm:' + String(window._dmOpenWith).toLowerCase() : null;
        return { club: cprefScopeOk(club) ? club : null, dm: cprefScopeOk(dm) ? dm : null };
    }
    function cprefApply() {
        try {
            var sc = cprefScopes();
            var card = document.getElementById('chatCard');
            if (card) cprefSetAttrs(card, cprefResolve(sc.club));
            /* ultimul club văzut, în copia locală: la pornire, aspectul lui apare de la început */
            if (sc.club && sc.club !== cpref.lastClub && cpref.uid && !cpref.early) { cpref.lastClub = sc.club; cprefSave(); }
            var dm = openDmModal();
            if (dm) cprefSetAttrs(dm, cprefResolve(sc.dm));
        } catch (e) { logErr('TO_CHAT.cpref', e); }
    }
    /* O dată pe sesiune (pe cont), la prima conversație văzută; `force` = din fereastra Aspect. */
    function cprefSync(force) {
        cprefLoad();
        var uid = cpref.uid;
        if (!uid || uid === 'demo' || typeof sb === 'undefined' || !sb || typeof sb.rpc !== 'function') return Promise.resolve(false);
        if (!force && cpref.synced === uid) return Promise.resolve(true);
        if (cpref.busy) return cpref.busy;
        cpref.synced = uid;                                     /* o încercare pe sesiune, nu la fiecare desenare */
        var gen = cpref.gen;
        cpref.busy = Promise.resolve(sb.rpc('my_chat_prefs')).then(function (r) {
            if (r && r.error) throw r.error;
            if (draftUid() !== uid) return false;
            /* O salvare făcută între timp e mai nouă decât citirea asta: n-o călca. */
            if (cpref.gen !== gen) return false;
            var rows = {};
            ((r && r.data) || []).forEach(function (x) { var c = x ? cprefClean(x, x.scope) : null; if (c && cprefScopeOk(x.scope)) rows[x.scope] = c; });
            cpref.rows = rows;
            cprefSave();
            cprefApply();
            return true;
        }).catch(function (e) {
            if (!(TO_CHAT.msg && TO_CHAT.msg.rpcMissing && TO_CHAT.msg.rpcMissing(e))) logErr('TO_CHAT.cpref', e);
            return false;
        }).then(function (v) { cpref.busy = null; return v; });
        return cpref.busy;
    }
    /* Citirea de pe server NU pe calea pornirii: dacă aplicația pornește direct pe Chat
       (reîncărcare pe #chat), loadChat rulează în pornire ⇒ citirea se amână până când
       telefonul e liber (requestIdleCallback; rezervă setTimeout: Safari nu-l are) și se
       face doar dacă chatul e încă pe ecran. EN: deferred off the startup path. */
    function cprefSyncSoon() {
        cprefLoad();
        if (cpref.soon || !cpref.uid || cpref.uid === 'demo' || cpref.synced === cpref.uid) return;
        cpref.soon = true;
        var run = function () { cpref.soon = false; try { if (chatVisible()) cprefSync(); } catch (e) { logErr('TO_CHAT.cpref', e); } };
        /* 8.8-b: plafon 1 s (era 2 s) / 400 ms fără requestIdleCallback (era 1 s): o schimbare
           făcută pe alt dispozitiv apare mai repede. */
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 1000 });
        else setTimeout(run, 400);
    }
    /* 8.3-c — fără „aspectul implicit, apoi al tău" (cerința lui Alex). Conversația se desenează
       mereu din copia de pe dispozitiv; clipirea apărea când copia lipsea sau era veche (aspect
       schimbat pe alt dispozitiv, ieșire și reintrare în cont, aplicația instalată la prima
       deschidere): citirea de pe server venea abia la 0,4–1 s DUPĂ intrarea în chat.
       Ca în WhatsApp (setările sunt deja pe dispozitiv când deschizi chatul):
       • citirea se face DEVREME, în timp liber, la ~3 s după ce contul e știut — nu pe calea
         pornirii; o cerere mică pe sesiune (`my_chat_prefs`, ≤ 200 de rânduri de identificatori);
       • dacă intri în chat exact cât citirea e în curs, conversația așteaptă cel mult 0,7 s,
         ascunsă (`data-cp-wait`), în loc să se arate cu aspectul implicit.
       EN: prefetch the appearance early (idle, ~3 s after login); briefly hold the conversation
           while that read is in flight instead of flashing the default look. */
    var cprefWarmT = 0, cprefWarmTries = 0;
    function cprefWarm(delay) {
        clearTimeout(cprefWarmT);
        cprefWarmT = setTimeout(function () {
            var run = function () {
                try {
                    var u = draftUid();
                    if (u && u !== 'demo') { cprefWarmTries = 0; cprefSync(); return; }
                    if (!u && ++cprefWarmTries <= 5) cprefWarm(4000);   /* contul încă nu e știut */
                } catch (e) { logErr('TO_CHAT.cpref', e); }
            };
            if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 });
            else run();
        }, delay || 3000);
    }
    var CP_HOLD_MS = 700;
    function cprefHold(root) {
        if (!root || !cpref.busy) return;
        if (!root.hasAttribute('data-cp-wait')) root.setAttribute('data-cp-wait', '');
        var done = function () { if (root.hasAttribute('data-cp-wait')) root.removeAttribute('data-cp-wait'); };
        cpref.busy.then(done, done);
        setTimeout(done, CP_HOLD_MS);
    }
    /* Salvarea (fereastra Aspect): ÎNTÂI serverul, apoi memoria, copia locală și ecranul. */
    function cprefPut(scope, row) {
        cprefLoad();
        if (!cprefScopeOk(scope)) return Promise.reject(new Error('cpref scope'));
        var c = cprefClean(row, scope);
        var commit = function () {
            cpref.gen++;
            if (c) cpref.rows[scope] = c; else delete cpref.rows[scope];
            cprefSave();
            cprefApply();
            return true;
        };
        if (cpref.uid === 'demo') return Promise.resolve(commit());
        if (!cpref.uid || typeof sb === 'undefined' || !sb) return Promise.reject(new Error('cpref auth'));
        return Promise.resolve(sb.rpc('set_my_chat_prefs', {
            p_scope: scope, p_bubble: c ? c.bubble : null, p_bg: c ? c.bg : null, p_size: c ? c.size : null
        })).then(function (r) { if (r && r.error) throw r.error; return commit(); });
    }
    /* Pornirea: copia locală se pune pe card ÎNAINTE să se știe contul (altfel cardul
       gol se vedea cu fundalul implicit până la primul loadChat). Copia e a ultimului cont
       (se șterge la ieșire); cprefLoad o confirmă sau o aruncă imediat ce contul e știut. */
    function cprefEarly() {
        if (cpref.loaded || draftUid()) return;
        try {
            var o = JSON.parse(window.localStorage.getItem(CP_LS) || 'null');
            if (!o || !o.uid || !o.rows || typeof o.rows !== 'object') return;
            var rows = {};
            Object.keys(o.rows).forEach(function (k) { var c = cprefClean(o.rows[k], k); if (c && cprefScopeOk(k)) rows[k] = c; });
            cpref.uid = String(o.uid); cpref.rows = rows; cpref.loaded = true; cpref.early = true; cpref.synced = null;
            cpref.lastClub = cprefScopeOk(o.club) ? o.club : null;
            var card = document.getElementById('chatCard');
            if (card) cprefSetAttrs(card, cprefResolve(cpref.lastClub));
        } catch (e) { /* stocare indisponibilă: drumul normal */ }
    }
    function cprefClear() {
        cpref.rows = {}; cpref.loaded = false; cpref.uid = null; cpref.synced = null; cpref.early = false; cpref.lastClub = null;
        try { window.localStorage.removeItem(CP_LS); } catch (e) { /* noop */ }
        try {
            [document.getElementById('chatCard'), document.getElementById('dmModal')].forEach(function (r) {
                if (r) cprefSetAttrs(r, { bubble: null, bg: null, size: null });
            });
        } catch (e) { /* noop */ }
    }

    /* ── Fișierele NOASTRE încărcate la cerere (KB §75.4) ─────────────────────
       RO: Primul fișier de felul ăsta: chat-prefs.js. Nu e în index.html; e în
           APP_SHELL (merge și fără internet, din același precache) și în HASHED.
           ⚠ Decalajul de versiune: după un deploy, o pagină VECHE ar primi fișierul NOU
           peste restul codului vechi. Fișierul poartă ștampila `turnorder-<hash>` pusă
           de build-cache.js; dacă nu e cea din `window.TO_RELEASE`, nu-l folosim și
           cerem reîncărcarea paginii. Promisiune memorată; la eșec se poate reîncerca.
       EN: lazy loader for our own files, with release-stamp check. */
    var modLoad = {};
    function verErr() { var e = new Error('module version'); e.isVersion = true; return e; }
    function ensureModule(file, name) {
        var rel = (typeof window.TO_RELEASE === 'string') ? window.TO_RELEASE : '';
        var have = window[name];
        if (have) return (rel && have.stamp !== rel) ? Promise.reject(verErr()) : Promise.resolve(have);
        if (modLoad[file]) return modLoad[file];
        modLoad[file] = new Promise(function (res, rej) {
            var s = document.createElement('script');
            /* T-1 (29 oct. 2026): aceeași adresă ca în APP_SHELL (`file?v=<hash>`, hash-ul din
               TO_RELEASE). Fără ?v=, imediat după un deploy SW-ul vechi dădea fișierul VECHI din
               cache (refuzat ⇒ „Reîncarcă?" deși pagina era deja nouă). Cu ?v= îl găsește în
               cache-ul nou sau îl ia din rețea. EN: request the same versioned URL as APP_SHELL. */
            var vq = /^turnorder-([a-z0-9]+)$/.exec(rel);
            s.src = file + (vq ? '?v=' + vq[1] : '');
            s.async = true;
            s.onload = function () {
                var m = window[name];
                if (!m) { rej(new Error('module ' + file)); return; }
                if (rel && m.stamp !== rel) { rej(verErr()); return; }
                res(m);
            };
            s.onerror = function () { rej(new Error('load ' + file)); };
            (document.head || document.body).appendChild(s);
        });
        modLoad[file].catch(function () { delete modLoad[file]; });
        return modLoad[file];
    }
    /* v9 8.3 — „încarcă, apoi cheamă" (tournaments.js, emoji): `fn(modul)` doar după
       încărcare; la eșec întoarce null. `quiet` = apel de fundal (ex. statisticile de
       turneu dintr-un profil): fără dialog / mesaj — doar jurnalul. Altfel: versiune
       amestecată ⇒ „Reîncarcă?"; fără internet ⇒ mesaj clar.
       EN: load-then-call helper; quiet mode for background calls. */
    function useModule(file, name, fn, quiet) {
        return ensureModule(file, name).then(function (m) {
            try { if (fn) fn(m); } catch (e) { logErr('TO_CHAT.useModule', e); }
            return m;
        }, function (e) {
            if (!(e && e.isVersion)) logErr('TO_CHAT.useModule', e);
            if (!quiet) {
                if (e && e.isVersion) {
                    if (typeof window.confirmModal === 'function') {
                        window.confirmModal(tr('lazy.reload', 'A new version of the app is available. Reload now?')).then(function (y) { if (y) location.reload(); });
                    }
                } else if (typeof window.showNotification === 'function') {
                    window.showNotification(tr('lazy.loadErr', 'Could not load this part of the app. Check your connection.'), 'error');
                }
            }
            return null;
        });
    }
    function cprefOpen(kind) {
        var sc = cprefScopes();
        var scope = kind === 'dm' ? sc.dm : sc.club;
        return ensureModule('chat-prefs.js', 'TO_CPREF').then(function (M) {
            cprefSync(true);
            M.open({ kind: kind === 'dm' ? 'dm' : 'club', scope: scope });
        }, function (e) {
            if (e && e.isVersion) {
                if (typeof window.confirmModal === 'function') {
                    window.confirmModal(tr('cpref.reload', 'A new version of the app is available. Reload now?')).then(function (y) { if (y) location.reload(); });
                }
                return;
            }
            logErr('TO_CHAT.cpref', e);
            if (typeof window.showNotification === 'function') window.showNotification(tr('cpref.loadErr', 'Could not open Appearance. Check your connection.'), 'error');
        });
    }
    TO_CHAT.ensureModule = ensureModule;
    TO_CHAT.useModule = useModule;
    /* 8.16-3d (31 oct. 2026): modulul e DEJA în pagină și e de ACEEAȘI versiune cu pagina? Întoarce
       modulul sau null. Pentru punțile care cheamă direct un fișier la cerere (polls.js): un modul
       REFUZAT (altă versiune, după un deploy) rămâne totuși în pagină — un script clasic își lasă
       funcțiile globale —, deci „funcția există" nu e de ajuns (KB §81.12). Fără TO_RELEASE (copie
       locală, teste) orice modul încărcat e bun, ca la `ensureModule`. EN: loaded AND same release. */
    TO_CHAT.readyModule = function (name) {
        var m = window[name], rel = (typeof window.TO_RELEASE === 'string') ? window.TO_RELEASE : '';
        return (m && (!rel || m.stamp === rel)) ? m : null;
    };
    TO_CHAT.cpref = {
        lists: { bubble: CP_BUB.slice(), bg: CP_BG.slice(), size: CP_SIZE.slice(), lightBubble: CP_LIGHT_BUB.slice(), lightBg: CP_LIGHT_BG.slice(), def: CP_DEF },
        get: function (scope) { cprefLoad(); var r = cpref.rows[scope]; return r ? { bubble: r.bubble, bg: r.bg, size: r.size } : null; },
        resolve: cprefResolve,
        put: cprefPut,
        apply: cprefApply,
        sync: cprefSync,
        open: cprefOpen,
        clear: cprefClear,
        scopeOk: cprefScopeOk
    };

    /* ========================================================================
       8.3 — BIBLIOTECA DE EMOJI: legătura din chat-core (club + DM)
       ------------------------------------------------------------------------
       RO: Selectorul (foaia ca o tastatură: căutare RO + EN, categorii, recente pe
           cont, nuanțe de piele) stă în chat-emoji.js, ÎNCĂRCAT LA CERERE la prima
           apăsare pe 😊 (pre-încărcat de la `pointerdown`); grila în emoji-data.js
           (precache), numele în emoji-names.js (doar la prima căutare). Aici, la
           pornire, doar:
           • butoanele 😊 — club `data-action="chat-emoji"`, DM `dm-emoji` (social.js);
             `pointerdown` + `preventDefault`: câmpul nu pierde focusul, deci aflăm
             înălțimea tastaturii înainte s-o închidem (foaia îi ia locul);
           • testul de afișare (detecție de SUPORT, nu de browser): un emoji color iese
             identic desenat cu alb și cu negru, literele / pătrățelul nu (tehnica din
             country-flag-emoji-polyfill, MIT); o secvență nesuportată iese lată;
           • steagurile: Windows (Chrome / Edge) le arată ca litere („RO") ⇒ clasa
             `to-flagfont` pe <html> și fontul nostru de steaguri (chat.css, 78 kB,
             descărcat de browser DOAR când apare un steag);
           • emoji-urile mari: un mesaj doar cu 1–3 emoji se vede mărit (ca în WhatsApp).
           Rezervă: fișierul nu se poate încărca (fără internet, niciodată deschis) ⇒
           selectorul vechi de 20 (app-modules-2.js), ca înainte.
       EN: emoji bridge — the picker is lazy-loaded; support tests, flag font, jumbo. */
    var EMO_FILE = 'chat-emoji.js';
    var EMO_FONTS = '"Twemoji Mozilla","Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji","EmojiOne Color","Android Emoji",sans-serif';
    var emo = { kb: 0, cv: null, flag: 0 };
    function emoKbNow() {
        try { var v = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--kb-height')); return v > 0 ? v : 0; } catch (e) { return 0; }
    }
    function emoCtx(kind) {
        var dm = kind === 'dm';
        var root = dm ? document.querySelector('#dmModal .dm-panel') : document.querySelector('#chatCard .chat-container');
        var input = document.getElementById(dm ? 'dmInput' : 'chatInput');
        var area = root ? root.querySelector('.chat-input-area') : null;
        var btn = root ? root.querySelector(dm ? '[data-action="dm-emoji"]' : '[data-action="chat-emoji"]') : null;
        return (root && input && area) ? { kind: dm ? 'dm' : 'club', root: root, input: input, area: area, btn: btn } : null;
    }
    function emoPrefetch() {
        try { ensureModule(EMO_FILE, 'TO_EMOJI').then(function (M) { if (M && M.prefetch) M.prefetch(); }, function () { /* se raportează la apăsare */ }); } catch (e) { /* noop */ }
    }
    function emoToggle(kind) {
        var M = window.TO_EMOJI;
        if (M && M.isOpen && M.isOpen(kind)) { M.close({ focus: true }); return; }
        var ctx = emoCtx(kind);
        if (!ctx) return;
        var k = emoKbNow();
        if (k) emo.kb = k;                               /* tastatura e deschisă acum: foaia îi ia locul */
        ensureModule(EMO_FILE, 'TO_EMOJI').then(function (Mod) {
            Mod.open(ctx, { kb: emo.kb });
        }, function (e) {
            if (e && e.isVersion) {
                if (typeof window.confirmModal === 'function') window.confirmModal(tr('lazy.reload', 'A new version of the app is available. Reload now?')).then(function (y) { if (y) location.reload(); });
                return;
            }
            logErr('TO_CHAT.emoji', e);
            /* 8.5-E: fără rezervă (selectorul vechi de 20, scos): biblioteca nu se poate încărca ⇒
               spunem asta; emoji-urile de pe tastatura telefonului merg oricum. */
            if (typeof window.showNotification === 'function') window.showNotification(tr('lazy.loadErr', 'Could not load this part of the app. Check your connection.'), 'error');
        });
    }
    try {
        document.addEventListener('pointerdown', function (e) {
            var b = e.target && e.target.closest ? e.target.closest('[data-action="chat-emoji"], [data-action="dm-emoji"]') : null;
            if (!b) return;
            e.preventDefault();
            emoPrefetch();
        }, true);
    } catch (e) { logErr('TO_CHAT.emoji', e); }
    function emoCanvas() {
        if (emo.cv !== null) return emo.cv;
        emo.cv = false;
        try {
            var c = document.createElement('canvas');
            c.width = c.height = 1;
            var x = c.getContext ? c.getContext('2d', { willReadFrequently: true }) : null;
            if (x && typeof x.getImageData === 'function') {
                x.textBaseline = 'top';
                x.font = '100px ' + EMO_FONTS;
                x.scale(0.01, 0.01);
                emo.cv = x;
            }
        } catch (e) { emo.cv = false; }
        return emo.cv;
    }
    function emoPix(x, s, col) {
        x.clearRect(0, 0, 100, 100);
        x.fillStyle = col;
        x.fillText(s, 0, 0);
        return x.getImageData(0, 0, 1, 1).data.join(',');
    }
    /* Se vede ca emoji color (nu ca litere / pătrățel)? Fără canvas (teste, browsere
       foarte vechi) ⇒ „da" (nu ascundem nimic). */
    function emoRenders(s) {
        var x = emoCanvas();
        if (!x) return true;
        try { var w = emoPix(x, s, '#fff'), b = emoPix(x, s, '#000'); return w === b && b.indexOf('0,0,0,') !== 0; } catch (e) { return true; }
    }
    /* O secvență (ZWJ) nesuportată se desenează ca mai multe glife ⇒ mai lată decât 😀. */
    function emoWide(s) {
        var x = emoCanvas();
        if (!x) return false;
        try { var r = x.measureText(String.fromCodePoint(0x1F600)).width; return r > 0 && x.measureText(s).width > r * 1.5; } catch (e) { return false; }
    }
    function flagCheck() {
        if (emo.flag) return emo.flag;
        emo.flag = 1;
        try {
            if (emoRenders(String.fromCodePoint(0x1F60A)) && !emoRenders(String.fromCodePoint(0x1F1E8, 0x1F1ED))) {
                emo.flag = 2;
                document.documentElement.classList.add('to-flagfont');
            }
        } catch (e) { emo.flag = 1; }
        return emo.flag;
    }
    /* Mesajul e DOAR 1–3 emoji? (grafeme, cu `Intl.Segmenter`; fără el ⇒ textul normal).
       Expresiile sunt construite în `try`: un browser fără `\p{…}` nu pierde tot fișierul. */
    var emoRe = null;
    try {
        emoRe = {
            ri: new RegExp(String.raw`[\u{1F1E6}-\u{1F1FF}]`, 'u'),
            one: new RegExp(String.raw`^(?:\p{Regional_Indicator}\p{Regional_Indicator}|[#*0-9]\u{FE0F}?\u{20E3}|\p{Extended_Pictographic}[\u{FE0F}\p{Emoji_Modifier}\u{E0020}-\u{E007F}]*(?:\u{200D}\p{Extended_Pictographic}[\u{FE0F}\p{Emoji_Modifier}]*)*)$`, 'u'),
            seg: (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null
        };
    } catch (e) { emoRe = null; }
    function jumboN(s) {
        if (!emoRe || !emoRe.seg || !s || s.length > 64) return 0;
        var t = s.trim();
        if (!t) return 0;
        var n = 0, it = emoRe.seg.segment(t)[Symbol.iterator](), r;
        while (!(r = it.next()).done) {
            var g = r.value.segment;
            if (!g.trim()) continue;
            if (!emoRe.one.test(g) || ++n > 3) return 0;
        }
        return n;
    }
    TO_CHAT.emoji = {
        toggle: emoToggle,
        prefetch: emoPrefetch,
        ctx: emoCtx,
        renders: emoRenders,
        wide: emoWide,
        flagCheck: flagCheck,
        jumbo: jumboN,
        kb: function (v) { if (v > 0) emo.kb = v; return emo.kb; }
    };

    /* ── Reacțiile (v9 8.4) — chat-react.js, încărcat la cerere ─────────────────
       RO: Listele (app-modules-2.js `_chatRowHtml` / `loadChat`, social.js
           `renderDMThread`) cer aici cheia (pentru semnătură), clasa și pastila
           fiecărui mesaj. Fișierul se încarcă la PRIMA desenare cu o conversație pe
           ecran — nimic la pornire dacă pe ecran nu e chatul. Până atunci (și fără
           fișier) răspunsurile sunt goale ⇒ listele arată exact ca înainte. La eșec
           (ex. fără internet), altă încercare după 30 s.
       EN: reactions bridge; lazy-loads chat-react.js the first time a conversation
           is on screen; empty answers until then. */
    var RX_FILE = 'chat-react.js';
    var rxl = { busy: false, at: 0 };
    function rxMod() { var M = window.TO_REACT; return (M && typeof M.key === 'function') ? M : null; }
    /* chatul CLUBULUI pe ecran (fără DM-ul deschis peste altă pagină, ca `chatVisible`) */
    function rxClubShown() {
        if (isFs(document.getElementById('chatCard'))) return true;
        var tab = document.getElementById('tab-chat');
        return !!(tab && tab.classList.contains('active'));
    }
    function rxLoad(kind) {
        if (rxl.busy || Date.now() - rxl.at < 30000) return;
        if (kind === 'dm' ? !openDmModal() : !rxClubShown()) return;
        rxl.busy = true;
        ensureModule(RX_FILE, 'TO_REACT').then(function (M) {
            try { M.start(); } catch (e) { logErr('TO_CHAT.react', e); }
        }, function (e) {
            rxl.busy = false; rxl.at = Date.now();
            if (!(e && e.isVersion)) logErr('TO_CHAT.react', e);
        });
    }
    /* 29 oct. 2026 (cererea lui Alex): preîncărcarea reacțiilor în timp liber, la ~6 s după
       pornire / autentificare (chat-react.js `prefetch`) ⇒ la PRIMA deschidere a chatului / a
       unui DM pastilele apar odată cu mesajele. Fără cont, în demo, fără internet sau cu
       „Economizor de date" ⇒ nimic. Mesajele încă neîncărcate ⇒ altă încercare (cel mult 5).
       EN: idle-time reactions prefetch ~6 s after start / sign-in. */
    var rxWarmT = 0, rxWarmTries = 0;
    function rxWarm(delay) {
        clearTimeout(rxWarmT);
        rxWarmT = setTimeout(function () {
            var run = function () {
                try {
                    var u = draftUid();
                    if (!u || u === 'demo') { if (!u && ++rxWarmTries <= 5) rxWarm(4000); return; }
                    if (navigator.onLine === false || (navigator.connection && navigator.connection.saveData)) return;
                    var have = (typeof state !== 'undefined' && state && state.chatMessages && state.chatMessages.length) || (window._dmAll && window._dmAll.length);
                    if (!have) { if (++rxWarmTries <= 5) rxWarm(4000); return; }
                    rxWarmTries = 0;
                    var go = function (M) { try { M.start(); if (typeof M.prefetch === 'function') M.prefetch(); } catch (e) { logErr('TO_CHAT.rxWarm', e); } };
                    var M = rxMod();
                    if (M) { go(M); return; }
                    if (rxl.busy) return;
                    rxl.busy = true;
                    ensureModule(RX_FILE, 'TO_REACT').then(go, function (e) {
                        rxl.busy = false; rxl.at = Date.now();
                        if (!(e && e.isVersion)) logErr('TO_CHAT.rxWarm', e);
                    });
                } catch (e) { logErr('TO_CHAT.rxWarm', e); }
            };
            if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 3000 });
            else run();
        }, delay || 6000);
    }
    TO_CHAT.react = {
        key: function (kind, id) { var M = rxMod(); if (M) return M.key(kind, id); rxLoad(kind); return ''; },
        cls: function (kind, id) { var M = rxMod(); return M ? M.cls(kind, id) : ''; },
        html: function (kind, id) { var M = rxMod(); return M ? M.html(kind, id) : ''; },
        visible: rxClubShown
    };

    /* ── Redirecționarea (v9 8.4-c) — chat-forward.js, încărcat la cerere ───────
       RO: Club și DM, unul sau mai multe mesaje, și cu poze (cererea lui Alex). Fără
           fișier (ex. fără internet la prima folosire) ⇒ în club calea veche (doar text,
           app-modules-2.js `_msgForwardText`), în DM un mesaj clar.
       EN: forwarding bridge (lazy module) with the old text-only path as fallback. */
    var FWD_FILE = 'chat-forward.js';
    function forwardMsgs(kind, ids, fallback) {
        ensureModule(FWD_FILE, 'TO_FWD').then(function (M) { M.open(kind, ids); }, function (e) {
            if (e && e.isVersion) {
                if (typeof window.confirmModal === 'function') window.confirmModal(tr('lazy.reload', 'A new version of the app is available. Reload now?')).then(function (y) { if (y) location.reload(); });
                return;
            }
            logErr('TO_CHAT.forward', e);
            if (typeof fallback === 'function') { try { fallback(ids); } catch (x) { logErr('TO_CHAT.forward', x); } }
            else if (typeof window.showNotification === 'function') window.showNotification(tr('lazy.loadErr', 'Could not load this part of the app. Check your connection.'), 'error');
        });
    }
    TO_CHAT.forward = forwardMsgs;

    /* ══ 8.6 — PREVIZUALIZAREA LINKURILOR (club + DM, 22 oct.) ════════════════════
       RO: Cardul unui link (titlu, descriere, imagine, domeniu) îl face EXPEDITORUL,
           înainte de trimitere (chat-link.js, la cerere, prin funcția `link-preview` de
           pe server), și pleacă în mesaj (`link_preview`, verificat de server —
           V9_86B_LINK_PREVIEW.sql). Cei care citesc nu cer nimic nimănui; imaginea stă în
           dosarul conversației, cu aceleași reguli ca pozele (în DM: doar cei doi).
           Aici rămâne doar ce trebuie la desenare (cardul din bulă, fără clipire) și
           legătura cu câmpul: modulul se încarcă abia când textul conține „http".
           • Domeniul afișat vine MEREU din adresă (titlul îl alege expeditorul).
           • Adresa se verifică din nou aici, cu aceeași listă de caractere ca serverul;
             orice nepotrivire ⇒ fără card (textul cu linkul rămâne, ca înainte).
           • Cardul NU e `<a>`: pe Android, apăsarea lungă pe un link deschide meniul
             browserului în locul meniului mesajului. O atingere (sau Enter) deschide
             linkul într-o filă nouă, fără să trimită adresa aplicației (`noreferrer`).
       EN: link cards — made by the sender (lazy chat-link.js), stored in the message and
           validated by the server; here: the bubble renderer + the composer hook. */
    var LP_FILE = 'chat-link.js';
    var LP_URL = /^https?:\/\/[A-Za-z0-9._~:\/?#@!$&()*+,;=%\[\]-]+$/;
    var LP_YT = /^[A-Za-z0-9_-]{11}$/;
    var LP_IMG = /^(?:club|dm)\/[A-Za-z0-9\/_.-]+\.jpg$/;
    var lpOffUntil = 0;
    function lpHost(u) { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (e) { return ''; } }
    function lpOk(u) { return typeof u === 'string' && u.length <= 2048 && LP_URL.test(u) && !!lpHost(u); }
    function lpStr(v, max) { return typeof v === 'string' ? v.slice(0, max) : ''; }
    /* Forma din bază / din câmp ⇒ obiectul curat (sau null). */
    function lpOf(v) {
        if (!v || typeof v !== 'object' || !lpOk(v.u)) return null;
        var o = { u: v.u }, t = lpStr(v.t, 200), d = lpStr(v.d, 300), s = lpStr(v.s, 80);
        if (t) o.t = t;
        if (d) o.d = d;
        if (s) o.s = s;
        if (typeof v.y === 'string' && LP_YT.test(v.y)) o.y = v.y;
        if (typeof v.i === 'string' && LP_IMG.test(v.i) && v.i.indexOf('..') === -1) o.i = v.i;
        return o;
    }
    /* Cardul din bulă. `local` = imaginea din telefon cât se trimite (adresă `blob:`). */
    function lpHtml(raw, local) {
        var lp = lpOf(raw);
        if (!lp) return '';
        var h = lpHost(lp.u), img = '';
        if (typeof local === 'string' && local.indexOf('blob:') === 0) img = '<img src="' + esc(local) + '" alt="">';
        else if (lp.i) img = '<img data-sp="' + esc(lp.i) + '" src="' + esc(window._SP_PH || '') + '" loading="lazy" decoding="async" alt="">';
        /* 8.6-C2: la YouTube, imaginea / ▶ pornește playerul (chat-video.js); restul cardului deschide linkul */
        var play = lp.y ? '<span class="lp-play" role="button" tabindex="0" data-action="lp-play" aria-label="' + esc(tr('chat.lpPlay', 'Play the video')) + '"><i class="fas fa-play" aria-hidden="true"></i></span>' : '';
        return '<div class="lp-card' + (lp.y ? ' is-yt' : '') + '" role="link" tabindex="0" data-action="lp-open" data-href="' + esc(lp.u) + '"' + (lp.y ? ' data-yt="' + esc(lp.y) + '"' : '') + '>'
            + (img ? '<span class="lp-img">' + img + play + '</span>' : (play ? '<span class="lp-img lp-noimg">' + play + '</span>' : ''))
            + '<span class="lp-body">' + ((lp.t || lp.s) ? '<span class="lp-title">' + esc(lp.t || lp.s) + '</span>' : '')   /* fără titlu: doar domeniul (nu de două ori) */
            + (lp.d ? '<span class="lp-desc">' + esc(lp.d) + '</span>' : '')
            + '<span class="lp-dom"><i class="fas fa-link" aria-hidden="true"></i> ' + esc(h) + '</span></span></div>';
    }
    function lpOpen(card) {
        var u = card.getAttribute('data-href');
        if (!lpOk(u)) return;
        try { window.open(u, '_blank', 'noopener,noreferrer'); } catch (e) { logErr('TO_CHAT.link', e); }
    }
    /* 8.6-C2 — YouTube în aplicație (decizia D9): chat-video.js abia la ▶; dacă nu se poate
       încărca (fără internet, versiune nouă), linkul se deschide ca înainte. */
    var VID_FILE = 'chat-video.js';
    function lpPlay(card) {
        var id = card.getAttribute('data-yt'), u = card.getAttribute('data-href');
        if (!LP_YT.test(id || '') || !lpOk(u)) { lpOpen(card); return; }
        var t = card.querySelector('.lp-title');
        useModule(VID_FILE, 'TO_VIDEO', function (M) { if (!M.open(id, t ? t.textContent : '', u, card)) lpOpen(card); }, true)
            .then(function (m) { if (!m) lpOpen(card); });
    }
    function bindLinkCards(list) {
        if (!list || list.__toLp) return;
        list.__toLp = true;
        var kind = list.id === 'chatMessages' ? 'club' : 'dm';
        function hit(e) { var c = e.target && e.target.closest ? e.target.closest('[data-action="lp-open"]') : null; return (c && list.contains(c)) ? c : null; }
        /* imaginea / ▶ unui card YouTube ⇒ playerul (8.6-C2) */
        function isPlay(e, c) { var p = e.target && e.target.closest ? e.target.closest('.lp-img, [data-action="lp-play"]') : null; return !!(p && c.contains(p) && c.hasAttribute('data-yt')); }
        list.addEventListener('click', function (e) {
            var c = hit(e);
            /* în selecție atingerea bifează mesajul; imediat după meniu (apăsare lungă) nu deschidem nimic */
            if (!c || selActive(kind) || Date.now() - lastMenu.at < 1000) return;
            e.preventDefault();
            if (isPlay(e, c)) lpPlay(c); else lpOpen(c);
        });
        list.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            var c = hit(e);
            if (!c) return;
            e.preventDefault();
            if (isPlay(e, c)) lpPlay(c); else lpOpen(c);
        });
    }
    /* Câmpul s-a schimbat ⇒ chat-link.js (încărcat abia la primul „http" din text). */
    function linkNote(st) {
        try {
            if (!st || !st.el) return;
            var key = draftKeyFor(st.el);
            if (!key) return;
            if (window.TO_LINK) { window.TO_LINK.note(st.el, key); return; }
            if (!/https?:\/\//i.test(st.el.value || '') || Date.now() < lpOffUntil) return;
            if (typeof window.isDemoMode === 'function' && window.isDemoMode()) return;
            useModule(LP_FILE, 'TO_LINK', function (M) { M.note(st.el, draftKeyFor(st.el)); }, true)
                .then(function (m) { if (!m) lpOffUntil = Date.now() + 60000; });   /* fără internet: nu reîncercăm la fiecare literă */
        } catch (e) { logErr('TO_CHAT.link', e); }
    }
    /* La trimitere: cardul din câmp sau, la redirecționare, cel al mesajului original. Calea
       imaginii lui aparține dosarului conversației originale ⇒ nu se refolosește; dacă
       chat-forward.js a descărcat imaginea (`blob`), ea se urcă din nou, ca o copie a ta, în
       dosarul conversației noi (8.6-C1b, cerut de Alex: cardul redirecționat era „incomplet"). */
    function lpTake(kind, job) {
        try {
            if (job.fwdBatch != null || job.fwd) {
                var f = lpOf(job.lp);
                if (f) {
                    delete f.i;
                    var fb = job.lp && job.lp.blob;
                    if (fb && typeof fb.size === 'number' && fb.size > 0) { f.blob = fb; try { f.local = URL.createObjectURL(fb); } catch (e) { /* fără imaginea locală */ } }
                }
                return f;
            }
            if (!window.TO_LINK) return null;
            var c = window.TO_LINK.take((kind === 'club' ? 'club:' : 'dm:') + job.ctx, job.text);
            var o = c ? lpOf({ u: c.u, t: c.t, d: c.d, s: c.s, y: c.y }) : null;
            if (o && c.blob) { o.blob = c.blob; try { o.local = URL.createObjectURL(c.blob); } catch (e) { /* fără imaginea locală */ } }
            return o;
        } catch (e) { logErr('TO_CHAT.link', e); return null; }
    }
    /* Ce se salvează / se arată (fără octeții imaginii și fără adresa locală). */
    function lpWire(lp) {
        if (!lp) return null;
        var o = { u: lp.u };
        ['t', 'd', 's', 'y', 'i'].forEach(function (k) { if (lp[k]) o[k] = lp[k]; });
        return o;
    }
    TO_CHAT.link = { html: lpHtml, of: lpOf };

    /* ========================================================================
       RECUPERAREA MESAJELOR PIERDUTE — chatul de club (v9 N1b)
       ------------------------------------------------------------------------
       RO: Cu aplicația în fundal, Android îngheață pagina, iar legătura în timp
           real cade; mesajele trimise între timp NU sunt retrimise de server la
           revenire. Apăsarea pe notificare doar redesena ce era deja în memorie ⇒
           mesajul din notificare lipsea până la reîncărcare (confirmat pe Samsung,
           aplicația în fundal).
           Acum cerem de la server mesajele lipsă:
             • când pagina redevine vizibilă (inclusiv la apăsarea pe notificare);
             • când revine internetul;
             • când legătura în timp real se reface după o cădere.
           • Cost: întâi DOAR id-urile + pozele ultimelor 50 de mesaje (~1-2 kB);
             textul complet se cere numai pentru mesajele chiar lipsă.
           • „Ultimele 50", nu „mai noi decât ultimul meu": la scriere ora o punea
             telefonul expeditorului, deci un ceas rămas în urmă l-ar fi făcut să pară
             mai vechi și ar fi fost sărit. (Din B3a ora o pune serverul — `now()` —
             dar „ultimele 50" rămâne regula mai sigură.) 50 = cât arată lista.
           • Se apără singură (§55.3): o singură cerere în zbor și cel mult una la
             15 s. NIMIC la pornire — încărcarea de la pornire aduce deja tot.
           • Demo: zero trafic. Ștergerile făcute cât erai plecat apar, ca înainte,
             abia la reîncărcare (în afara acestui batch).
       EN: catch up on club messages missed while the page was frozen: on
           visible / online / realtime rejoin, compare the latest 50 ids with
           memory and fetch only the missing rows.
       ⚠ Maparea rândului e aceeași ca la sosirea în timp real
         (`subscribeRealtimeChat`, supabase-integration.js); se unifică atunci când
         codul chatului de club se mută în adaptorul lui (8.13).
       ======================================================================== */
    var CU_FRESH_MS = 15000;
    var CU_WINDOW = 50;
    var cu = { busy: null, at: 0, club: null };

    function clubRow(msg) {
        var mb = (msg.sender_id && typeof memberById === 'function') ? memberById(msg.sender_id) : null;
        return {
            id: msg.id, sender: msg.sender_name, senderEmail: msg.sender_email, senderId: msg.sender_id || null,
            text: msg.text, timestamp: msg.timestamp,
            edited: !!msg.edited, replyTo: msg.reply_to || null, replySender: msg.reply_sender || null, replySnippet: msg.reply_snippet || null,
            photos: Array.isArray(msg.photos) ? msg.photos : [],
            deletedAt: msg.deleted_at || null, deletedByAdmin: !!msg.deleted_by_admin,   /* B2a: urma ștergerii / deleted trace */
            mentions: Array.isArray(msg.mentions) ? msg.mentions : null,                 /* 8.1-f */
            linkPreview: msg.link_preview || null,                                       /* 8.6 */
            liveRef: msg.live_ref || null,                                               /* 8.16-4b: atașamentul viu (sondaj) / live attachment */
            isAdmin: !!((mb && mb.isAdmin) || (state.users && state.users[msg.sender_email] && state.users[msg.sender_email].isAdmin))
        };
    }

    function catchUpClub() {
        try {
            if (typeof sb === 'undefined' || !sb || typeof currentClubId === 'undefined' || !currentClubId) return null;
            if (typeof isDemoMode === 'function' && isDemoMode()) return null;
            if (typeof state === 'undefined' || !state || !state.currentUser || !Array.isArray(state.chatMessages)) return null;
            if (cu.busy) return cu.busy;
            var club = currentClubId;
            if (cu.club === club && Date.now() - cu.at < CU_FRESH_MS) return null;
            cu.busy = (async function () {
                /* B2a: + `deleted_at` ⇒ un mesaj șters cât erai plecat devine urmă și
                   fără reîncărcare. Fără coloană (B1a retrasă): cererea de dinainte.
                   EN: also catch deletions; falls back when the column is missing. */
                var cuSel = function (cols) {
                    return sb.from('messages').select(cols).eq('club_id', club)
                        .order('timestamp', { ascending: false }).limit(CU_WINDOW);
                };
                var r1 = await cuSel('id, photos, deleted_at, deleted_by_admin');
                if (r1.error && /deleted_/.test(String(r1.error.message || ''))) r1 = await cuSel('id, photos');
                if (r1.error) throw r1.error;
                if (club !== currentClubId) return;
                var have = {};
                state.chatMessages.forEach(function (m) { have[String(m.id)] = m; });
                var missing = [], changed = 0, added = 0;
                (r1.data || []).forEach(function (row) {
                    var ex = have[String(row.id)];
                    if (!ex) missing.push(row.id);
                    else if (row.deleted_at && !ex.deletedAt) { markClubDeleted(ex, row.deleted_by_admin, row.deleted_at); changed++; }
                    else if (Array.isArray(row.photos) && (ex.photos || []).length !== row.photos.length) { ex.photos = row.photos; changed++; }
                });
                if (missing.length) {
                    var r2 = await sb.from('messages').select('*').in('id', missing);
                    if (r2.error) throw r2.error;
                    if (club !== currentClubId) return;
                    (r2.data || []).forEach(function (row) {
                        /* Realtime a putut aduce mesajul între cele două cereri. */
                        if (state.chatMessages.some(function (m) { return String(m.id) === String(row.id); })) return;
                        if (adopt('club', row)) return;   /* 8.1-s: al meu, încă „se trimite" */
                        state.chatMessages.push(clubRow(row));
                        added++;
                    });
                    if (added) state.chatMessages.sort(function (a, b) { return new Date(a.timestamp) - new Date(b.timestamp); });
                }
                if ((added || changed) && typeof currentTab !== 'undefined' && currentTab === 'chat' && typeof loadChat === 'function') loadChat();
                if (added && typeof updateTabBadges === 'function') updateTabBadges();
            })().catch(function (e) { logErr('TO_CHAT.catchUp', e); }).then(function () {
                cu.busy = null; cu.at = Date.now(); cu.club = club;
            });
            return cu.busy;
        } catch (e) { logErr('TO_CHAT.catchUp', e); return null; }
    }

    /* Starea canalului de timp real (din `subscribeRealtimeChat`). Recuperăm doar
       la o RE-conectare: prima conectare urmează încărcării de la pornire (nicio
       cerere în plus), iar canalele vechi închise la schimbarea clubului au jetonul
       lor, deci nu declanșează nimic pentru canalul nou. */
    var liveTok = (typeof WeakMap === 'function') ? new WeakMap() : null;
    function liveStatus(status, tok) {
        if (!liveTok || !tok) return;
        var s = liveTok.get(tok);
        if (!s) { s = { down: false }; liveTok.set(tok, s); }
        if (status === 'SUBSCRIBED') {
            if (s.down) { s.down = false; catchUpClub(); }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            s.down = true;
        }
    }

    try {
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') catchUpClub();
        });
        window.addEventListener('online', function () { catchUpClub(); });
        /* Pagina restaurată din memoria browserului (înapoi/înainte). */
        window.addEventListener('pageshow', function (e) { if (e && e.persisted) catchUpClub(); });
    } catch (e) { logErr('TO_CHAT.catchUpInit', e); }

    TO_CHAT.club = { catchUp: catchUpClub, liveStatus: liveStatus };

    /* ========================================================================
       MESAJELE MAI VECHI LA DERULARE ÎN SUS (8.1-c3a)
       ------------------------------------------------------------------------
       RO: Înainte: la pornire venea TOT istoricul clubului, dar se vedeau doar
           ultimele 50 — nimeni nu putea derula mai sus (cerut de Alex). Acum:
           • la pornire, doar ultimele 200 (supabase-integration.js; căutarea mergea
             oricum pe ultimele 200);
           • ajungi sus în listă ⇒ încă 50: întâi din memorie, apoi de pe server
             (`lte` pe ora celui mai vechi + eliminarea dublurilor: două mesaje cu
             aceeași oră nu se pierd), până la începutul conversației;
           • ce era pe ecran rămâne pe loc (`prependTop`), fără redesenare completă;
           • ieși din chat ⇒ fereastra revine la 50 (lista nu crește la nesfârșit);
           • demo: doar din memorie, zero trafic.
       EN: older messages on scroll-up: memory first, then 50 per server page. */
    var CP_PAGE = 50, CP_EDGE = 200;
    var cp = { club: null, win: CP_PAGE, end: false, endAt: null, busy: null };
    function cpSync() {
        var c = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        if (cp.club !== c) { cp.club = c; cp.win = CP_PAGE; cp.end = false; cp.endAt = null; }
        /* 8.1-h: câmpul clubului primește ciorna clubului curent (și la prima desenare
           după pornire, când contul abia s-a încărcat). EN: the club draft follows the club. */
        attached.forEach(function (a) { if (a.el.id === 'chatInput') draftSwap(a); });
        /* 8.8: aspectul clubului curent (copia locală, fără cerere); serverul o dată pe
           sesiune, doar când chatul chiar e pe ecran, AMÂNAT (loadChat rulează și la pornire). */
        cprefApply();
        cprefHold(document.getElementById('chatCard'));       /* 8.3-c: citirea e în curs ⇒ fără aspectul implicit */
        try {
            var tabC = document.getElementById('tab-chat');
            if ((tabC && tabC.classList.contains('active')) || isFs(document.getElementById('chatCard'))) cprefSyncSoon();
        } catch (e) { logErr('TO_CHAT.cpref', e); }
        /* Memoria a fost reîncărcată (ex. o hidratare completă) ⇒ capătul știut nu mai e valabil. */
        var st = (typeof state !== 'undefined') ? state : null;
        var first = st && Array.isArray(st.chatMessages) && st.chatMessages[0];
        if (cp.end && (!first || String(first.id) !== cp.endAt)) { cp.end = false; cp.endAt = null; }
    }
    function clubWin() { cpSync(); return cp.win; }
    function olderLoading(list, on) {
        try {
            var host = list && list.parentNode; if (!host) return;
            var el = host.querySelector(':scope > .chat-older');
            if (!el && on) {
                el = document.createElement('div');
                el.className = 'chat-older';
                el.setAttribute('role', 'status');
                el.innerHTML = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i> <span></span>';
                host.appendChild(el);
            }
            if (!el) return;
            if (on) { el.querySelector('span').textContent = tr('chat.olderLoading', 'Loading older messages…'); el.style.top = (list.offsetTop + 8) + 'px'; }
            el.hidden = !on;
        } catch (e) { logErr('TO_CHAT.older', e); }
    }
    function clubOlder(list) {
        try {
            cpSync();
            if (cp.busy) return cp.busy;
            var st = (typeof state !== 'undefined') ? state : null;
            if (!st || !Array.isArray(st.chatMessages) || String(st.chatSearchQuery || '').trim()) return null;
            var total = st.chatMessages.length;
            if (cp.win < total) {
                cp.win = Math.min(cp.win + CP_PAGE, total);
                if (typeof loadChat === 'function') loadChat();
                return null;
            }
            if (cp.end || !total) return null;
            if ((typeof isDemoMode === 'function' && isDemoMode()) || typeof sb === 'undefined' || !sb || !cp.club) {
                cp.end = true; cp.endAt = String(st.chatMessages[0].id); return null;
            }
            var club = cp.club, oldest = st.chatMessages[0];
            olderLoading(list, true);
            cp.busy = (async function () {
                var r = await sb.from('messages').select('*').eq('club_id', club)
                    .lte('timestamp', oldest.timestamp)
                    .order('timestamp', { ascending: false }).limit(CP_PAGE + 1);
                if (r.error) throw r.error;
                if (club !== currentClubId || st.chatMessages[0] !== oldest) return;   /* altă stare între timp */
                var have = {};
                st.chatMessages.forEach(function (m) { have[String(m.id)] = true; });
                var rows = (r.data || []).filter(function (x) { return !have[String(x.id)]; }).map(clubRow).reverse();
                if ((r.data || []).length < CP_PAGE + 1 || !rows.length) { cp.end = true; cp.endAt = String((rows[0] || oldest).id); }
                if (!rows.length) return;
                st.chatMessages = rows.concat(st.chatMessages);
                cp.win = Math.min(cp.win + rows.length, st.chatMessages.length);
                if (typeof loadChat === 'function') loadChat();
            })().catch(function (e) { logErr('TO_CHAT.older', e); }).then(function () {
                cp.busy = null; olderLoading(list, false);
            });
            return cp.busy;
        } catch (e) { logErr('TO_CHAT.older', e); return null; }
    }
    function bindClubOlder(list) {
        if (!list || list.__toOlder) return;
        list.__toOlder = true;
        /* Doar citim `scrollTop` (fără scrieri înainte) ⇒ fără recalculare forțată. */
        list.addEventListener('scroll', function () {
            if (cp.busy || list.scrollTop > CP_EDGE) return;
            var tab = document.getElementById('tab-chat');
            if (!tab || !tab.classList.contains('active')) return;
            clubOlder(list);
        }, { passive: true });
    }
    TO_CHAT.club.win = clubWin;
    TO_CHAT.club.older = clubOlder;

    /* ── Salt la mesajul citat, oricât de vechi (8.1-d, club + DM) ───────────────
       RO: Simptom (Alex): la un răspuns dat unui mesaj de acum câteva sute,
           atingerea citatului nu ducea nicăieri: mesajul nu era pe ecran (se văd
           ultimele 50) și nici în memorie (ultimele 200). Acum, ca în WhatsApp:
           • e pe ecran ⇒ derulare + evidențiere (ca înainte);
           • e în memorie ⇒ fereastra afișată coboară până la el (+15 de context);
           • altfel ⇒ îl cerem serverului (cu drepturile tale, RLS): întâi doar ora
             lui (o cerere mică), apoi mesajele dintre el și cele din memorie, cel
             mult 1000 (limita API-ului), într-o singură cerere. Peste 1000 ⇒ mesajul
             „prea departe" (derulezi în sus). Nu mai există ⇒ „nu mai există".
           • Cât se încarcă: capsula „Se încarcă mesajele mai vechi…"; o a doua
             atingere în timpul încărcării nu pornește altă cerere.
           • Căutarea deschisă ascunde mesajele care nu se potrivesc ⇒ se închide întâi.
           • Demo: doar memoria. Coloane explicite; dacă serverul nu are una dintre
             ele (schemă mai veche), cererea de dinainte (`*`).
       EN: jump to the quoted message: on screen → highlight; in memory → widen the
           window; else fetch the gap from the server (≤ 1000 rows). */
    var JUMP_MAX = 1000, JUMP_CTX = 15;
    var CLUB_COLS = 'id, sender_name, sender_email, sender_id, text, timestamp, edited, reply_to, reply_sender, reply_snippet, photos, deleted_at, deleted_by_admin, mentions, link_preview, live_ref';
    var jumpBusy = null;
    function colMissing(err) { return !!err && (err.code === '42703' || /column .* does not exist/i.test(String(err.message || ''))); }
    var jumpHi = { id: null, until: 0, mo: null };
    var jumpQuietUntil = 0;                 /* cât durează saltul, istoricul nu se mișcă (`settleEnts`) */
    var JUMP_TIMEOUT = 10000, JUMP_PIN_MS = 2500;
    /* Mesajul rămâne unde a ajuns cât se mai așază lista. Simptom (Alex, prima dată pe
       un dispozitiv / după logare): ajungea la mesaj, apoi „sărea" câteva mesaje în
       jos — pozele și numele din citate se încărcau abia atunci și schimbau înălțimea
       rândurilor. A doua oară erau deja în memorie, deci nu se mai vedea. Acum, 2,5 s
       după sosire, orice schimbare din listă (rând redesenat, poză încărcată) e
       compensată pe loc; se oprește la prima atingere / rotiță / tastă a ta.
       Citiri și scrieri într-un singur cadru (`requestAnimationFrame`, §54.4). */
    function jumpPin(list, id) {
        var el = document.getElementById(id);
        if (!list || !el || typeof MutationObserver !== 'function') return;
        var base = el.getBoundingClientRect().top - list.getBoundingClientRect().top;
        var raf = 0, done = false, mo = null, t = 0;
        var stop = function () {
            if (done) return;
            done = true;
            if (mo) mo.disconnect();
            clearTimeout(t);
            if (raf) cancelAnimationFrame(raf);
            list.removeEventListener('load', kick, true);
            ['wheel', 'touchstart', 'pointerdown'].forEach(function (ev) { list.removeEventListener(ev, stop, { passive: true }); });
            document.removeEventListener('keydown', stop, true);
        };
        var check = function () {
            raf = 0;
            if (done) return;
            var x = document.getElementById(id);
            if (!x) return;
            var d = (x.getBoundingClientRect().top - list.getBoundingClientRect().top) - base;
            if (Math.abs(d) > 1) list.scrollTop += d;
        };
        var kick = function () { if (!raf && !done) raf = requestAnimationFrame(check); };
        try {
            mo = new MutationObserver(kick);
            mo.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'style'] });
            list.addEventListener('load', kick, true);                 /* pozele (evenimentul nu urcă ⇒ captură) */
            ['wheel', 'touchstart', 'pointerdown'].forEach(function (ev) { list.addEventListener(ev, stop, { passive: true }); });
            document.addEventListener('keydown', stop, true);
            t = setTimeout(stop, JUMP_PIN_MS);
        } catch (e) { logErr('TO_CHAT.jump', e); stop(); }
    }
    function jumpShow(el) {
        var reduce = false;
        try { reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { /* noop */ }
        var id = el.id, list = el.closest ? el.closest('#chatMessages, #dmThread') : null;
        var inView = false, far = false;
        try {
            var lr = list ? list.getBoundingClientRect() : null, er = el.getBoundingClientRect();
            inView = !!lr && er.top >= lr.top && er.bottom <= lr.bottom;
            far = !!lr && Math.abs((er.top + er.bottom) / 2 - (lr.top + lr.bottom) / 2) > list.clientHeight * 1.5;
        } catch (e) { /* noop */ }
        /* Departe ⇒ direct, ca în WhatsApp (derularea lină peste mii de pixeli dura peste
           o secundă și putea fi întreruptă); aproape ⇒ lin, ca înainte. */
        var smooth = !reduce && !far;
        jumpQuietUntil = Date.now() + 3000 + JUMP_PIN_MS;
        try { el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' }); } catch (e) { /* noop */ }
        el.classList.add('chat-highlight');
        /* O redesenare în acest timp (numele din citate sosite, pagina mai veche adusă la
           derulare) reface rândurile și ștergea evidențierea ⇒ o punem la loc pe rândul
           nou, doar cât durează. */
        if (jumpHi.mo) jumpHi.mo.disconnect();
        var mine = { id: id, until: Date.now() + 5000, mo: null };
        jumpHi = mine;
        try {
            if (list && typeof MutationObserver === 'function') {
                mine.mo = new MutationObserver(function () {
                    var x = document.getElementById(mine.id);
                    if (x && Date.now() < mine.until && !x.classList.contains('chat-highlight')) x.classList.add('chat-highlight');
                });
                mine.mo.observe(list, { childList: true, subtree: true });
            }
        } catch (e) { logErr('TO_CHAT.jump', e); }
        /* Cele 1,5 s de evidențiere încep după ce derularea s-a OPRIT (măsurat: 120 de
           mesaje mai sus ⇒ ~1,3 s de derulare lină; înainte, evidențierea se stingea
           exact când ajungeai). `scrollend` unde există; altfel o estimare. */
        var started = false;
        var start = function () {
            if (started) return;
            started = true;
            jumpQuietUntil = Date.now() + JUMP_PIN_MS;
            jumpPin(list, id);
            mine.until = Date.now() + 1500;
            setTimeout(function () {
                if (mine.mo) mine.mo.disconnect();
                var x = document.getElementById(id);
                if (x && (jumpHi === mine || jumpHi.id !== id)) x.classList.remove('chat-highlight');
            }, 1500);
        };
        if (inView || !smooth || !list) start();
        else if ('onscrollend' in window) { list.addEventListener('scrollend', start, { once: true }); setTimeout(start, 3000); }
        else setTimeout(start, 700);
    }
    function jumpNote(k, fb, kind) {
        try { if (typeof window.showNotification === 'function') window.showNotification(tr(k, fb), kind || 'info'); } catch (e) { /* noop */ }
    }
    /* o: { list, find() → elementul, load() → Promise; 'far' = prea departe } */
    function jumpRun(o) {
        try {
            var el = o.find();
            if (el) { jumpShow(el); return Promise.resolve(true); }
            if (jumpBusy) return jumpBusy;
            olderLoading(o.list, true);
            /* Fără internet (mod avion) cererea nu „pică" pe loc, rămânea agățată ⇒
               cel mult 10 s, apoi mesajul de eroare (testul 5 al lui Alex). */
            jumpBusy = withTimeout(Promise.resolve().then(o.load), JUMP_TIMEOUT).then(function (res) {
                var el2 = o.find();
                if (el2) { requestAnimationFrame(function () { jumpShow(el2); }); return true; }
                if (res === 'far') jumpNote('chat.quoteFar', 'That message is too far back to jump to. Scroll up to find it.');
                else jumpNote('chat.quoteGone', 'The original message is no longer available.');
                return false;
            }, function (e) {
                logErr('TO_CHAT.jump', e);
                jumpNote('chat.quoteErr', 'Could not load the message. Please try again.', 'error');
                return false;
            }).then(function (r) { jumpBusy = null; olderLoading(o.list, false); return r; });
            return jumpBusy;
        } catch (e) { logErr('TO_CHAT.jump', e); return Promise.resolve(false); }
    }
    function clubJumpLoad(id) {
        var st = (typeof state !== 'undefined') ? state : null;
        if (!st || !Array.isArray(st.chatMessages) || !st.chatMessages.length) return null;
        var idxOf = function () {
            for (var i = st.chatMessages.length - 1; i >= 0; i--) if (String(st.chatMessages[i].id) === id) return i;
            return -1;
        };
        var widen = function (i) {
            cpSync();
            var w = Math.min(st.chatMessages.length - i + JUMP_CTX, st.chatMessages.length);
            if (w > cp.win) cp.win = w;
            if (typeof loadChat === 'function') loadChat();
        };
        var i0 = idxOf();
        if (i0 !== -1) { widen(i0); return null; }
        if ((typeof isDemoMode === 'function' && isDemoMode()) || typeof sb === 'undefined' || !sb) return null;
        var club = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        if (!club) return null;
        var prev = cp.busy;
        var run = (async function () {
            if (prev) { try { await prev; } catch (e) { /* noop */ } }
            var i1 = idxOf();                                  /* adus între timp de derulare */
            if (i1 !== -1) { widen(i1); return; }
            if (typeof navigator !== 'undefined' && navigator.onLine === false) throw netErr('offline');   /* eroare pe loc */
            var r0 = await sb.from('messages').select('id, timestamp').eq('club_id', club).eq('id', id).limit(1);
            if (r0.error) throw r0.error;
            var t = (r0.data || [])[0];
            if (!t) return;                                    /* șters definitiv */
            var oldest = st.chatMessages[0];
            var q = function (cols) {
                return sb.from('messages').select(cols).eq('club_id', club)
                    .gte('timestamp', t.timestamp).lte('timestamp', oldest.timestamp)
                    .order('timestamp', { ascending: false }).limit(JUMP_MAX);
            };
            var r = await q(CLUB_COLS);
            if (r.error && colMissing(r.error)) r = await q('*');
            if (r.error) throw r.error;
            if (club !== currentClubId || st.chatMessages[0] !== oldest) return;   /* altă stare între timp */
            var data = r.data || [];
            if (!data.some(function (x) { return String(x.id) === id; })) return data.length >= JUMP_MAX ? 'far' : undefined;
            var have = {};
            st.chatMessages.forEach(function (m) { have[String(m.id)] = true; });
            var rows = data.filter(function (x) { return !have[String(x.id)]; }).map(clubRow).reverse();
            if (rows.length) st.chatMessages = rows.concat(st.chatMessages);
            var i2 = idxOf();
            if (i2 !== -1) widen(i2);
        })();
        /* Cât durează, derularea în sus nu cere altă pagină (`clubOlder` așteaptă). */
        var mark = run.then(noop, noop).then(function () { if (cp.busy === mark) cp.busy = null; });
        cp.busy = mark;
        return run;
    }
    function clubJump(id) {
        id = String(id);
        var list = document.getElementById('chatMessages');
        var find = function () { return document.getElementById('msg-' + id); };
        try {
            var st = (typeof state !== 'undefined') ? state : null;
            if (!find() && st && String(st.chatSearchQuery || '').trim()) closeClubSearch();
        } catch (e) { logErr('TO_CHAT.jump', e); }
        return jumpRun({ list: list, find: find, load: function () { return clubJumpLoad(id); } });
    }
    TO_CHAT.jump = jumpRun;
    TO_CHAT.club.jump = clubJump;

    /* ── Căutarea în TOT istoricul clubului, pe server (8.1-c3s) ───────────────
       RO: În memorie stau doar ultimele mesaje (8.1-c3a), deci căutarea locală nu
           mai vede tot. `search_club_messages` (V9_C3S_CAUTARE.sql) caută pe server
           în text ȘI în numele celui care a scris, cu drepturile celui care caută
           (doar membrii văd mesajele), cele mai noi 50.
           • Rezultatele din memorie apar imediat; cele de pe server se adaugă după o
             pauză de 300 ms la tastare (nu la fiecare literă). Un răspuns venit
             pentru un text deja schimbat se ignoră.
           • Minim 2 caractere, ca pe server. Fără funcția serverului sau în demo:
             doar căutarea din memorie, ca înainte.
       EN: server-side club search (text + sender name), debounced, latest-wins. */
    var CS_DELAY = 300;
    var cs = { key: '', rows: null, pending: false, seq: 0, timer: 0 };
    function clubSearch(q) {
        var club = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        q = String(q || '').trim();
        var key = (club || '') + '|' + q.toLowerCase();
        if (key === cs.key) return;
        cs.key = key; cs.rows = null; cs.pending = false;
        var my = ++cs.seq;
        clearTimeout(cs.timer);
        if (q.length < 2 || !club || typeof sb === 'undefined' || !sb || (typeof isDemoMode === 'function' && isDemoMode())) return;
        cs.pending = true;
        cs.timer = setTimeout(function () {
            sb.rpc('search_club_messages', { p_club: club, p_q: q }).then(function (r) {
                if (my !== cs.seq) return;
                cs.pending = false;
                if (r.error) { if (!rpcMissing(r.error)) logErr('TO_CHAT.search', r.error); }
                else cs.rows = (r.data || []).map(clubRow);
                var st = (typeof state !== 'undefined') ? state : null;
                if (st && String(st.chatSearchQuery || '').trim().toLowerCase() === q.toLowerCase() && typeof loadChat === 'function') loadChat();
            }, function (e) { if (my === cs.seq) { cs.pending = false; logErr('TO_CHAT.search', e); } });
        }, CS_DELAY);
    }
    function clubSearchRows(q) {
        var club = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        return ((club || '') + '|' + String(q || '').trim().toLowerCase() === cs.key) ? cs.rows : null;
    }
    /* ── Numărul EXACT de necitite pentru butonul de chat din hub (8.1-c3) ──────
       RO: Hub-ul (nav.js, `subChat`) numără necititele din memorie, adică cel mult
           ultimele 200 (defect văzut de Alex: 470 necitite, butonul arăta 200). Doar
           când memoria NU le cuprinde pe toate (200 încărcate, toate mai noi decât
           ultima citire) cerem serverului numărul lor — o numărătoare, fără rânduri,
           o dată pe club și pe reperul de citire; după răspuns hub-ul se redesenează.
           Mesajele sosite după numărătoare se adună din memorie. Altfel: nicio cerere.
       EN: exact unread count for the hub when the loaded window can't hold them all. */
    var ut = { key: '', n: null, at: 0 };
    function clubUnreadTotal(sinceMs, localN) {
        try {
            var st = (typeof state !== 'undefined') ? state : null;
            var msgs = (st && st.chatMessages) || [];
            var me = st && st.currentUser && st.currentUser.id;
            var club = (typeof currentClubId !== 'undefined') ? currentClubId : null;
            if (!msgs.length || msgs.length < 200 || !sinceMs || new Date(msgs[0].timestamp).getTime() <= sinceMs) return null;
            if (!club || !me || typeof sb === 'undefined' || !sb || (typeof isDemoMode === 'function' && isDemoMode())) return null;
            var key = club + '|' + sinceMs;
            if (ut.key === key) {
                if (ut.n == null) return null;
                var later = msgs.filter(function (m) { return new Date(m.timestamp).getTime() > ut.at && m.senderId !== me && m.sender !== 'Sistem'; }).length;
                return Math.max(ut.n + later, localN || 0);
            }
            ut.key = key; ut.n = null;
            ut.at = new Date(msgs[msgs.length - 1].timestamp).getTime();
            sb.from('messages').select('id', { count: 'exact', head: true }).eq('club_id', club)
                .gt('timestamp', new Date(sinceMs).toISOString()).neq('sender_id', me).neq('sender_name', 'Sistem')
                .lte('timestamp', new Date(ut.at).toISOString())
                .then(function (r) {
                    if (ut.key !== key || !r || r.error || typeof r.count !== 'number') return;
                    ut.n = r.count;
                    if (window.TO_NAV && typeof window.TO_NAV.refresh === 'function') window.TO_NAV.refresh();
                }, function (e) { logErr('TO_CHAT.unreadTotal', e); });
            return null;
        } catch (e) { logErr('TO_CHAT.unreadTotal', e); return null; }
    }
    TO_CHAT.club.unreadTotal = clubUnreadTotal;
    TO_CHAT.club.search = clubSearch;
    TO_CHAT.club.searchRows = clubSearchRows;
    TO_CHAT.club.searchPending = function () { return !!cs.pending; };

    /* ========================================================================
       MENIUL MESAJULUI — unul singur pentru club și DM (8.2, standardul §13.2)
       ------------------------------------------------------------------------
       RO: Simptom raportat: la apăsarea lungă, degetul selecta din greșeală
           textul butoanelor din meniul apărut. Cauza (din cod): meniul se
           deschidea la 500 ms CU DEGETUL ÎNCĂ PE ECRAN, iar gestul de selecție al
           telefonului continua peste butoanele meniului, neprotejate.
           • Deschis prin atingere ⇒ meniul ignoră orice apăsare până la 300 ms
             după ridicarea degetului (plasă de siguranță: 1,2 s).
           • Meniul nu poate fi selectat ca text (chat.css).
           • ✕, `Escape` și clicul pe fundal închid pe aceeași cale (`__dispose`, §66.4).
           • Două deschideri ale aceluiași mesaj în < 800 ms ⇒ a doua e ignorată
             (Android trimite și `contextmenu` la apăsarea lungă, pe lângă
             temporizatorul nostru de 500 ms).
           • Pe telefon/tabletă apare ca foaie de jos (D1), pe desktop centrat.
           • Loc rezervat (ascuns) pentru bara de reacții (8.4).
           • Butoane prin delegare (`data-action`) și funcții, fără `onclick` cu
             text (§35): id-ul mesajului nu mai ajunge în cod executabil.
           Fiecare conversație își dă doar lista de opțiuni (`items`).
       EN: one message menu for club + DM: inert until the finger that opened it
           lifts, unselectable, ✕/Escape/backdrop close the same way, dedupes the
           Android contextmenu + long-press double open, bottom sheet on touch.
       ======================================================================== */
    var MENU_ARM_MS = 300;
    var MENU_ARM_MAX_MS = 1200;
    var MENU_DEDUP_MS = 800;
    var lastMenu = { key: null, at: 0 };

    /* E un deget pe ecran chiar acum? (pasiv, pe faza de captură — doar citim) */
    var touchDown = false;
    try {
        document.addEventListener('touchstart', function () { touchDown = true; }, { passive: true, capture: true });
        var touchUp = function () { touchDown = false; };
        document.addEventListener('touchend', touchUp, { passive: true, capture: true });
        document.addEventListener('touchcancel', touchUp, { passive: true, capture: true });
    } catch (e) { logErr('TO_CHAT.menuInit', e); }

    function esc(s) { return window.escHtml(s == null ? '' : String(s)); }
    function tr(k, fb) { try { var v = (typeof window.t === 'function') ? window.t(k) : null; return (v && v !== k) ? v : fb; } catch (e) { return fb; } }

    function closeMenu(id) {
        var m = document.getElementById(id);
        if (m) { if (typeof m.__dispose === 'function') m.__dispose(); else m.remove(); }
    }

    /* opts: { id: 'msgMenuModal' | 'dmMsgMenuModal', key: id-ul mesajului,
               preview: textul mesajului, items: [{ icon, label, run, danger }] } */
    function openMenu(opts) {
        /* 8.1-g: cât e activă selecția multiplă, apăsarea lungă / clicul dreapta pe un
           mesaj îl bifează în loc să deschidă meniul. */
        var isMsgMenu = (opts.id === 'msgMenuModal' || opts.id === 'dmMsgMenuModal') && opts.key != null;
        var selKind = opts.id === 'dmMsgMenuModal' ? 'dm' : 'club';
        if (isMsgMenu && selActive(selKind)) { selToggle(String(opts.key)); return null; }
        var dedupKey = opts.id + ':' + String(opts.key);
        var now = Date.now();
        if (lastMenu.key === dedupKey && now - lastMenu.at < MENU_DEDUP_MS && document.getElementById(opts.id)) return null;
        lastMenu = { key: dedupKey, at: now };
        closeMenu(opts.id);
        if (isMsgMenu) clearListSelection();             /* 8.8-c: fără text rămas selectat */

        var items = opts.items || [];
        /* 8.1-g: „Selectează" în meniul unui mesaj trimis (club + DM), înaintea opțiunilor
           de ștergere / raportare; nu la mesajele în curs de trimitere / netrimise. */
        if (isMsgMenu && !/^tmp_/.test(String(opts.key))) {
            var selIt = { icon: 'fa-square-check', label: tr('chat.select', 'Select'), run: function () { selStart(selKind, String(opts.key)); } };
            var di = -1;
            for (var k = 0; k < items.length; k++) { if (items[k] && items[k].danger) { di = k; break; } }
            items = items.slice();
            if (di === -1) items.push(selIt); else items.splice(di, 0, selIt);
        }
        var text = (opts.preview == null) ? '' : plainText(opts.preview);   /* 8.1-f: fără markere */
        var preview = esc(text.slice(0, 100)) + (text.length > 100 ? '…' : '');
        var modal = document.createElement('div');
        modal.className = 'modal active msg-menu-modal';
        modal.id = opts.id;
        /* `title` (ex. meniul ⋮ din antet) în locul citatului unui mesaj. */
        var headHtml = (opts.title != null)
            ? '<div class="msg-menu-preview is-title">' + esc(opts.title) + '</div>'
            : '<div class="msg-menu-preview">' + preview + '</div>';
        modal.innerHTML = '<div class="modal-content msg-menu" role="dialog" aria-modal="true">'
            + '<div class="msg-menu-grip" aria-hidden="true"></div>'
            + '<div class="msg-menu-head">'
            +   headHtml
            +   '<button type="button" class="msg-menu-x" data-action="menu-close" aria-label="' + esc(tr('common.close', 'Close')) + '" title="' + esc(tr('common.close', 'Close')) + '"><i class="fas fa-times" aria-hidden="true"></i></button>'
            + '</div>'
            + '<div class="msg-menu-react" hidden></div>'
            + '<div class="msg-menu-items">' + items.map(function (it, i) {
                /* 8.4: `emoji` în locul iconiței (lista „cine a reacționat", chat-react.js) */
                return '<button type="button" class="msg-menu-item' + (it.danger ? ' danger' : '') + '" data-action="menu-item" data-i="' + i + '">'
                    + (it.emoji ? '<span class="msg-menu-emo" aria-hidden="true">' + esc(it.emoji) + '</span>' : '<i class="fas ' + esc(it.icon) + '" aria-hidden="true"></i>')
                    + '<span>' + esc(it.label) + '</span></button>';
            }).join('') + '</div>'
            + '</div>';

        var box = modal.firstChild;
        var st = { armed: true, byTouch: touchDown, prevFocus: document.activeElement, closed: false };
        var bkey = 'menu:' + opts.id;                     /* 8.1-d: „înapoi" / Escape îl închid */
        function close() {
            if (st.closed) return;
            st.closed = true;
            backDone(bkey);
            if (modal.parentNode) modal.parentNode.removeChild(modal);
            /* Focusul înapoi unde era — doar fără atingere (pe telefon ar redeschide
               tastatura dacă înainte era în câmpul de scriere). */
            if (!st.byTouch) {
                try { if (st.prevFocus && st.prevFocus.focus && document.contains(st.prevFocus)) st.prevFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ }
            }
        }
        modal.__dispose = close;

        /* Cât timp meniul e „neînarmat", ORICE clic e înghițit: și pe butoane, și pe
           fundal (inclusiv ascultătorul global de fundal din app-core.js). */
        modal.addEventListener('click', function (e) {
            if (!st.armed) { e.stopImmediatePropagation(); e.preventDefault(); }
        }, true);
        modal.addEventListener('click', function (e) {
            if (e.target === modal) { close(); return; }
            var b = e.target && e.target.closest ? e.target.closest('[data-action]') : null;
            if (!b || !modal.contains(b)) return;
            if (b.getAttribute('data-action') === 'menu-close') { close(); return; }
            if (b.getAttribute('data-action') === 'menu-item') {
                var it = items[+b.getAttribute('data-i')];
                close();
                if (it && typeof it.run === 'function') {
                    try { it.run(); } catch (e2) { logErr('TO_CHAT.menu', e2); }
                }
            }
        });

        /* 8.4: bara de reacții (chat-react.js), doar la un mesaj trimis și doar dacă poți
           reacționa (aceleași reguli ca serverul); altfel rămâne ascunsă, ca înainte. */
        if (isMsgMenu && !/^tmp_/.test(String(opts.key)) && window.TO_REACT && typeof window.TO_REACT.bar === 'function') {
            try { window.TO_REACT.bar(box.querySelector('.msg-menu-react'), selKind, String(opts.key), close); } catch (e) { logErr('TO_CHAT.menu', e); }
        }
        document.body.appendChild(modal);
        backOpen(bkey, close);
        modal.__toBack = bkey;     /* scos direct din pagină (ex. `closeMsgMenu`) ⇒ eliberat (observatorul de <body>) */
        enableSheetSwipe(st, box, close);

        if (st.byTouch) {
            /* Deschis cu degetul încă pe ecran ⇒ inert până la ridicare + 300 ms. */
            st.armed = false;
            box.classList.add('is-inert');
            var timer = setTimeout(arm, MENU_ARM_MAX_MS);
            var onUp = function () {
                document.removeEventListener('touchend', onUp, true);
                document.removeEventListener('touchcancel', onUp, true);
                clearTimeout(timer);
                timer = setTimeout(arm, MENU_ARM_MS);
            };
            if (!touchDown) onUp();
            else {
                document.addEventListener('touchend', onUp, true);
                document.addEventListener('touchcancel', onUp, true);
            }
        } else {
            /* Mouse / tastatură: focus pe prima opțiune (accesibilitate). */
            try { var first = box.querySelector('.msg-menu-item'); if (first) first.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
        function arm() {
            st.armed = true;
            box.classList.remove('is-inert');
        }
        return modal;
    }

    /* ── Tragerea în jos închide foaia (8.2-d) ──────────────────────────────────
       RO: Bara de sus promitea gestul aplicațiilor native, dar nu făcea nimic.
           Acum: tragi foaia în jos din zona de sus (bara + rândul cu textul și ✕);
           peste 80 px sau o aruncare rapidă ⇒ se închide alunecând, altfel revine.
           • Doar în modul foaie (dispozitive de atingere); pe desktop meniul e centrat.
           • Zona de tras are `touch-action: none` (chat.css) ⇒ browserul nu mai
             derulează și nu mai face „trage pentru reîncărcare" acolo, deci
             ascultătorii pot rămâne PASIVI (fără blocarea derulării, §54).
           • Opțiunile rămân doar de apăsat (zona lor nu pornește tragerea).
           • Cât timp foaia e inertă (degetul care a deschis-o e încă pe ecran),
             tragerea nu pornește.
           • Mișcare doar prin `transform` (fără recalcularea paginii); fără animație
             la „reducere animații".
       EN: swipe the sheet down from its top area to dismiss it. */
    var SWIPE_CLOSE_PX = 80;
    var SWIPE_FLICK_PX = 24;
    var SWIPE_FLICK_V = 0.5;   /* px/ms */
    var mqReduce = null;
    try { mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null; } catch (e) { /* noop */ }

    function enableSheetSwipe(st, box, close) {
        if (!(mqTouch && mqTouch.matches)) return;
        var zones = [box.querySelector('.msg-menu-grip'), box.querySelector('.msg-menu-head')].filter(Boolean);
        var startY = 0, startT = 0, dy = 0, dragging = false;
        function onStart(e) {
            var t = e.touches && e.touches[0];
            if (!st.armed || !t || e.touches.length > 1) return;
            startY = t.clientY; startT = Date.now(); dy = 0; dragging = true;
            box.style.transition = 'none';
        }
        function onMove(e) {
            if (!dragging) return;
            var t = e.touches && e.touches[0]; if (!t) return;
            dy = Math.max(0, t.clientY - startY);
            box.style.transform = dy ? 'translateY(' + dy + 'px)' : '';
        }
        function onEnd() {
            if (!dragging) return;
            dragging = false;
            var v = dy / Math.max(1, Date.now() - startT);
            box.style.transition = '';
            if (dy > SWIPE_CLOSE_PX || (dy > SWIPE_FLICK_PX && v > SWIPE_FLICK_V)) {
                if (mqReduce && mqReduce.matches) { close(); return; }
                box.style.transform = 'translateY(100%)';
                var done = false;
                var fin = function () { if (done) return; done = true; close(); };
                box.addEventListener('transitionend', fin, { once: true });
                setTimeout(fin, 260);   /* plasă: `transitionend` poate lipsi */
            } else {
                box.style.transform = '';
            }
        }
        zones.forEach(function (z) {
            z.addEventListener('touchstart', onStart, { passive: true });
            z.addEventListener('touchmove', onMove, { passive: true });
            z.addEventListener('touchend', onEnd, { passive: true });
            z.addEventListener('touchcancel', onEnd, { passive: true });
        });
    }

    TO_CHAT.menu = { open: openMenu, close: closeMenu };

    /* v9 8.7-D3 + D6 — bannerul din aplicație la un mesaj de club urmează regula notificării
       de pe server (trg_chat_push): relație blocată, în orice sens ⇒ nimic; altfel alegerea
       pentru club (club-notif.js, la cerere, fără nicio cerere la pornire). Modulul nu se
       poate încărca ⇒ bannerul, ca înainte. `show(true)` = „te-a menționat".
       EN: in-app banner gate = the server rule (blocks both ways, per-club level). */
    function notifGate(clubId, msg, show) {
        try {
            if (msg && msg.sender_id && typeof window.isBlockedPair === 'function' && window.isBlockedPair(msg.sender_id)) return;
        } catch (e) { logErr('TO_CHAT.notifGate', e); }
        useModule('club-notif.js', 'TO_CNOTIF', function (M) {
            M.allow(clubId, msg).then(function (v) { if (v) show(v === 'mention'); });
        }, true).then(function (m) { if (!m) show(false); });
    }
    TO_CHAT.notifGate = notifGate;

    /* ── Legătura cu chatul de club ────────────────────────────────────────────
       RO: Câmpul e static în index.html. Se mută în adaptorul de club când acela
           va exista (8.13). Garda pe `TEXTAREA`: cu un index.html vechi (câmpul
           încă `<input>`) nu atingem nimic.
       EN: club chat binding; moves to the club adapter once it exists. */
    /* ── Antetul chatului de club (8.1-a): [←] [⤢] [⋮] ────────────────────────
       RO: Căutarea, notificările și golirea chatului au trecut sub ⋮ (foaia de jos
           pe telefon, centrat pe desktop). Funcțiile lor rămân cele de azi
           (app-modules-2.js), deja protejate de lipsa vechilor butoane.
           • „Șterge chat" apare doar cu dreptul `chat.clear` — aceeași regulă ca
             `clearChat` și ca fostul buton (`data-cap`); serverul o impune oricum.
           • „Notificări" deschide alegerea pentru club: Toate / Doar mențiuni / Fără (8.7-D3).
           • ← duce în pagina clubului (`hubclub`); din ecranul complet iese întâi.
       EN: club chat header — search, notifications and clear chat moved under ⋮;
           ← goes back to the club hub. */
    function clubHeadMenu() {
        var items = [];
        items.push({ icon: 'fa-search', label: tr('chat.searchTitle', 'Search chat'), run: openClubSearch });
        items.push({ icon: 'fa-palette', label: tr('cpref.title', 'Appearance'), run: function () { cprefOpen('club'); } });   /* 8.8 */
        /* v9 8.7-D3: notificările clubului, pe CONT (Toate / Doar mențiuni / Fără, club-notif.js),
           în locul comutatorului vechi, doar pe dispozitiv, care oprea doar bannerul din aplicație (H4).
           EN: per-club account-level notification level replaces the old device-only toggle. */
        items.push({ icon: 'fa-bell', label: tr('cnotif.title', 'Notifications'),
            run: function () {
                var cid = (typeof currentClubId !== 'undefined') ? currentClubId : null;
                var nm = (typeof window.clubDisplayName === 'function') ? window.clubDisplayName() : '';
                useModule('club-notif.js', 'TO_CNOTIF', function (M) { M.open(cid, nm); });
            } });
        if (typeof window.can === 'function' && window.can('chat.clear')) {
            items.push({ icon: 'fa-trash', label: tr('chat.clear', 'Clear chat'), danger: true,
                run: function () { if (typeof window.clearChat === 'function') window.clearChat(); } });
        }
        openMenu({ id: 'chatHeadMenuModal', key: 'head', title: tr('chat.title', 'Club Chat'), items: items });
    }
    function clubBack() {
        try {
            if (document.querySelector('#chatCard.chat-fullscreen') && typeof window.toggleChatFullscreen === 'function') window.toggleChatFullscreen();
        } catch (e) { logErr('TO_CHAT.back', e); }
        /* 8.1-d: întâi se consumă intrarea din istoric a chatului, apoi se schimbă tabul. */
        clubExit(function () { if (typeof window.switchTab === 'function') window.switchTab('hubclub'); });
    }
    function bindClubHead() {
        /* Pe card, nu pe antet: cardul e mutat în <body> la ecran complet și își
           duce ascultătorul cu el. */
        var card = document.getElementById('chatCard');
        if (!card || card.__toHead) return;
        card.__toHead = true;
        /* 8.13-1 (28 oct. 2026): și ⤢, ➤ și ✕ din bara de răspuns, care aveau handlere inline
           (regula „zero onclick=", decizia lui Alex din 27 oct.). Faza de bubbling, ca înainte:
           compunerea își normalizează textul pe CAPTURA zonei de scris, deci tot înaintea
           trimiterii. EN: fullscreen, send and reply-cancel moved from inline handlers. */
        card.addEventListener('click', function (e) {
            var b = e.target && e.target.closest ? e.target.closest('[data-action="chat-menu"], [data-action="chat-back"], [data-action="chat-emoji"], [data-action="chat-fs"], [data-action="chat-send"], [data-action="chat-reply-x"]') : null;
            if (!b || !card.contains(b)) return;
            var act = b.getAttribute('data-action');
            if (act === 'chat-menu') clubHeadMenu();
            else if (act === 'chat-emoji') emoToggle('club');   /* 8.3 (înainte: un onclick spre selectorul vechi, scos în 8.5-E) */
            else if (act === 'chat-fs') { if (typeof window.toggleChatFullscreen === 'function') window.toggleChatFullscreen(); }
            else if (act === 'chat-send') { if (typeof window.sendMessage === 'function') window.sendMessage(); }
            else if (act === 'chat-reply-x') { if (typeof window.cancelChatReply === 'function') window.cancelChatReply(); }
            else clubBack();
        });
    }

    /* ── Căutarea plutitoare (8.1-b2) ──────────────────────────────────────────
       RO: Câmpul `#chatSearch` e comutat în continuare de `toggleChatSearch`
           (app-modules-2.js, neatinsă: golește textul și resetează filtrul la
           închidere). Bara care îl înconjoară (`#chatSearchBar`, cu lupa și ✕) îl
           urmează: se arată ÎNAINTE de comutare, fiindcă `toggleChatSearch` pune
           focusul imediat, iar un câmp dintr-un părinte ascuns nu primește focus
           (tastatura nu s-ar deschide). Un MutationObserver pe `style` o ține în
           acord și dacă altcineva comută câmpul.
           • ✕ și Escape închid. Escape NU mai ajunge la ascultătorul global care
             iese din ecranul complet (app-modules-2.js).
           • „Caută" din ⋮ cu căutarea deja deschisă doar mută focusul în câmp.
       EN: floating search bar that follows #chatSearch; ✕ / Escape close it. */
    function searchEls() {
        return { bar: document.getElementById('chatSearchBar'), inp: document.getElementById('chatSearch') };
    }
    function searchIsOpen(inp) { var d = inp ? inp.style.display : ''; return !!d && d !== 'none'; }
    function openClubSearch() {
        var s = searchEls();
        if (!s.inp || typeof window.toggleChatSearch !== 'function') return;
        if (s.bar) s.bar.hidden = false;
        if (searchIsOpen(s.inp)) { try { s.inp.focus({ preventScroll: true }); } catch (e) { /* noop */ } return; }
        window.toggleChatSearch();
    }
    function closeClubSearch() {
        var s = searchEls();
        if (s.inp && searchIsOpen(s.inp) && typeof window.toggleChatSearch === 'function') window.toggleChatSearch();
        if (s.bar) s.bar.hidden = true;
        /* Focusul pe ⋮ (de unde s-a deschis) — doar fără atingere: pe telefon,
           focusul pe câmpul de scriere ar redeschide tastatura. */
        if (!isTouchPrimary()) {
            try { var mb = document.getElementById('chatMenuBtn'); if (mb) mb.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
    }
    function bindClubSearch() {
        var s = searchEls();
        if (!s.bar || !s.inp || s.bar.__toSearch) return;
        s.bar.__toSearch = true;
        var sync = function () { s.bar.hidden = !searchIsOpen(s.inp); syncClubLayers(); };   /* 8.1-d */
        sync();
        try { new MutationObserver(sync).observe(s.inp, { attributes: true, attributeFilter: ['style'] }); } catch (e) { logErr('TO_CHAT.search', e); }
        s.bar.addEventListener('click', function (e) {
            var x = e.target && e.target.closest ? e.target.closest('[data-action="chat-search-close"]') : null;
            if (!x) return;
            e.preventDefault();
            closeClubSearch();
        });
        s.inp.addEventListener('keydown', function (e) {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            closeClubSearch();
        });
        /* 8.13-1: căutarea la fiecare literă (înainte: `oninput` în index.html). */
        s.inp.addEventListener('input', function () {
            if (typeof window.chatSearchChanged === 'function') window.chatSearchChanged(s.inp.value);
        });
    }

    /* ── Chatul de club pe toată înălțimea (8.1-b2) ──────────────────────────
       RO: Cerința: de la bara de sus (selectorul de cluburi) până la bara de
           navigare de jos. Înainte, conversația avea înălțime FIXĂ (420 px pe orice
           telefon, 500 px pe desktop) ⇒ gol dedesubt și pagină care derula.
           Măsurăm: înălțimea nouă = cea de acum + cât lipsește (sau prisosește)
           până când documentul are exact înălțimea ferestrei. Rezultatul e
           `--chat-fill` (chat.css).
           • Formula nu depinde de propria valoare ⇒ se așază dintr-o trecere; se
             scrie doar la o diferență ≥ 1 px (altfel ResizeObserver ar bucla).
           • Cu tastatura deschisă (sub 768 px) NU măsurăm: înălțimea o dă modulul
             de tastatură din app-core.js (`--chat-avail`, §59), neatins. Pe ecran complet nici
             atât (cardul e în <body>, cu regulile lui).
           • Minimum 280 px (telefon în peisaj): sub el pagina derulează, ca azi.
           • Recalcul: la intrarea în chat, la redimensionare / rotire, la
             închiderea tastaturii și când se schimbă ceva deasupra (bannere,
             fonturi) — ResizeObserver pe `#mainContainer`, conectat DOAR cât
             chatul e deschis. Măsurarea rulează în `requestAnimationFrame`, o dată
             pe cadru (§54.4).
           • Fără ResizeObserver / MutationObserver (browsere foarte vechi) chatul
             rămâne la înălțimea fixă de azi.
       EN: fills the viewport height in the chat tab; keyboard handling stays in
           app-core.js. */
    var FILL_MIN = 280;
    var fillRaf = 0;
    var mqNarrow = null;
    try { mqNarrow = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null; } catch (e) { /* noop */ }
    function fitClub() {
        fillRaf = 0;
        try {
            var card = document.getElementById('chatCard');
            var tab = document.getElementById('tab-chat');
            var main = document.getElementById('mainContainer');
            if (!card || !tab || !main || !tab.classList.contains('active')) return;
            if (card.parentNode !== tab || card.classList.contains('chat-fullscreen')) return;
            /* Doar sub 768 px, unde se aplică `--chat-avail`. Peste, `kb-open` apare și
               când fereastra de desktop e micșorată pe înălțime (≥ 120 px față de
               maxim) — prins la test: chatul nu se mai reașeza și pagina derula. */
            if (document.body.classList.contains('kb-open') && mqNarrow && mqNarrow.matches) return;
            var cont = card.querySelector('.chat-container');
            var r = cont ? cont.getBoundingClientRect() : null;
            if (!r || !r.height) return;
            var padB = parseFloat(getComputedStyle(document.body).paddingBottom) || 0;
            /* Capătul documentului în coordonatele PAGINII: același rezultat oricât
               ar fi derulată pagina în acest moment. */
            var docBottom = main.getBoundingClientRect().bottom + (window.scrollY || 0) + padB;
            var target = Math.max(FILL_MIN, Math.floor(r.height + (window.innerHeight || 0) - docBottom));
            var cur = parseFloat(card.style.getPropertyValue('--chat-fill')) || 0;
            if (card.classList.contains('chat-fill') && Math.abs(target - cur) < 1) return;
            card.style.setProperty('--chat-fill', target + 'px');
            card.classList.add('chat-fill');
        } catch (e) { logErr('TO_CHAT.fit', e); }
    }
    function scheduleFit() { if (!fillRaf) fillRaf = requestAnimationFrame(fitClub); }
    function bindClubFill() {
        var tab = document.getElementById('tab-chat');
        var main = document.getElementById('mainContainer');
        if (!tab || !main || tab.__toFill) return;
        if (typeof ResizeObserver !== 'function' || typeof MutationObserver !== 'function') return;
        tab.__toFill = true;
        var on = false;
        var ro = new ResizeObserver(scheduleFit);
        var sync = function () {
            var act = tab.classList.contains('active');
            if (act && !on) { on = true; ro.observe(main); scheduleFit(); }
            else if (!act && on) { on = false; ro.disconnect(); }
        };
        new MutationObserver(sync).observe(tab, { attributes: true, attributeFilter: ['class'] });
        var onResize = function () { if (on) scheduleFit(); };
        window.addEventListener('resize', onResize);
        if (window.visualViewport) window.visualViewport.addEventListener('resize', onResize);
        sync();
    }
    TO_CHAT.club.fit = scheduleFit;

    /* ── Butonul „înapoi" al telefonului închide stratul de deasupra (8.1-c1bis) ─
       RO: Cerința: în conversația privată, „înapoi" de pe telefon (sau din
           browser / mouse) o închide ca ←, în loc să navigheze pe ecranul de
           dedesubt. Mecanism comun, refolosibil la 8.1-d (ecran complet, foi):
           • la deschidere: o intrare nouă în istoric, cu ACEEAȘI adresă
             (`pushState` fără URL) ⇒ nu pornește niciun `hashchange`, deci
             navigarea aplicației (nav.js, cele două ascultătoare) nu vede nimic;
           • „înapoi" scoate intrarea ⇒ `popstate` ⇒ închidem stratul;
           • închis altfel (←, Escape, fundal) ⇒ consumăm noi intrarea
             (`history.back()`), ca următorul „înapoi" să nu fie „mort";
           • ⚠ consumăm DOAR dacă intrarea noastră e chiar cea curentă și adresa e
             aceeași ca la deschidere: dacă între timp s-a navigat (alt tab, rută
             din notificare), un `back()` ar întoarce aplicația pe ecranul vechi.
             În cazul acela rămâne o intrare fără efect (un „înapoi" în gol, rar).
           • Dacă ajungem PE intrarea noastră (s-a scos una de deasupra ei, ex.
             navigare prin #), stratul rămâne deschis.
           Nimic din garda de modificări nesalvate (§66.11) nu e atins: nu chemăm
           `switchTab` și nu schimbăm adresa.
       EN: phone/browser Back closes the top layer via a same-URL history entry. */
    /* ── 8.1-d: „înapoi" și `Escape` închid câte UN strat, de sus în jos ────────
       RO: Înainte: „înapoi" părăsea chatul cu meniul / căutarea încă deschise, iar
           pe ecran complet cu ⋮ deschis `Escape` închidea meniul ȘI ecranul complet
           (două ascultătoare globale, app-core.js + app-modules-2.js).
           • Straturile se ÎNREGISTREAZĂ (`TO_CHAT.back.open/done`, același API):
             DM-ul (social.js), selecția, orice meniu / foaie (`openMenu`), căutarea
             clubului, ecranul complet și — cât chatul e pe ecran — orice fereastră
             nouă de deasupra (poza mărită, profil, editare, confirmare…; observate
             pe <body>).
           • Fiecare strat are intrarea LUI în istoric, pusă la DESCHIDERE (deci în
             timpul atingerii tale). „Înapoi" scoate intrarea de sus ⇒ se închide
             stratul ei; nu se adaugă nimic în timpul lui „înapoi".
             ⚠ 8.1-d (prima variantă) ținea o singură intrare și o punea la loc după
             fiecare „înapoi". Chrome pe Android sare peste intrările adăugate FĂRĂ o
             atingere a utilizatorului ⇒ al doilea „înapoi" ieșea din aplicație
             (raportat de Alex: pașii 6 și 8). De aici regula: intrări noi doar la
             deschidere.
           • Închis altfel (✕, fundal, opțiune din meniu) ⇒ intrarea devine „liberă":
             o preia stratul deschis imediat după (ex. Selectează, Caută, Răspunde
             privat), altfel se consumă după 400 ms (`history.go(-n)`), iar dacă între
             timp s-a navigat, e sărită când „înapoi" ajunge la ea (fără apăsări moarte).
           • `Escape` (desktop) închide stratul de SUS văzut pe ecran (`closeTop`),
             prins pe `<html>` la captură: DUPĂ dialogurile de confirmare (care îl
             opresc pe `document`), dar ÎNAINTEA celor două ascultătoare globale.
             Doar când chatul e pe ecran; altfel nu se schimbă nimic.
           • Codurile intrărilor au sesiunea în ele ⇒ după o reîncărcare, intrările
             vechi nu se confundă cu cele noi (sunt sărite).
           Nimic din garda de modificări nesalvate (§66.11) nu e atins: nu chemăm
           `switchTab` (doar ← din chat, ca înainte) și nu schimbăm adresa.
       EN: one history entry per chat layer, pushed when the layer opens (inside the
           user's gesture: Chrome skips entries added without one); Back pops the top
           entry and closes its layer; Escape closes the topmost visible layer. */
    var layers = {}, layerOrder = [];
    var ents = [];                                     /* intrările noastre, de jos în sus: { id, key, href } */
    var RUN = Date.now().toString(36), entSeq = 0;
    var bi = { n: 0, at: 0, then: null, timer: 0 };    /* `history.back()`-urile noastre */
    var BI_MAX_MS = 2000, SETTLE_MS = 400;
    var settleT = 0, pendingPush = [];
    function noop() {}
    function biActive() {
        if (bi.n && Date.now() - bi.at > BI_MAX_MS) bi.n = 0;      /* popstate pierdut: nu blocăm „înapoi" */
        return bi.n > 0;
    }
    function ourBack(k, then) {
        bi.n++; bi.at = Date.now(); bi.then = then || null;
        if (then) { clearTimeout(bi.timer); bi.timer = setTimeout(biLanded, 600); }   /* plasă: fără popstate */
        if ((k || 1) === 1) history.back(); else history.go(-k);
    }
    function biLanded() {
        clearTimeout(bi.timer); bi.timer = 0;
        var f = bi.then; bi.then = null;
        var p = pendingPush; pendingPush = [];
        p.forEach(function (key) { if ((key in layers) && !entOf(key)) pushEnt(key); });
        if (f) { try { f(); } catch (e) { logErr('TO_CHAT.back', e); } }
    }
    function entOf(key) {
        for (var i = ents.length - 1; i >= 0; i--) if (ents[i].key === key) return ents[i];
        return null;
    }
    function onTop() {
        var t = ents[ents.length - 1];
        return !!t && !!history.state && history.state.toBack === t.id && location.href === t.href;
    }
    function pushEnt(key) {
        try {
            if (!window.history || typeof history.pushState !== 'function') return;
            var id = RUN + ':' + (++entSeq);
            history.pushState({ toBack: id }, '');
            ents.push({ id: id, key: key, href: location.href });
        } catch (e) { logErr('TO_CHAT.back', e); }
    }
    function backOpen(key, close) {
        if (!(key in layers)) layerOrder.push(key);
        layers[key] = (typeof close === 'function') ? close : noop;
        if (entOf(key)) return;                        /* același strat redeschis (ex. alt DM) */
        var top = ents[ents.length - 1];
        if (top && top.key == null && onTop()) { top.key = key; return; }   /* preia intrarea unui strat tocmai închis */
        if (biActive()) { pendingPush.push(key); return; }
        pushEnt(key);
    }
    function unregister(key) {
        if (!(key in layers)) return false;
        delete layers[key];
        var i = layerOrder.indexOf(key);
        if (i !== -1) layerOrder.splice(i, 1);
        var en = entOf(key);
        if (en) en.key = null;                          /* intrare liberă */
        return true;
    }
    function scheduleSettle() { clearTimeout(settleT); settleT = setTimeout(settleEnts, SETTLE_MS); }
    function backDone(key) {
        if (!unregister(key)) return;
        scheduleSettle();
    }
    /* Intrările libere de sus se consumă — doar dacă suntem chiar pe ele (altfel s-a
       navigat între timp; vor fi sărite la „înapoi"). Nu în timpul unui salt la citat:
       o deplasare în istoric oprea derularea lină (testul 4, desktop). */
    function settleEnts() {
        settleT = 0;
        try {
            if (biActive() || Date.now() < jumpQuietUntil) { scheduleSettle(); return; }
            var k = 0;
            for (var i = ents.length - 1; i >= 0 && ents[i].key == null; i--) k++;
            if (!k || !onTop()) return;
            ents.splice(ents.length - k, k);
            ourBack(k);
        } catch (e) { logErr('TO_CHAT.back', e); }
    }
    /* Rulează închiderea înregistrată a unui strat (ex. `_dmOnBack`). */
    function runLayer(key) {
        var f = layers[key];
        if (!f) return false;
        unregister(key);
        try { f(); } catch (e) { logErr('TO_CHAT.back', e); }
        return true;
    }

    /* Fereastra / meniul de DEASUPRA stratului: ultimul `.modal.active` sau meniu
       de persoană (block.js) aflat după el în pagină (ferestrele noi se adaugă la
       finalul lui <body>). DM-ul e strat separat, nu „deasupra". */
    function overlayAbove(anchor) {
        var l = document.querySelectorAll('.modal.active, .user-menu-pop');
        for (var i = l.length - 1; i >= 0; i--) {
            var el = l[i];
            if (el.id === 'dmModal' || el === anchor || (anchor && anchor.contains(el))) continue;
            if (anchor && !(anchor.compareDocumentPosition(el) & 4)) continue;   /* 4 = FOLLOWING */
            return el;
        }
        return null;
    }
    function closeOverlay(el) {
        if (el.id === 'lightboxOverlay') {
            if (typeof window.closeLightbox === 'function') window.closeLightbox(); else el.remove();
            return;
        }
        if (el.classList.contains('user-menu-pop')) {
            if (typeof window._blkCloseMenus === 'function') window._blkCloseMenus(); else el.remove();
            return;
        }
        /* Aceeași cale ca `closeAllModals` (statică ⇒ ascunsă; `__dispose`; altfel scoasă). */
        if (typeof window._disposeModal === 'function') window._disposeModal(el);
        else if (typeof el.__dispose === 'function') el.__dispose();
        else el.remove();
    }
    function openDmModal() { return document.querySelector('#dmModal.modal.active'); }   /* DM-ul deschis (social.js openDM) */
    function isFs(card) { return !!(card && card.classList.contains('chat-fullscreen')); }
    function exitFs() {
        var card = document.getElementById('chatCard');
        unregister('chat-fs');
        if (isFs(card) && typeof window.toggleChatFullscreen === 'function') window.toggleChatFullscreen();
    }
    /* Chatul e pe ecran: DM deschis, ecran complet sau tabul Chat. */
    function chatVisible() {
        if (openDmModal()) return true;
        if (isFs(document.getElementById('chatCard'))) return true;
        var tab = document.getElementById('tab-chat');
        return !!(tab && tab.classList.contains('active'));
    }
    /* Închide stratul de SUS văzut pe ecran (Escape). Întoarce `true` dacă a închis ceva. */
    function closeTop() {
        var lb = document.getElementById('lightboxOverlay');
        if (lb) { closeOverlay(lb); return true; }
        var dm = openDmModal();
        var card = document.getElementById('chatCard');
        var ov = overlayAbove(dm || card);
        if (ov) { closeOverlay(ov); return true; }
        if (mp.box && !mp.box.hidden) { mentionClose(); return true; }
        if (window.TO_EMOJI && window.TO_EMOJI.isOpen && window.TO_EMOJI.isOpen()) { window.TO_EMOJI.close(); return true; }   /* 8.3 */
        if (selActive(dm ? 'dm' : 'club')) { selEnd(); return true; }
        if (dm) {
            var dsb = dm.querySelector('#dmSearchBar');
            var sx = (dsb && !dsb.hidden) ? dsb.querySelector('[data-action="dm-search-close"]') : null;
            if (sx) { sx.click(); return true; }
            if (runLayer('dm')) return true;
            if (typeof window.closeDM === 'function') { window.closeDM(); return true; }
            return false;
        }
        var s = searchEls();
        if (s.inp && searchIsOpen(s.inp)) { unregister('chat-search'); closeClubSearch(); return true; }
        if (isFs(card)) { exitFs(); return true; }
        return false;
    }
    window.addEventListener('popstate', function (e) {
        if (biActive()) { bi.n--; biLanded(); return; }
        try {
            var s = e.state && e.state.toBack;
            var idx = -1;
            if (s) for (var i = 0; i < ents.length; i++) if (ents[i].id === s) { idx = i; break; }
            if (idx !== -1 && idx === ents.length - 1) {
                /* Ai revenit PE intrarea noastră de sus (dintr-o pagină din față): stratul
                   rămâne. Dacă era liberă, o sărim. */
                if (ents[idx].key == null) { ents.pop(); history.back(); }
                return;
            }
            var left = ents.splice(idx + 1);            /* intrările părăsite, de jos în sus */
            var did = false;
            for (var j = left.length - 1; j >= 0; j--) {
                var k = left[j].key;
                if (k && (k in layers)) { did = true; runLayer(k); }
            }
            if (did) {
                if (s && idx === -1) ourBack(1);        /* am aterizat pe o intrare veche (altă sesiune) */
                else scheduleSettle();                  /* intrarea de dedesubt, dacă e liberă */
                return;
            }
            /* Doar intrări libere / vechi: „înapoi" face pasul următor, nu e mort. */
            if (left.length || s) history.back();
        } catch (err) { logErr('TO_CHAT.back', err); }
    });
    /* `Escape` = stratul de sus (vezi mai sus de ce pe `<html>`, la captură). */
    function chatOnScreen() {
        if (openDmModal()) return true;
        if (isFs(document.getElementById('chatCard'))) return true;
        var tab = document.getElementById('tab-chat');
        return !!(layerOrder.length && tab && tab.classList.contains('active'));
    }
    try {
        document.documentElement.addEventListener('keydown', function (e) {
            if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
            try {
                if (!chatOnScreen() || !closeTop()) return;
            } catch (err) { logErr('TO_CHAT.esc', err); return; }
            scheduleSettle();                           /* intrarea stratului închis se consumă */
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        }, true);
    } catch (e) { logErr('TO_CHAT.esc', e); }
    /* Ferestrele noi de deasupra chatului (poza mărită, profil, editare, confirmare,
       meniul persoanei) devin straturi cât chatul e pe ecran: intrarea se pune când
       apar (tot în timpul atingerii care le-a deschis), se eliberează când dispar.
       DM-ul și meniurile se înregistrează singure. */
    var ovSeq = 0;
    /* v9 zona 2 (3 nov. 2026): o fereastră marcată `data-back` devine strat ORIUNDE, nu doar cât chatul e pe
       ecran. Raportat de Alex: în Catalog, „înapoi" de pe telefon schimba tabul din spate și lăsa „Adaugă joc"
       deschisă. Marcajul se pune zonă cu zonă (ferestrele fiecărei zone, la curățenia ei), ca să nu se schimbe
       deodată comportamentul tuturor ferestrelor aplicației. Închiderea e aceeași ca mai sus (`closeOverlay`).
       EN: modals marked `data-back` become Back layers anywhere, not only while the chat is on screen. */
    function backAnywhere(el) { return !!(el && el.hasAttribute && el.hasAttribute('data-back')); }
    function isOverlayEl(el) {
        if (!el || el.nodeType !== 1 || el.id === 'dmModal' || el.id === 'chatCard') return false;
        if (el.classList.contains('msg-menu-modal')) return false;
        return el.id === 'lightboxOverlay' || el.classList.contains('user-menu-pop')
            || (el.classList.contains('modal') && el.classList.contains('active'));
    }
    function bindOverlayLayers() {
        if (!document.body || document.body.__toOverlays || typeof MutationObserver !== 'function') return;
        document.body.__toOverlays = true;
        try {
            new MutationObserver(function (recs) {
                recs.forEach(function (r) {
                    for (var i = 0; i < r.removedNodes.length; i++) {
                        var x = r.removedNodes[i];
                        if (x && x.__toBack) { var k = x.__toBack; x.__toBack = null; backDone(k); }
                    }
                    for (var j = 0; j < r.addedNodes.length; j++) {
                        var el = r.addedNodes[j];
                        if (!isOverlayEl(el) || el.__toBack || !(chatVisible() || backAnywhere(el))) continue;
                        el.__toBack = 'ov:' + (++ovSeq);
                        backOpen(el.__toBack, (function (node) { return function () { closeOverlay(node); }; })(el));
                    }
                });
            }).observe(document.body, { childList: true });
        } catch (e) { logErr('TO_CHAT.back', e); }
    }
    /* ← din chat (clubBack): ieși din ecranul complet și consumi intrările chatului
       ÎNAINTE de a schimba tabul, ca „înapoi" de după să nu calce pe intrări moarte. */
    function clubExit(go) {
        try {
            ['chat-fs', 'chat-search', 'chat-select'].forEach(unregister);
            var k = 0;
            for (var i = ents.length - 1; i >= 0 && ents[i].key == null; i--) k++;
            if (k && k === ents.length && onTop() && !biActive()) {
                ents = [];
                ourBack(k, go);
                return;
            }
        } catch (e) { logErr('TO_CHAT.back', e); }
        go();
    }
    TO_CHAT.back = { open: backOpen, done: backDone, closeTop: closeTop };

    /* ── Separatoarele de zile (8.1-c1bis, club + DM) ────────────────────────
       RO: Cerința: data scoasă din fiecare mesaj privat („10/10, 11:37" era
           redundantă) și, în ambele conversații, un separator discret între zile,
           ca în alte aplicații de chat: „Azi", „Ieri", ziua săptămânii pentru
           ultima săptămână, apoi data (cu anul doar dacă e alt an). Textele
           „azi/ieri" există deja (`time.today`, `time.yesterday`, RO + EN).
           Ziua se calculează în fusul orar al telefonului (ca ora din bule).
       EN: day separators shared by club chat and DMs. */
    function dayKey(ts) {
        var d = new Date(ts);
        if (isNaN(d.getTime())) return '';
        return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
    }
    function cap(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }
    function dayLabel(ts) {
        var d = new Date(ts);
        if (isNaN(d.getTime())) return '';
        var start = function (x) { return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime(); };
        var days = Math.round((start(new Date()) - start(d)) / 86400000);
        var loc = (typeof window.appDateLocale === 'function') ? window.appDateLocale() : undefined;
        if (days === 0) return cap(tr('time.today', 'today'));
        if (days === 1) return cap(tr('time.yesterday', 'yesterday'));
        try {
            if (days > 1 && days < 7) return cap(d.toLocaleDateString(loc, { weekday: 'long' }));
            var o = { day: 'numeric', month: 'long' };
            if (d.getFullYear() !== new Date().getFullYear()) o.year = 'numeric';
            return d.toLocaleDateString(loc, o);
        } catch (e) { return d.toDateString(); }
    }
    function daySepHtml(ts) {
        var l = dayLabel(ts);
        return l ? '<div class="chat-day"><span>' + esc(l) + '</span></div>' : '';
    }
    /* Lipește separatoarele între rânduri: `items` = [{ id, ts, html }], `prevTs` =
       ziua ultimului rând deja afișat (la adăugarea la final, fără separator dublu),
       `unread` = { id, n }: bara „N mesaje necitite" înaintea primului necitit
       (după separatorul lui de zi, dacă are unul). */
    function withDays(items, prevTs, unread) {
        var out = '';
        var last = prevTs ? dayKey(prevTs) : '';
        for (var i = 0; i < items.length; i++) {
            var k = dayKey(items[i].ts);
            if (k && k !== last) { out += daySepHtml(items[i].ts); last = k; }
            if (unread && items[i].id != null && String(items[i].id) === String(unread.id)) out += unreadHtml(unread.n);
            out += items[i].html;
        }
        return out;
    }
    TO_CHAT.day = { key: dayKey, label: dayLabel, withDays: withDays, todayKey: function () { return dayKey(Date.now()); } };

    /* ── Deschiderea la primul mesaj necitit (8.1-c2a, club + DM) ─────────────
       RO: Ca în WhatsApp: când intri într-o conversație cu mesaje necitite, vezi
           bara „N mesaje necitite" sus, iar sub ea primul necitit — nu capătul
           conversației. Dacă necititele încap pe ecran, rămâi jos (derularea se
           oprește singură la capăt). Bara stă până ieși din conversație.
           Română: „20 DE mesaje" de la 20 în sus (și 100, 120…), ca în gramatică.
       EN: open conversations at the first unread message, with an unread bar. */
    function unreadLabel(n) {
        n = +n || 0;
        var ro = (typeof window.appLangCode === 'function') && window.appLangCode() === 'ro';
        var key = n === 1 ? 'chat.unread1' : ((ro && (n % 100 === 0 || n % 100 >= 20)) ? 'chat.unreadNde' : 'chat.unreadN');
        return tr(key, n === 1 ? '1 unread message' : '{n} unread messages').replace('{n}', String(n));
    }
    function unreadHtml(n) {
        return '<div class="chat-unread" data-unread="1"><span>' + esc(unreadLabel(n)) + '</span></div>';
    }
    /* Pune bara sus (cu 8 px aer). Întoarce true dacă exista. */
    function scrollToUnread(list) {
        var bar = list && list.querySelector('.chat-unread');
        if (!bar) return false;
        list.scrollTop += bar.getBoundingClientRect().top - list.getBoundingClientRect().top - 8;
        return true;
    }
    TO_CHAT.unread = { html: unreadHtml, label: unreadLabel, scrollTo: scrollToUnread };

    /* ── Actualizare rând cu rând, fără „pâlpâire" (8.1-c2b, chatul de club) ─────
       RO: Simptom (Alex): chatul de club „clipea" la fiecare schimbare din club;
           DM-ul nu. Cauza: `loadChat` redesena TOATĂ lista (`innerHTML`) când se
           schimba ceva vizibil — cel mai des bifele ✓✓ (citirile altora vin la
           fiecare reîmprospătare a clubului). Avatarele și pozele erau recreate,
           deci se reîncărcau ⇒ clipirea. Acum, când lista are aceleași mesaje în
           aceeași ordine, comparăm fiecare rând cu HTML-ul lui anterior:
           • identic ⇒ nu-l atingem deloc;
           • diferă DOAR bifa ⇒ schimbăm doar bifa (clasa + textul);
           • altfel (editat, poze noi) ⇒ înlocuim DOAR acel rând.
           Dacă vreun rând lipsește din pagină, întoarcem false ⇒ desenare completă,
           ca înainte. Separatoarele de zi și bara de necitite rămân pe loc.
       EN: keyed in-place row patching instead of re-rendering the whole list. */
    /* (8.1-c2c: bifa are acum și `title` ⇒ grupul 2 = atributul, grupul 3 = textul) */
    var SEEN_RE = /<span class="(chat-seen[^"]*)"(?: title="([^"]*)")?>([^<]*)<\/span>/;
    function rowOf(id) {
        var m = document.getElementById('msg-' + id);
        return (m && m.closest) ? m.closest('.chat-row') : null;
    }
    /* 28 oct. 2026: `find` opțional (id ⇒ rândul), ca DM-ul (`#dmmsg-<id>`, chiar rândul) să
       folosească același mecanism; fără el, rândul chatului de club (ca înainte).
       EN: optional row finder so the DM thread can reuse the same patching. */
    function patchRows(container, items, find) {
        var rows = [];
        find = find || rowOf;
        for (var i = 0; i < items.length; i++) {
            var r = find(items[i].id);
            if (!r || !container.contains(r)) return false;      /* lipsește ⇒ desenare completă */
            rows.push(r);
        }
        for (var j = 0; j < items.length; j++) {
            var row = rows[j], html = items[j].html, old = row.__toHtml;
            if (old === html) continue;
            var nSeen = html.match(SEEN_RE);
            if (old && old.replace(SEEN_RE, '') === html.replace(SEEN_RE, '') && nSeen) {
                var s = row.querySelector('.chat-seen');
                if (s) {
                    s.className = nSeen[1];
                    if (nSeen[2] != null) s.title = nSeen[2].replace(/&quot;/g, '"').replace(/&amp;/g, '&'); else s.removeAttribute('title');
                    s.textContent = nSeen[3];
                    row.__toHtml = html;
                    continue;
                }
            }
            var t = document.createElement('template');
            t.innerHTML = html.trim();
            var nr = t.content.firstElementChild;
            if (!nr) return false;
            row.parentNode.replaceChild(nr, row);
            nr.__toHtml = html;
            /* 28 oct. 2026 (raportat de Alex): bula `.chat-message` are animația de intrare
               `messageIn` (style.css: din invizibil, 10 px mai jos). Un rând ÎNLOCUIT aici (🕓 → ✓
               la trimitere, „Nu s-a trimis", editare, reacție) o rula din nou ⇒ mesajul clipea.
               Măsurat: la fel înainte și după 8.13-1 (de la 8.1-s). DM-ul n-o are (`.dm-msg`).
               Rândul actualizat nu mai „intră" (chat.css, `.is-patched`); mesajele noi da.
               EN: replaced rows skip the entrance animation (it made the message blink). */
            nr.classList.add('is-patched');
        }
        return true;
    }
    /* Ține minte HTML-ul fiecărui rând desenat (comparat la următoarea actualizare). */
    function stampRows(container, items, find) {
        find = find || rowOf;
        for (var i = 0; i < items.length; i++) {
            var r = find(items[i].id);
            if (r && container.contains(r)) r.__toHtml = items[i].html;
        }
    }
    TO_CHAT.patchRows = patchRows;
    TO_CHAT.stampRows = stampRows;

    /* ── Fereastra glisantă a chatului de club (8.1-c2c) ─────────────────────────
       RO: `loadChat` arată ultimele 50 de mesaje. Defect (Alex: insigna ↓ apărea în
           DM, dar NU în club): într-un club cu peste 50 de mesaje, fiecare mesaj nou
           scoate primul din listă ⇒ lista nu mai era „aceeași + mesaje noi" ⇒
           desenare completă (deci și „pâlpâire") și fără numărul de mesaje noi. Acum
           scoatem doar rândurile ieșite din fereastră (sus), refacem separatorul de
           zi al primului rând rămas și păstrăm pe ecran exact ce citeai (ancoră pe
           primul rând rămas). EN: sliding 50-message window without a full redraw. */
    function trimTop(container, ids, firstId, firstTs) {
        var anchor = rowOf(firstId);
        if (!anchor || !container.contains(anchor)) return false;
        var before = anchor.getBoundingClientRect().top;
        for (var i = 0; i < ids.length; i++) {
            var r = rowOf(ids[i]);
            if (r && container.contains(r)) r.parentNode.removeChild(r);
        }
        while (container.firstElementChild && container.firstElementChild !== anchor) container.removeChild(container.firstElementChild);
        var sep = daySepHtml(firstTs);
        if (sep) container.insertAdjacentHTML('afterbegin', sep);
        var delta = anchor.getBoundingClientRect().top - before;
        if (delta) container.scrollTop += delta;
        return true;
    }
    TO_CHAT.trimTop = trimTop;

    /* 8.1-c3a: perechea lui `trimTop` — mesaje mai VECHI adăugate SUS (derulare în sus).
       `html` = rândurile noi (cu separatoarele lor de zi), `anchorId` = fostul prim rând.
       Separatorul de zi de deasupra fostului prim rând rămâne doar dacă ziua lui diferă
       de a ultimului rând adăugat; poziția de citire rămâne pe loc (ancoră pe rând).
       EN: prepend older rows; keep the reader's position and one separator per day. */
    function prependTop(container, html, anchorId, anchorTs, lastHeadTs) {
        var anchor = rowOf(anchorId);
        if (!anchor || !container.contains(anchor)) return false;
        var before = anchor.getBoundingClientRect().top;
        var n = container.firstElementChild;
        while (n && n !== anchor) {
            var nx = n.nextElementSibling;
            if (n.classList && n.classList.contains('chat-day')) container.removeChild(n);
            n = nx;
        }
        if (dayKey(anchorTs) !== dayKey(lastHeadTs)) {
            var sep = daySepHtml(anchorTs);
            if (sep) container.insertAdjacentHTML('afterbegin', sep);
        }
        container.insertAdjacentHTML('afterbegin', html);
        var delta = anchor.getBoundingClientRect().top - before;
        if (delta) container.scrollTop += delta;
        return true;
    }
    TO_CHAT.prependTop = prependTop;

    /* Mesaje noi sosite cât citeai mai sus ⇒ numărul lor pe butonul ↓ (8.1-c2b). */
    function notifyNew(list, n) {
        if (list && list.__toDownApi && n > 0) list.__toDownApi.add(n);
    }
    TO_CHAT.notifyNew = notifyNew;

    /* ── Mesaje grupate ca în WhatsApp (8.1-e, E2; D20, D22) ─────────────────────
       RO: Mesajele consecutive ale aceleiași persoane formează un GRUP: numele și
           avatarul apar o singură dată, lângă primul mesaj (D20), bulele stau mai
           apropiate, iar „coada" bulei apare doar la primul (chat.css). Grupul se
           rupe la: altă persoană, altă zi, bara de necitite, un mesaj de sistem sau
           o pauză de peste 5 minute (D20). Un mesaj șters nu poartă numele și
           avatarul (D22); dacă e primul din grup, ele trec pe următorul mesaj.
           ⚠ De ce clase puse DUPĂ desenare, nu alt HTML: rândul unui mesaj rămâne
             același oricare i-ar fi vecinii ⇒ `patchRows` / `trimTop` /
             `prependTop` rămân neatinse, iar la sosirea unui mesaj nou rândul de
             dinainte NU se recreează (doar își schimbă o clasă) ⇒ fără „pâlpâire".
           ⚠ Ora vine din DATE, nu dintr-un atribut din HTML: la trimitere ora o pune
             serverul (B3a), deci un atribut cu ora ar fi recreat rândul propriu la
             fiecare mesaj trimis.
           • `items` = [{ id, ts, sid (persoana), sys (mesaj de sistem), del (șters) }],
             în ordinea din listă; `unreadId` = primul necitit (bara de deasupra lui).
       EN: consecutive messages from one sender are grouped by toggling classes
           after render (rows stay neighbour-independent ⇒ no re-creation). */
    var GROUP_GAP_MS = 5 * 60 * 1000;
    function groupPlan(items, unreadId) {
        var out = [], prev = null, lead = false;
        for (var i = 0; i < items.length; i++) {
            var it = items[i] || {}, t = new Date(it.ts).getTime();
            var cont = !!prev && !it.sys && !prev.sys && !!it.sid && String(it.sid) === prev.sid
                && !isNaN(t) && !isNaN(prev.t) && Math.abs(t - prev.t) <= GROUP_GAP_MS
                && dayKey(it.ts) === prev.day
                && !(unreadId != null && String(it.id) === String(unreadId));
            if (!cont) lead = false;
            var isLead = !it.del && !lead;
            if (isLead) lead = true;
            out.push({ id: String(it.id), cont: cont, sub: !isLead });
            prev = { sid: String(it.sid || ''), t: t, day: dayKey(it.ts), sys: !!it.sys };
        }
        return out;
    }
    function groupRows(container, items, unreadId, rowFn) {
        try {
            if (!container || !items) return;
            var plan = groupPlan(items, unreadId);
            for (var i = 0; i < plan.length; i++) {
                var r = rowFn(plan[i].id);
                if (!r || !container.contains(r)) continue;
                /* Doar dacă diferă ⇒ un rând neschimbat nu e atins deloc. */
                if (r.classList.contains('g-cont') !== plan[i].cont) r.classList.toggle('g-cont', plan[i].cont);
                if (r.classList.contains('g-sub') !== plan[i].sub) r.classList.toggle('g-sub', plan[i].sub);
            }
            selPaint(container, plan, rowFn);                 /* 8.1-g: bifele selecției, după redesenare */
        } catch (e) { logErr('TO_CHAT.group', e); }
    }
    /* 8.14: punctul online pe avatarele din chatul de club (presence.js; datele vin din
       canalul clubului, deja deschis). Pe învelișul avatarului, construit cu DOM: avatarul
       are `overflow: hidden` și ar tăia punctul. EN: online dots on club avatars. */
    var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    function presDots(container) {
        try {
            var P = window.TO_PRES;
            if (!P || !container) return;
            var hits = container.querySelectorAll('.chat-avatar-hit[data-uid]');
            for (var i = 0; i < hits.length; i++) {
                var u = hits[i].getAttribute('data-uid') || '';
                var isId = UUID_RE.test(u);
                P.dot(hits[i], isId ? u : '', isId ? '' : u);
            }
            P.paint(container);
        } catch (e) { logErr('TO_CHAT.presence', e); }
    }
    /* ── 8.7-D1 — conversația văzută ⇒ notificările ei dispar din bara telefonului ──────
       RO: ca la WhatsApp: când chatul clubului sau DM-ul e pe ecran, notificările de sistem ale
           acelei conversații (tag `chat-<club>` / `dm-<persoană>`, puse de sw.js) se închid.
           Doar cu pagina VIZIBILĂ (o redesenare în fundal nu înseamnă „văzut"), doar cu
           notificări permise, cel mult o dată la 2 s pe conversație. Când: la fiecare desenare a
           conversației (`TO_CHAT.group.club / dm`, chemate deja de app-modules-2.js / social.js)
           și la revenirea aplicației în prim-plan, cu o conversație deschisă.
       EN: close the open conversation's system notifications while it is on screen. */
    var notifSeenAt = {};
    function convTag() {
        try {
            if (window._dmOpenWith) return 'dm-' + window._dmOpenWith;
            if (typeof currentTab !== 'undefined' && currentTab === 'chat' && typeof currentClubId !== 'undefined' && currentClubId) return 'chat-' + currentClubId;
        } catch (e) { /* noop */ }
        return null;
    }
    function notifSeen(tag) {
        try {
            if (!tag || document.visibilityState !== 'visible') return;
            if (!('serviceWorker' in navigator) || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
            var now = Date.now();
            if (notifSeenAt[tag] && now - notifSeenAt[tag] < 2000) return;
            notifSeenAt[tag] = now;
            navigator.serviceWorker.getRegistration().then(function (reg) {
                if (!reg || typeof reg.getNotifications !== 'function') return;
                return reg.getNotifications({ tag: tag }).then(function (list) {
                    list.forEach(function (n) { try { n.close(); } catch (e) { /* noop */ } });
                });
            }).catch(function (e) { logErr('TO_CHAT.notifSeen', e); });
        } catch (e) { logErr('TO_CHAT.notifSeen', e); }
    }
    try { document.addEventListener('visibilitychange', function () { if (!document.hidden) notifSeen(convTag()); }); } catch (e) { logErr('TO_CHAT.notifSeen', e); }
    /* 26 oct. 2026: indicatorul „scrie…" (chat-typing.js, la cerere) urmează conversația de pe
       ecran; modulul își verifică singur plecarea (alt tab, DM închis, aplicația în fundal).
       Fără demo și fără pagină ascunsă: nicio încărcare. EN: typing indicator follows the
       conversation on screen. */
    /* 8.7-D8 (26 oct.) — cifra de pe iconița aplicației instalate = cifra clopoțelului.
       RO: Badging API — Windows / macOS (Chrome, Edge, aplicația instalată) și iPhone (aplicația
           de pe ecranul de start, iOS 16.4+); Android Chrome n-o are ⇒ nimic (punctul îl pune
           sistemul). Se scrie doar la schimbare. Cifra se ține și în Cache Storage
           (`to-badge-v1`, doar un număr), ca sw.js s-o crească cu 1 la fiecare notificare
           primită cu aplicația închisă de tot; la deschidere, aplicația pune iar cifra exactă.
           Ieșirea din cont ⇒ 0 (renderNotifBadge fără utilizator).
       EN: app icon badge = bell count (feature-detected), mirrored for the service worker. */
    var badgeLast = -1;
    function appBadge(n) {
        try {
            n = Math.max(0, Math.floor(Number(n) || 0));
            if (n === badgeLast) return;
            badgeLast = n;
            if (typeof navigator.setAppBadge !== 'function') return;
            var p = n > 0 ? navigator.setAppBadge(n) : (typeof navigator.clearAppBadge === 'function' ? navigator.clearAppBadge() : navigator.setAppBadge(0));
            if (p && typeof p.catch === 'function') p.catch(function () { /* neinstalată / fără drept: nimic */ });
            if (typeof caches !== 'undefined' && caches && typeof caches.open === 'function') {
                caches.open('to-badge-v1').then(function (c) { return c.put('/__badge', new Response(String(n))); }).catch(function () { /* noop */ });
            }
        } catch (e) { logErr('TO_CHAT.badge', e); }
    }
    TO_CHAT.appBadge = appBadge;
    /* 27 oct. 2026 (Alex: emoji-urile apăreau „linie cu linie", pe Windows în zeci de secunde):
       încălzirea emoji-urilor (chat-emoji.js `warm`), o singură dată pe pagină, la ~2 s după
       prima conversație de pe ecran, cu pagina vizibilă; fără demo. Măsurat pe Windows: toate
       1.923 la rece 2,9–3,9 s ⇒ după încălzire 44 ms. Fișierele vin din memoria aplicației
       (sw.js), nu din rețea. EN: one-time emoji font warm-up after the first conversation. */
    var emoWarmT = 0, emoWarmDone = false;
    function emojiWarmSoon() {
        /* `convTag()`: doar cu o conversație CHIAR pe ecran — loadChat() rulează și la pornire,
           pe Dashboard; fără gardă, încălzirea pornea la toată lumea. EN: only with a chat on screen. */
        if (emoWarmDone || emoWarmT || !convTag()) return;
        emoWarmT = setTimeout(function () {
            emoWarmT = 0;
            try {
                if (!convTag() || document.visibilityState !== 'visible' || (typeof window.isDemoMode === 'function' && window.isDemoMode())) return;
                emoWarmDone = true;
                ensureModule(EMO_FILE, 'TO_EMOJI').then(function (M) { if (M && M.warm) return M.warm(); })
                    .catch(function (e) { if (!(e && e.isVersion)) emoWarmDone = false; });   /* fără internet ⇒ altă dată */
            } catch (e) { logErr('TO_CHAT.emoWarm', e); }
        }, 2000);
    }
    var typingFailAt = 0;   /* încărcare eșuată (fără internet) ⇒ pauză 60 s, nu la fiecare desenare */
    function typingSync() {
        try {
            if (document.visibilityState !== 'visible' || (typeof window.isDemoMode === 'function' && window.isDemoMode())) return;
            if (typingFailAt && Date.now() - typingFailAt < 60000) return;
            useModule('chat-typing.js', 'TO_TYPING', function (M) { M.sync(); }, true).then(function (m) { if (!m) typingFailAt = Date.now(); });
        } catch (e) { logErr('TO_CHAT.typing', e); }
    }
    TO_CHAT.group = {
        plan: groupPlan,
        club: function (container, items, unreadId) { groupRows(container, items, unreadId, rowOf); presDots(container); notifSeen(convTag()); typingSync(); emojiWarmSoon(); },
        dm: function (container, items, unreadId) {
            groupRows(container, items, unreadId, function (id) { return document.getElementById('dmmsg-' + id); });
            notifSeen(convTag());
            typingSync();
            emojiWarmSoon();
        }
    };

    /* ── Inițiale colorate în locul siluetei gri (8.1-e, E4; D23) ─────────────────
       RO: Pentru cine n-are poză sau emoji: un cerc cu 1–2 inițiale (prima literă
           din primele două cuvinte care au litere; diacriticele păstrate), pe una
           din 8 culori (chat.css, `.to-ini-c0…c7`, alb ≥ 5:1). Culoarea vine din
           id-ul persoanei (FNV-1a), nu din nume ⇒ nu se schimbă la redenumire.
           Fără nicio literă (ex. doar emoji) ⇒ '' și apelantul păstrează silueta.
           ⚠ La o relație blocată apelantul NU cheamă funcția (rămâne silueta, D23).
           ⚠ Funcție pură, fără DOM: același calcul se poate refolosi în service
             worker pentru iconița notificării (8.7-C).
           • Litera: `\p{L}` (+ semnele combinate `\p{M}`); construit cu `new RegExp`
             într-un `try`, ca un browser fără suport să nu oprească tot fișierul.
       EN: coloured initials; colour from the profile id; pure, reusable in the SW. */
    var INI_COLORS = 8;
    var INI_L = (function () { try { return new RegExp('\\p{L}', 'u'); } catch (e) { return /[A-Za-zÀ-ɏͰ-ϿЀ-ӿ]/; } })();
    var INI_M = (function () { try { return new RegExp('\\p{M}', 'u'); } catch (e) { return /[̀-ͯ]/; } })();
    function iniLetters(name) {
        var words = String(name || '').trim().split(/\s+/), out = [];
        for (var w = 0; w < words.length && out.length < 2; w++) {
            var ch = Array.from(words[w]);
            for (var i = 0; i < ch.length; i++) {
                if (!INI_L.test(ch[i])) continue;
                var s = ch[i];
                for (var j = i + 1; j < ch.length && INI_M.test(ch[j]); j++) s += ch[j];
                out.push(s.toUpperCase());
                break;
            }
        }
        return out.join('');
    }
    function iniColor(id) {
        var s = String(id == null ? '' : id), h = 2166136261;
        for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
        return h % INI_COLORS;
    }
    function iniHtml(id, name, size) {
        var l = iniLetters(name);
        if (!l) return '';
        size = +size || 32;
        return '<span class="to-ini to-ini-c' + iniColor(id) + '" style="width:' + size + 'px;height:' + size + 'px;font-size:' + Math.round(size * 0.4) + 'px;" aria-hidden="true">' + esc(l) + '</span>';
    }
    TO_CHAT.initials = { letters: iniLetters, color: iniColor, html: iniHtml };

    /* ── Mesaj șters cu urmă + „Redirecționat" (B2a) ───────────────────────────
       RO: Serverul (B1a: `delete_chat_message` / `delete_direct_message`) nu mai
           scoate rândul: îi golește textul, pozele și citatul și pune `deleted_at`.
           Urma e comună pentru club și DM, ca în WhatsApp:
             • „Ai șters acest mesaj" pe mesajele tale, „Mesaj șters" pe ale altora;
             • „Mesaj șters de un administrator" când l-a șters un admin de club.
           • 60 de ore = limita serverului („șterge pentru toți" din WhatsApp); aici
             doar ascunde opțiunea după termen, autorizarea rămâne pe server.
           • `canDeleteAny`: pragul din registru (`chat.deleteAny` = 3, ca
             `rbac_role_rank >= 3` din funcția serverului), dar FĂRĂ ocolirea
             proprietarului platformei din `can()`: serverul verifică doar rangul din
             club, deci butonul ar duce la un refuz (D3, Alex, 11 oct. 2026).
       EN: shared deleted-message trace for club and DM; the 60 h window and the
           admin threshold mirror the server (no platform-owner bypass). */
    var DEL_WINDOW_MS = 60 * 3600 * 1000;
    /* 8.13-3 (28 oct. 2026): `chat.deleteAny` stă acum în registrul `CAP_MIN_RANK`
       (supabase-integration.js), ca restul capabilităților — o singură sursă; nu mai e
       adăugată de aici la rulare. EN: the capability now lives in the main registry. */
    function deletedHtml(mine, byAdmin) {
        var txt = byAdmin ? tr('chat.msgDeletedAdmin', 'Deleted by an admin')
            : (mine ? tr('chat.msgDeletedMine', 'You deleted this message') : tr('chat.msgDeleted', 'This message was deleted'));
        return '<span class="chat-deleted"><i class="fas fa-ban" aria-hidden="true"></i> ' + esc(txt) + '</span>';
    }
    function fwdHtml() {
        return '<div class="chat-fwd"><i class="fas fa-share" aria-hidden="true"></i> ' + esc(tr('chat.forwarded', 'Forwarded')) + '</div>';
    }
    function withinDelete(ts) {
        var t0 = new Date(ts).getTime();
        return !isNaN(t0) && Date.now() - t0 <= DEL_WINDOW_MS;
    }
    function canDeleteAny() {
        try {
            var need = (typeof CAP_MIN_RANK === 'object' && CAP_MIN_RANK && CAP_MIN_RANK['chat.deleteAny']) || 3;
            return typeof roleRank === 'function' && typeof currentClubMyRole === 'function' && roleRank(currentClubMyRole()) >= need;
        } catch (e) { logErr('TO_CHAT.msg.canDeleteAny', e); return false; }
    }
    /* Funcția serverului lipsește ⇒ apelantul trece pe varianta fără ea (ex. căutarea doar
       locală, recentele de emoji doar în memorie). Ștergerile nu mai au o astfel de rezervă
       din 8.13-3 (ștergerea directă e refuzată de server din B3b). */
    function rpcMissing(err) {
        return !!err && (err.code === 'PGRST202' || /could not find the function/i.test(String(err.message || '')));
    }
    /* Refuzurile serverului, pe înțeles (RO/EN), nu codul brut. */
    function deleteErrText(err) {
        var m = String((err && err.message) || '');
        if (/delete_window_closed/.test(m)) return tr('chat.deleteExpired', 'Deleting is allowed only within the first 60 hours.');
        if (/not_allowed/.test(m)) return tr('rbac.noPermission', 'You do not have permission for this action.');
        return tr('m2.deleteErrorColon', 'Delete error: ') + m;
    }
    /* Rândul de club din memorie devine urmă, exact ce a lăsat serverul; citatele lui
       din răspunsuri se golesc tot ca pe server. EN: mirror the server-side wipe. */
    function markClubDeleted(row, byAdmin, at) {
        if (!row) return;
        row.deletedAt = at || new Date().toISOString();
        row.deletedByAdmin = !!byAdmin;
        row.text = ''; row.photos = []; row.edited = false;
        row.replyTo = null; row.replySender = null; row.replySnippet = null;
        try {
            (state.chatMessages || []).forEach(function (m) {
                if (m && m.replyTo != null && String(m.replyTo) === String(row.id)) m.replySnippet = null;
            });
        } catch (e) { logErr('TO_CHAT.msg.markClubDeleted', e); }
    }
    TO_CHAT.msg = { deletedHtml: deletedHtml, fwdHtml: fwdHtml, withinDelete: withinDelete, canDeleteAny: canDeleteAny,
        rpcMissing: rpcMissing, deleteErrText: deleteErrText, markClubDeleted: markClubDeleted };

    /* ── Avatarul deschide profilul, ca numele (8.1-b3) ───────────────────────
       RO: `_chatRowHtml` (app-modules-2.js) marchează avatarul cu
           `data-action="chat-profile"` + `data-uid` (același id ca numele).
           ⚠ Poza de profil are propriul `onclick` (mărire în lightbox, din
             `avatarHtml`) cu `stopPropagation` ⇒ o delegare obișnuită n-ar primi
             niciodată clicul. Ascultăm pe faza de CAPTURĂ (§65.6) și oprim noi
             evenimentul, deci în chat avatarul deschide profilul, nu poza.
           • Enter / Space pentru tastatură (avatarul are `role="button"`).
       EN: the avatar opens the profile like the name; capture phase because the
           photo avatar stops propagation for its own lightbox. */
    function bindClubProfiles(list) {
        if (!list || list.__toProfiles) return;
        list.__toProfiles = true;
        var hit = function (e) {
            var el = e.target && e.target.closest ? e.target.closest('[data-action="chat-profile"]') : null;
            return (el && list.contains(el)) ? el : null;
        };
        var open = function (el) {
            var uid = el.getAttribute('data-uid');
            if (uid && typeof window.openLeaderboardProfile === 'function') window.openLeaderboardProfile(uid);
        };
        list.addEventListener('click', function (e) {
            var el = hit(e); if (!el) return;
            e.stopPropagation();
            e.preventDefault();
            open(el);
        }, true);
        list.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            var el = hit(e); if (!el) return;
            e.preventDefault();
            open(el);
        });
    }

    /* ── Chatul de club: poza, citatul și numele din rând (8.13-1, 28 oct. 2026) ──
       RO: Aveau handlere inline (`onclick=`), cu id-urile în cod executabil. Acum
           `data-action` + `data-*` (escHtml) și o singură delegare pe listă.
           ⚠ Faza de BUBBLING, intenționat (nu captura, ca la avatar): „plasa" de după
           glisare (`quietUntil`) și bifarea din modul de selecție opresc clicul pe
           CAPTURĂ, ca să nu se deschidă poza / profilul. Un ascultător pus tot pe
           captură, pe aceeași listă, ar fi rulat oricum (`stopPropagation` nu oprește
           ascultătorii aceluiași nod). Aici le respectăm exact ca înainte.
           Fără `preventDefault` / `stopPropagation`: același efect ca handlerul inline.
       EN: delegated photo / quote / name clicks; bubbling on purpose so the
           post-swipe guard and selection mode still block them. */
    function bindClubRowActions(list) {
        if (!list || list.__toRowAct) return;
        list.__toRowAct = true;
        list.addEventListener('click', function (e) {
            var el = e.target && e.target.closest ? e.target.closest('[data-action="chat-photo"], [data-action="chat-quote"], [data-action="chat-name"]') : null;
            if (!el || !list.contains(el)) return;
            var act = el.getAttribute('data-action');
            if (act === 'chat-photo') {
                if (typeof window.openLightbox === 'function')
                    window.openLightbox(el.getAttribute('data-gal'), { type: 'chat_msg', id: el.getAttribute('data-mid'), clubId: el.getAttribute('data-cid') }, el);
            } else if (act === 'chat-quote') {
                if (typeof window.scrollToMessage === 'function') window.scrollToMessage(el.getAttribute('data-mid'));
            } else if (typeof window.openLeaderboardProfile === 'function') {
                window.openLeaderboardProfile(el.getAttribute('data-uid'));
            }
        });
    }

    /* ── Chatul de club: primul necitit la intrare (8.1-c2a) ───────────────────
       RO: Ultima citire proprie (`state.chatReads[me]`) e actualizată de
           `markChatRead` (app-modules-2.js) CHIAR la intrarea în chat, înainte de
           orice derulare. O citim exact înainte, printr-un înveliș ADITIV (§40) —
           funcția originală rulează neschimbată. Captăm doar la INTRARE (prima
           chemare cu mesaje după ce chatul a devenit activ), nu la fiecare mesaj
           primit cât stai în chat.
           • Poziționarea o face `landOnTab` (nav.js) prin `TO_CHAT.club.land()`,
             în locul derulării la capăt; dacă `landOnTab` a rulat deja (pornire
             direct în chat, din notificare), o plasă la 100 ms o face oricum.
           • Fără citire anterioară (prima intrare în club) nu se schimbă nimic.
           • Bara dispare la ieșirea din chat.
       EN: snapshot the user's last read right before markChatRead updates it. */
    var clubWasActive = false;
    var clubUnread = null;     /* { id, n } */
    var clubLandPending = false;
    function captureClubUnread() {
        var tab = document.getElementById('tab-chat');
        if (!tab || !tab.classList.contains('active') || clubWasActive) return;
        /* ⚠ `state` e `let` global (app-core.js), NU proprietate pe `window`
           (`window.state` e undefined — aceeași capcană notată în nav.js). */
        var st = (typeof state !== 'undefined') ? state : null;
        var msgs = (st && st.chatMessages) || [];
        if (!msgs.length) return;                 /* încă fără date: încercăm la următoarea chemare */
        clubWasActive = true;
        var me = st.currentUser && st.currentUser.id;
        var reads = st.chatReads || {};
        var last = (me && reads[me]) ? new Date(reads[me]).getTime() : 0;
        if (!me || !last) return;
        var isNew = function (m) {
            var t = new Date(m.timestamp).getTime();
            return t > last && m.senderId !== me && m.sender !== 'Sistem';
        };
        var all = msgs.filter(isNew);
        if (!all.length) return;
        /* 8.1-c3a: peste 50 de necitite ⇒ fereastra crește până la primul necitit din
           memorie (înainte: primul dintre ultimele 50 afișate) și se redesenează (doar
           rândurile adăugate sus). Bara arată numărul total. */
        var settle = function (total) {
            var m2 = st.chatMessages || [];
            var all2 = m2.filter(isNew);
            if (!all2.length) return;
            cpSync();
            var need = m2.length - m2.indexOf(all2[0]);
            var grow = need > cp.win;
            if (grow) cp.win = Math.min(need, m2.length);
            var shown = m2.slice(-cp.win).filter(isNew);
            clubUnread = { id: String((shown[0] || all2[all2.length - 1]).id), n: Math.max(total || 0, all2.length) };
            clubLandPending = true;
            if (grow && typeof loadChat === 'function') { try { loadChat(); } catch (e) { logErr('TO_CHAT.unread', e); } }
            setTimeout(function () { if (clubLandPending) landClub(); }, 100);
        };
        /* 8.1-c3: în memorie stau ultimele 200. Dacă TOATE sunt mai noi decât ultima ta
           citire, necititele pot începe mai devreme (defect văzut de Alex: 230 necitite,
           bara arăta 200). Le aducem întâi pe cele dintre ultima citire și cel mai vechi
           mesaj din memorie (cel mult 500, cele mai noi), apoi deschidem la primul
           necitit. Peste 500, numărul exact vine dintr-o numărătoare pe server (fără
           rânduri). Fără server / în demo / club mic (sub 200 = tot istoricul): ca înainte.
           EN: unread may start before the loaded window — fetch the gap, then land. */
        var first = msgs[0];
        var club = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        var gap = msgs.length >= 200 && new Date(first.timestamp).getTime() > last && club
            && typeof sb !== 'undefined' && sb && !(typeof isDemoMode === 'function' && isDemoMode());
        if (!gap) { settle(0); return; }
        var lastIso = new Date(last).toISOString(), GAP_MAX = 500;
        sb.from('messages').select('*').eq('club_id', club).gt('timestamp', lastIso).lt('timestamp', first.timestamp)
            .order('timestamp', { ascending: false }).limit(GAP_MAX)
            .then(function (r) {
                if (r.error) throw r.error;
                if (club !== currentClubId || (st.chatMessages || [])[0] !== first || !tab.classList.contains('active')) return null;
                var have = {};
                st.chatMessages.forEach(function (m) { have[String(m.id)] = true; });
                var rows = (r.data || []).filter(function (x) { return !have[String(x.id)]; }).map(clubRow).reverse();
                if (rows.length) st.chatMessages = rows.concat(st.chatMessages);
                if ((r.data || []).length < GAP_MAX) return { total: 0 };
                return sb.from('messages').select('id', { count: 'exact', head: true }).eq('club_id', club)
                    .gt('timestamp', lastIso).neq('sender_id', me).neq('sender_name', 'Sistem')
                    .then(function (c) { return { total: (c && !c.error && c.count) || 0 }; });
            })
            .then(function (res) { if (res) settle(res.total); }, function (e) { logErr('TO_CHAT.unreadGap', e); settle(0); });
    }
    function landClub() {
        if (!clubLandPending || !clubUnread) return false;
        clubLandPending = false;
        var list = document.getElementById('chatMessages');
        if (!list) return false;
        /* Captarea a venit DUPĂ desenarea listei ⇒ bara se pune acum în pagină;
           desenările următoare o includ singure (`unreadMark`). */
        if (!list.querySelector('.chat-unread')) {
            var msg = document.getElementById('msg-' + clubUnread.id);
            var row = msg && msg.closest ? msg.closest('.chat-row') : null;
            if (!row) return false;
            row.insertAdjacentHTML('beforebegin', unreadHtml(clubUnread.n));
        }
        var go = function () { scrollToUnread(list); };
        go();
        setTimeout(go, 80);
        return true;
    }
    function bindClubUnread() {
        var tab = document.getElementById('tab-chat');
        if (!tab || tab.__toUnread) return;
        tab.__toUnread = true;
        /* 8.1-c2b: bara a fost „citită" (ai derulat prin necitite până la capăt) ⇒ nu
           o mai punem la desenările următoare. */
        var cm = document.getElementById('chatMessages');
        if (cm) cm.__toUnreadSeen = function () { clubUnread = null; };
        var orig = window.markChatRead;
        if (typeof orig === 'function' && !orig.__toWrapped) {
            var wrapped = function () {
                try { captureClubUnread(); } catch (e) { logErr('TO_CHAT.unread', e); }
                return orig.apply(this, arguments);
            };
            wrapped.__toWrapped = true;
            window.markChatRead = wrapped;
        }
        if (typeof MutationObserver === 'function') {
            new MutationObserver(function () {
                if (tab.classList.contains('active')) return;
                if (sel.kind === 'club') selEnd();            /* 8.1-g: ieșirea din chat oprește selecția */
                clubWasActive = false;
                clubLandPending = false;
                cp.win = CP_PAGE;   /* 8.1-c3a: la ieșire, fereastra revine la ultimele 50 */
                if (clubUnread) {
                    clubUnread = null;
                    var bar = document.querySelector('#chatMessages .chat-unread');
                    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
                }
            }).observe(tab, { attributes: true, attributeFilter: ['class'] });
        }
    }
    TO_CHAT.club.land = landClub;
    TO_CHAT.club.unreadMark = function () { return clubUnread; };

    /* ── Bifele de „văzut" în chatul de club (8.1-c2c) ─────────────────────────
       RO: Trei stări, ca în WhatsApp: ✓ trimis · ✓✓ văzut de cel puțin unul (ca
           până acum) · ✓✓ în altă culoare când l-au văzut TOȚI membrii cu cont ai
           clubului (în afară de expeditor). Membrii fără cont (jucători adăugați
           de admin) nu pot citi, deci nu intră în „toți"; nici cei ascunși.
           Citirile vin din `state.chatReads` (id → ultima citire), ca înainte.
       EN: three tick states; "seen by all" = every account member except sender. */
    function clubSeenLevel(reads, senderId, ts) {
        reads = reads || {};
        var t = new Date(ts).getTime();
        var sid = String(senderId || '');
        var seenAt = function (id) { return reads[id] && new Date(reads[id]).getTime() >= t; };
        var any = false;
        for (var k in reads) { if (k !== sid && seenAt(k)) { any = true; break; } }
        if (!any) return 0;
        var st = (typeof state !== 'undefined') ? state : null;
        var m = (st && st.members) || {};
        var n = 0;
        for (var key in m) {
            var x = m[key];
            if (!x || !x.dbId || x.hidden || String(x.dbId) === sid) continue;
            n++;
            if (!seenAt(String(x.dbId))) return 1;
        }
        return n > 0 ? 2 : 1;
    }
    TO_CHAT.club.seenLevel = clubSeenLevel;

    /* ========================================================================
       TRIMITEREA CU STARE: „se trimite" / „nu s-a trimis, reîncearcă" (8.1-s)
       ------------------------------------------------------------------------
       RO: Înainte: în club mesajul nu apărea deloc până la confirmare (fără
           internet: câmpul se golea și nimic), iar în DM apărea cu ✓ „trimis" deși
           nu plecase, și dispărea cu tot cu text la eroare. Acum, ca în WhatsApp:
           • mesajul apare IMEDIAT, cu 🕓; la confirmare devine ✓ (doar rândul lui se
             actualizează, nimic nu clipește);
           • eșec ⇒ „Nu s-a trimis · Atinge pentru a reîncerca", cu textul și pozele
             păstrate; apăsare lungă ⇒ Reîncearcă / Copiază / Renunță;
           • eșec de REȚEA ⇒ retrimitere singură la revenirea internetului
             (evenimentul `online`), în ordinea trimiterii — cu reîncercări repetate
             (fix2, vezi `scheduleAuto`);
           • fără dubluri: fiecare mesaj are un cod unic (`client_id`, V9_C4). Dacă o
             încercare a ajuns pe server dar răspunsul s-a pierdut, serverul refuză a
             doua (23505), iar aplicația îl ia pe primul. Același cod leagă și
             mesajul venit prin timp real de rândul provizoriu. Fără coloană pe
             server: se trimite fără cod, ca înainte;
           • ordinea: o coadă pe conversație ⇒ mesajele pleacă unul după altul;
           • o cerere blocată (rețea slabă) trece pe „nu s-a trimis" după 20 s, ca să
             poți reîncerca (codul unic oprește dublura dacă prima ajunge totuși).
           Demo: neatins (zero trafic, ca înainte).
       EN: optimistic send with pending / failed states, retry and auto-retry on
           reconnect, idempotent through a client-generated id. */
    var SEND_TIMEOUT = 20000, UPLOAD_TIMEOUT = 60000;
    var sendQ = {};
    function newCid() {
        try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) { /* noop */ }
        return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, function () { return (Math.random() * 16 | 0).toString(16); });
    }
    function netErr(m) { var e = new Error(m || 'network'); e.isNet = true; return e; }
    function isNet(e) {
        if (!e) return false;
        if (e.isNet || (typeof navigator !== 'undefined' && navigator.onLine === false)) return true;
        return /failed to fetch|networkerror|load failed|network request failed|timeout|fetch/i.test(String(e.message || e));
    }
    function withTimeout(p, ms) {
        var t;
        return Promise.race([p, new Promise(function (res, rej) { t = setTimeout(function () { rej(netErr('timeout')); }, ms); })])
            .then(function (v) { clearTimeout(t); return v; }, function (e) { clearTimeout(t); throw e; });
    }
    function revokeLocal(row) {
        (row && row._local || []).forEach(function (u) { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
        if (row && row._lpLocal) { try { URL.revokeObjectURL(row._lpLocal); } catch (e) { /* noop */ } }   /* 8.6 */
    }
    function missingCol(err, col) { return !!err && new RegExp(col).test(String(err.message || '')) && /column|schema cache|does not exist/i.test(String(err.message || '')); }
    /* 8.5-B — miniatura pozei (chat-thumb.js, la cerere), în același dosar:
       `…/0-abc.jpg` → `…/0-abc.thumb.jpg`. Niciodată nu oprește trimiterea: orice eșec
       (fișier, decodare, rețea) ⇒ fără miniatură, se va vedea originalul. Serverul o
       pune la șters odată cu poza (V9_85B_MINIATURI.sql). EN: best-effort thumbnail. */
    /* ⚠ Fiecare pas are limită de timp: un fișier care nu se mai încarcă (rețea blocată) ar
       fi ținut mesajul „în trimitere" la nesfârșit (prins de regresie, 18 oct.). După o
       încărcare eșuată a fișierului, un minut fără miniaturi (fără așteptări repetate). */
    var THUMB_TIMEOUT = 20000, THUMB_LOAD_MS = 4000, THUMB_MAKE_MS = 6000, thumbOffUntil = 0;
    /* 8.5-C: și pozele din sesiuni / anunțuri (bucket session-photos), cu latura mică de
       320 px — se afișează la 72–96 px (istoric, anunțuri, sesiunea live), iar la 320 px
       ajung și pe un ecran dens; la apăsare, galeria arată originalul (8.5-B2).
       Serverul o pune la șters odată cu poza (V9_85C_MINIATURI_SESIUNI.sql). */
    var THUMB_SIDE = { 'chat-photos': 0, 'session-photos': 320 };
    async function upThumb(f, cale, bucket) {
        bucket = bucket || 'chat-photos';
        if (!Object.prototype.hasOwnProperty.call(THUMB_SIDE, bucket) || Date.now() < thumbOffUntil) return;
        var M;
        try { M = await withTimeout(ensureModule('chat-thumb.js', 'TO_THUMB'), THUMB_LOAD_MS); }   /* încărcat deja ⇒ imediat */
        catch (e) { thumbOffUntil = Date.now() + 60000; return; }
        try {
            var b = await withTimeout(M.make(f, THUMB_SIDE[bucket]), THUMB_MAKE_MS);
            if (!b) return;
            await withTimeout(sb.storage.from(bucket).upload(M.pathOf(cale), b, { contentType: 'image/jpeg', upsert: false }), THUMB_TIMEOUT);
        } catch (e) { /* fără miniatură: se vede originalul */ }
    }
    /* 8.5-C: pentru urcările din afara chatului (uploadSessionPhotos / uploadAnnouncementPhotos,
       supabase-integration.js). Nu aruncă niciodată; se termină în cel mult ~30 s. */
    TO_CHAT.thumbUp = function (bucket, f, cale) { return upThumb(f, cale, bucket); };

    /* Adaptoarele: ce diferă între club și DM. */
    var AD = {
        club: {
            table: 'messages',
            list: function () { return (typeof state !== 'undefined' && state && state.chatMessages) || []; },
            cidOf: function (r) { return r && r.clientId; },
            lastTs: function () { var l = AD.club.list(); var m = l[l.length - 1]; return m ? new Date(m.timestamp).getTime() : 0; },
            makeRow: function (job) {
                var me = state.currentUser;
                return { id: 'tmp_' + job.cid, clientId: job.cid, pending: true, failed: false,
                    sender: me.name, senderEmail: me.email, senderId: me.id, isAdmin: !!me.isAdmin,
                    text: job.text, timestamp: new Date(Math.max(Date.now(), AD.club.lastTs() + 1)).toISOString(),
                    photos: [], _local: job.files.map(function (f) { try { return URL.createObjectURL(f); } catch (e) { return ''; } }).filter(Boolean),
                    edited: false, replyTo: job.reply ? job.reply.id : null, replySender: job.reply ? job.reply.sender : null, replySnippet: job.reply ? job.reply.snippet : null,
                    mentions: job.mentions || null, liveRef: job.liveRef || null,   /* 8.16-4b */
                    linkPreview: lpWire(job.lp), _lpLocal: (job.lp && job.lp.local) || null };   /* 8.6 */
            },
            path: function (job, i, ext) { return 'club/' + job.ctx + '/' + job.cid + '/' + i + '-' + Math.random().toString(36).slice(2) + '.' + ext; },
            payload: function (job, paths) {
                var me = state.currentUser;
                var p = { club_id: job.ctx, sender_name: me.name, sender_email: me.email, sender_id: me.id, text: job.text, photos: paths,
                    reply_to: job.reply ? job.reply.id : null, reply_sender: job.reply ? job.reply.sender : null, reply_snippet: job.reply ? job.reply.snippet : null };
                if (job.mentions && job.mentions.length && !job.noMent) p.mentions = job.mentions;   /* 8.1-f */
                if (job.liveRef && !job.noLive) p.live_ref = job.liveRef;   /* 8.16-4b: {k, id} — verificat de server (guard_message_live_ref) */
                return p;
            },
            map: function (data) { var r = clubRow(data); if (state.currentUser && data.sender_id === state.currentUser.id) r.isAdmin = !!state.currentUser.isAdmin; return r; },
            sameCtx: function (job) { return typeof currentClubId !== 'undefined' && currentClubId === job.ctx; },
            render: function () { if (typeof loadChat === 'function') loadChat(); },
            /* Rândul provizoriu primește id-ul real ȘI în pagină ⇒ loadChat îl vede ca
               „același rând, schimbat" și îl actualizează doar pe el (fără pâlpâire). */
            rekey: function (oldId, newId) {
                var c = document.getElementById('chatMessages');
                var el = document.getElementById('msg-' + oldId);
                if (el) { el.id = 'msg-' + newId; el.setAttribute('data-mid', String(newId)); }
                if (c && c.dataset.chatIds) c.dataset.chatIds = c.dataset.chatIds.split(',').map(function (x) { return x === String(oldId) ? String(newId) : x; }).join(',');
                if (c && c.dataset.chatLastId === String(oldId)) c.dataset.chatLastId = String(newId);
            }
        },
        dm: {
            table: 'direct_messages',
            list: function () { return window._dmAll || (window._dmAll = []); },
            cidOf: function (r) { return r && r.client_id; },
            makeRow: function (job) {
                var me = state.currentUser.id, l = AD.dm.list(), last = 0;
                l.forEach(function (m) { var t = new Date(m.created_at).getTime(); if (t > last) last = t; });
                return { id: 'tmp_' + job.cid, client_id: job.cid, pending: true, failed: false, sender_id: me, recipient_id: job.ctx, body: job.text,
                    photos: [], _local: job.files.map(function (f) { try { return URL.createObjectURL(f); } catch (e) { return ''; } }).filter(Boolean),
                    created_at: new Date(Math.max(Date.now(), last + 1)).toISOString(), read_at: null, forwarded: !!job.fwd,   /* 8.4-c */
                    reply_to: job.reply ? job.reply.id : null, reply_sender: job.reply ? job.reply.sender : null, reply_snippet: job.reply ? job.reply.snippet : null,
                    reply_photo: (job.reply && job.reply.photo) || null,   /* RP: poza citată din chatul clubului */
                    link_preview: lpWire(job.lp), _lpLocal: (job.lp && job.lp.local) || null };   /* 8.6 */
            },
            path: function (job, i, ext) {
                var me = state.currentUser.id;
                var pair = (typeof _dmPairKey === 'function') ? _dmPairKey(me, job.ctx) : [me, job.ctx].sort().join('_');
                return 'dm/' + pair + '/' + job.cid + '/' + i + '-' + Math.random().toString(36).slice(2) + '.' + ext;
            },
            payload: function (job, paths) {
                var p = { sender_id: state.currentUser.id, recipient_id: job.ctx, body: job.text, photos: paths };
                if (job.reply) { p.reply_to = job.reply.id; p.reply_sender = job.reply.sender; p.reply_snippet = job.reply.snippet; }
                if (job.reply && job.reply.photo && !job.noRp) p.reply_photo = job.reply.photo;   /* RP */
                if (job.fwd && !job.noFwd) p.forwarded = true;   /* 8.4-c: redirecționare (chat-forward.js) */
                return p;
            },
            map: function (data) { return data; },
            sameCtx: function () { return true; },
            render: function () { if (typeof window.renderDMThread === 'function') window.renderDMThread(); try { if (typeof window.refreshDmUi === 'function') window.refreshDmUi(); } catch (e) { /* noop */ } },
            /* 28 oct. 2026: ca la club — rândul provizoriu primește id-ul real și în pagină, ca
               `renderDMThread` să-l vadă „același rând, schimbat" și să-l actualizeze DOAR pe el.
               Înainte nu făcea nimic ⇒ la fiecare confirmare lista părea alta ⇒ desenare completă
               (pozele se reîncărcau: „clipea toată conversația"). EN: keep the row, swap its id. */
            rekey: function (oldId, newId) {
                var b = document.getElementById('dmThread');
                var el = document.getElementById('dmmsg-' + oldId);
                if (el) { el.id = 'dmmsg-' + newId; el.setAttribute('data-mid', String(newId)); }
                if (b && b.dataset.dmIds) b.dataset.dmIds = b.dataset.dmIds.split(',').map(function (x) { return x === String(oldId) ? String(newId) : x; }).join(',');
                if (b && b.dataset.dmLast === String(oldId)) b.dataset.dmLast = String(newId);
            }
        }
    };
    function sendNotice(e, kind) {
        try {
            if (typeof window.showNotification !== 'function') return;
            /* v9 H23 (D3): în DM, refuzul de drept al serverului (42501: blocare sau setarea
               „cine îmi poate scrie") arată textul neutru al barei de blocare, nu eroarea
               tehnică — nu se poate deduce care dintre cele două a fost.
               EN: DM permission refusals show the neutral block text, not the raw error. */
            if (kind === 'dm' && e && e.code === '42501') { window.showNotification(tr('block.dmBar', 'You cannot send messages to this user.'), 'error'); return; }
            window.showNotification(tr('chat.notSentToast', 'Message not sent:') + ' ' + String((e && e.message) || ''), 'error');
        } catch (x) { /* noop */ }
    }
    function done(kind, row, data) {
        var ad = AD[kind], list = ad.list();
        var real = ad.map(data);
        var i = list.indexOf(row);
        if (i !== -1) {
            if (list.some(function (m) { return m !== row && String(m.id) === String(real.id); })) list.splice(i, 1);
            else { ad.rekey(row.id, real.id); list[i] = real; }
        }
        row.pending = false; row.failed = false; row._done = true;
        revokeLocal(row);
        ad.render();
    }
    function fail(kind, row, e) {
        if (row._done) return;
        row.pending = false; row.failed = true; row.errNet = isNet(e);
        if (!row.errNet) sendNotice(e, kind);
        AD[kind].render();
        if (row.errNet) scheduleAuto(true);
    }
    async function attempt(kind, row) {
        var ad = AD[kind], job = row._job, force = row._force, silent = row._silent;
        row._force = false; row._silent = false;
        if (row._done || ad.list().indexOf(row) === -1) return;   /* renunțat între timp */
        /* fix2: o încercare AUTOMATĂ nu schimbă bula (rămâne „Nu s-a trimis") până nu
           reușește — altfel ar clipi la fiecare încercare. / Silent auto attempts. */
        if (!silent) { row.pending = true; row.failed = false; ad.render(); }
        try {
            /* Telefonul știe că e fără internet ⇒ „nu s-a trimis" imediat (se retrimite
               singur la revenire), nu după 20 s: offline, biblioteca Supabase încearcă întâi
               să reîmprospăteze sesiunea, iar mesajul stătea „în trimitere" (Alex, mod avion).
               „Reîncearcă" apăsat de om încearcă oricum: dacă telefonul raportează greșit
               „offline", mesajul tot poate pleca. / A manual retry always tries the network. */
            if (!force && typeof navigator !== 'undefined' && navigator.onLine === false) throw netErr('offline');
            if (job.files.length && !job.paths) {
                var paths = [];
                for (var i = 0; i < job.files.length; i++) {
                    var f = job.files[i];
                    var ext = (f.type === 'image/png') ? 'png' : (f.type === 'image/webp' ? 'webp' : 'jpg');
                    var cale = ad.path(job, i, ext);
                    var up = await withTimeout(sb.storage.from('chat-photos').upload(cale, f, { contentType: f.type || 'image/jpeg', upsert: false }), UPLOAD_TIMEOUT);
                    if (up.error) throw up.error;
                    paths.push(cale);
                    await upThumb(f, cale);              /* 8.5-B: miniatura, înainte de mesaj (cei care îl primesc o au imediat) */
                }
                job.paths = paths;
            }
            /* 8.6: imaginea cardului, în dosarul conversației, ÎNAINTEA mesajului (serverul cere
               să existe și să fie a expeditorului). Fără rețea ⇒ se reîncearcă tot mesajul;
               refuzată ⇒ cardul pleacă fără imagine (mesajul nu se blochează din cauza ei). */
            if (job.lp && job.lp.blob && !job.lp.i && !job.noLp) {
                try {
                    var lpc = ad.path(job, 'lp', 'jpg');
                    var lpu = await withTimeout(sb.storage.from('chat-photos').upload(lpc, job.lp.blob, { contentType: 'image/jpeg', upsert: false }), UPLOAD_TIMEOUT);
                    if (lpu.error) throw lpu.error;
                    job.lp.i = lpc;
                } catch (e) {
                    if (isNet(e)) throw e;
                    job.lp.blob = null;
                }
            }
            var payload = ad.payload(job, job.paths || []);
            if (!job.noCid) payload.client_id = job.cid;
            if (job.lp && !job.noLp) payload.link_preview = lpWire(job.lp);   /* 8.6 */
            var res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            if (res.error && !job.noCid && missingCol(res.error, 'client_id')) {
                job.noCid = true; delete payload.client_id;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            /* 8.6: serverul fără coloană (SQL nerulat / rollback) sau cardul refuzat de server
               ⇒ mesajul pleacă fără card, ca înainte. EN: never block a message on its card. */
            if (res.error && payload.link_preview && (missingCol(res.error, 'link_preview') || /bad_link_preview|photo_not_owned/.test(String(res.error.message || '')))) {
                job.noLp = true; delete payload.link_preview;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            /* 8.16-4b: serverul fără coloana `live_ref` (SQL nerulat / rollback) sau trimiterea refuzată
               (`bad_live_ref`) ⇒ mesajul pleacă fără card, cu textul de rezervă („📊 Sondaj: …").
               EN: never block a message on its live attachment. */
            if (res.error && payload.live_ref && (missingCol(res.error, 'live_ref') || /bad_live_ref/.test(String(res.error.message || '')))) {
                job.noLive = true; delete payload.live_ref;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            /* 8.1-f: serverul fără coloana `mentions` (SQL nerulat / rollback) ⇒ mesajul
               pleacă fără mențiuni, ca înainte. EN: no mentions column ⇒ send without. */
            if (res.error && payload.mentions && missingCol(res.error, 'mentions')) {
                job.noMent = true; delete payload.mentions;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            /* 8.4-c: fără coloana `forwarded` (B1a retrasă) ⇒ pleacă fără marcaj, ca înainte. */
            if (res.error && payload.forwarded && missingCol(res.error, 'forwarded')) {
                job.noFwd = true; delete payload.forwarded;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            /* RP: serverul fără coloana `reply_photo` (SQL nerulat / rollback) ⇒ pleacă fără miniatură,
               cu tot restul citatului. Înaintea verificării de mai jos, care ar arunca TOT citatul.
               EN: no reply_photo column ⇒ drop only the thumbnail, keep the quote. */
            if (res.error && kind === 'dm' && payload.reply_photo && missingCol(res.error, 'reply_photo')) {
                job.noRp = true; delete payload.reply_photo;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            if (res.error && kind === 'dm' && job.reply && missingCol(res.error, 'reply_')) {
                delete payload.reply_to; delete payload.reply_sender; delete payload.reply_snippet;
                res = await withTimeout(sb.from(ad.table).insert(payload).select().maybeSingle(), SEND_TIMEOUT);
            }
            /* A ajuns deja (răspunsul anterior s-a pierdut) ⇒ îl luăm pe acela. */
            if (res.error && res.error.code === '23505' && !job.noCid) {
                res = await withTimeout(sb.from(ad.table).select('*').eq('sender_id', state.currentUser.id).eq('client_id', job.cid).maybeSingle(), SEND_TIMEOUT);
            }
            if (res.error) throw res.error;
            if (!res.data) throw new Error('no_data');
            if (!row._done) done(kind, row, res.data);
        } catch (e) {
            /* Automată + tot fără rețea ⇒ nimic de arătat; următoarea e deja programată. */
            if (!(silent && isNet(e))) { logErr('TO_CHAT.send', e); fail(kind, row, e); }
        }
    }
    function enqueueRow(kind, row) {
        var key = kind + ':' + row._job.ctx;
        /* fix2: `_q` = încercări în coadă / în curs pentru rândul ăsta (Renunță și meniul
           le respectă). Al doilea argument din `then` ține coada vie chiar dacă o
           încercare ar arunca neprevăzut. / Per-row in-flight counter. */
        row._q = (row._q || 0) + 1;
        sendQ[key] = (sendQ[key] || Promise.resolve()).then(function () { return attempt(kind, row); })
            .then(function () { row._q--; }, function (e) { row._q--; logErr('TO_CHAT.send', e); });
        return sendQ[key];
    }
    function send(kind, job) {
        try {
            if (!state || !state.currentUser || typeof sb === 'undefined' || !sb) return null;
            job.cid = newCid();
            job.files = job.files || [];
            job.lp = lpTake(kind, job);                                /* 8.6: cardul linkului */
            var row = AD[kind].makeRow(job);
            row._job = job;
            AD[kind].list().push(row);
            AD[kind].render();
            return enqueueRow(kind, row);
        } catch (e) { logErr('TO_CHAT.send', e); return null; }
    }
    function findRow(kind, id) {
        var l = AD[kind].list();
        for (var i = 0; i < l.length; i++) if (String(l[i].id) === String(id) && l[i]._job) return l[i];
        return null;
    }
    function retry(kind, id, auto) {
        var row = findRow(kind, id);
        if (!row || !row.failed) return null;
        if (auto) {
            if (row._q) return null;                 /* o încercare e deja pe drum */
            row._silent = true;
            return enqueueRow(kind, row);
        }
        row.failed = false; row.pending = true; row._force = true;
        AD[kind].render();
        return enqueueRow(kind, row);
    }
    function discard(kind, id) {
        var row = findRow(kind, id);
        if (!row || row.pending || row._q) return;   /* pe drum ⇒ ar putea ajunge totuși */
        var l = AD[kind].list(), i = l.indexOf(row);
        if (i !== -1) l.splice(i, 1);
        revokeLocal(row);
        /* poze deja încărcate, fără mesaj ⇒ la curățat (aceeași încercare ca înainte) */
        var p = row._job && row._job.paths;
        if (p && p.length) {
            try { sb.from('storage_cleanup_queue').insert(p.map(function (x) { return { bucket_id: 'chat-photos', object_path: x }; })).then(function () {}, function () {}); } catch (e) { /* noop */ }
        }
        AD[kind].render();
    }
    /* Mesajul real a venit prin timp real / recuperare ÎNAINTE de răspunsul la trimitere
       ⇒ ia locul rândului provizoriu cu același cod (fără dublură). */
    function adopt(kind, data) {
        try {
            var cid = data && data.client_id;
            if (!cid || !state || !state.currentUser || data.sender_id !== state.currentUser.id) return false;
            var l = AD[kind].list();
            for (var i = 0; i < l.length; i++) {
                if (l[i]._job && AD[kind].cidOf(l[i]) === cid && !l[i]._done) { done(kind, l[i], data); return true; }
            }
            return false;
        } catch (e) { logErr('TO_CHAT.adopt', e); return false; }
    }
    /* fix2 — RETRIMITEREA SINGURĂ, CU REÎNCERCĂRI
       RO: Testat pe Samsung (13 oct. 2026): după modul avion mesajul rămânea „Nu s-a
           trimis" până la „Reîncearcă". Evenimentul `online` vine înainte ca rețeaua să
           fie utilizabilă (aceeași lecție ca la pornirea fără internet, app-core.js),
           iar singura încercare de atunci pica. Acum: prima încercare la 1,5 s după
           `online` / revenirea în aplicație / un eșec de rețea, apoi la 4, 10, 20, 30 s
           și la fiecare minut, cât timp există mesaje netrimise din cauza rețelei.
           Nimic cât telefonul spune „fără internet" (`online` repornește) sau cât
           aplicația e în fundal (revenirea repornește). Refuzul serverului NU se
           retrimite singur. După schimbarea contului listele sunt goale ⇒ nimic de
           retrimis (și `attempt` sare peste un rând scos din listă).
       EN: auto-resend with back-off after `online`, return to the app or a network
           failure; paused while offline or hidden; server refusals are never retried. */
    var AUTO_DELAYS = [1500, 4000, 10000, 20000, 30000, 60000];
    var autoTimer = null, autoStep = 0;
    function netFailedRows() {
        var out = [];
        if (typeof state === 'undefined' || !state || !state.currentUser) return out;
        ['club', 'dm'].forEach(function (kind) {
            AD[kind].list().forEach(function (r) { if (r && r._job && r.failed && r.errNet && !r._done) out.push({ kind: kind, row: r }); });
        });
        return out;
    }
    function scheduleAuto(reset) {
        if (reset) autoStep = 0;
        if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; }
        if (!netFailedRows().length) { autoStep = 0; return; }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        if (document.hidden) return;
        var d = AUTO_DELAYS[Math.min(autoStep, AUTO_DELAYS.length - 1)];
        autoStep++;
        autoTimer = setTimeout(function () { autoTimer = null; autoRetry(); }, d);
    }
    function autoRetry() {
        var jobs = netFailedRows().map(function (p) { return retry(p.kind, p.row.id, true); }).filter(Boolean);
        if (!jobs.length) { scheduleAuto(false); return; }
        Promise.all(jobs).then(function () { scheduleAuto(false); }, function () { scheduleAuto(false); });
    }
    try {
        window.addEventListener('online', function () { scheduleAuto(true); });
        document.addEventListener('visibilitychange', function () { if (!document.hidden) scheduleAuto(true); });
        window.addEventListener('pageshow', function () { scheduleAuto(true); });
    } catch (e) { logErr('TO_CHAT.send', e); }

    /* Starea din colțul bulei proprii: 🕓 cât se trimite; nimic când a eșuat (vezi
       nota de sub bulă). */
    function statusHtml(row) {
        if (row && row.pending) return '<span class="chat-seen pending" title="' + esc(tr('chat.sending', 'Sending…')) + '"><i class="fas fa-clock" aria-hidden="true"></i></span>';
        return '';
    }
    function failHtml(row, action) {
        if (!row || !row.failed) return '';
        return '<button type="button" class="chat-retry" data-action="' + action + '" data-mid="' + esc(String(row.id)) + '">'
            + '<i class="fas fa-circle-exclamation" aria-hidden="true"></i> ' + esc(tr('chat.notSent', 'Not sent · Tap to retry')) + '</button>';
    }
    /* Meniul unui mesaj netrimis: Reîncearcă / Copiază / Renunță. Cât se trimite: doar
       Copiază (cererea e pe drum; renunțarea ar putea lăsa totuși mesajul pe server). */
    function failedMenu(kind, row) {
        if (!TO_CHAT.menu) return;
        var id = String(row.id), txt = kind === 'club' ? row.text : row.body;
        var copy = { icon: 'fa-copy', label: tr('chat.copy', 'Copy'), run: function () { try { navigator.clipboard.writeText(String(txt || '')); } catch (e) { /* noop */ } } };
        var items = (row.pending || row._q) ? [copy] : [
            { icon: 'fa-rotate-right', label: tr('chat.retry', 'Retry'), run: function () { retry(kind, id); } },
            copy,
            { icon: 'fa-trash', label: tr('chat.discard', 'Discard'), danger: true, run: function () { discard(kind, id); } }
        ];
        TO_CHAT.menu.open({ id: kind === 'club' ? 'msgMenuModal' : 'dmMsgMenuModal', key: id, preview: txt, items: items });
    }
    /* Apăsarea pe „Nu s-a trimis" (delegare; containerul chatului de club e static). */
    function bindClubRetry(list) {
        if (!list || list.__toRetry) return;
        list.__toRetry = true;
        list.addEventListener('click', function (e) {
            var b = e.target && e.target.closest ? e.target.closest('[data-action="chat-retry"]') : null;
            if (!b || !list.contains(b)) return;
            e.preventDefault(); e.stopPropagation();
            retry('club', b.getAttribute('data-mid'));
        });
    }
    TO_CHAT.send = {
        club: function (job) {
            job.ctx = currentClubId;
            try { job.mentions = mentionTake(job.text); } catch (e) { job.mentions = null; logErr('TO_CHAT.mention', e); }
            return send('club', job);
        },
        dm: function (job) { job.ctx = job.other; return send('dm', job); },
        retry: retry, discard: discard, adopt: adopt, statusHtml: statusHtml, failHtml: failHtml,
        failedMenu: failedMenu, autoRetry: autoRetry
    };

    /* 8.1-d: ecranul complet și căutarea clubului sunt straturi cât se văd (cu chatul pe
       ecran); ieșirea din tab le scoate din registru (rămân cum le-ai lăsat). */
    /* 26 oct. 2026 (cererea lui Alex): la deschiderea chatului / a unui DM, cursorul direct în
       câmpul de scris, ca la WhatsApp Desktop — DOAR cu mouse + tastatură (detecție de suport):
       pe ecran tactil s-ar deschide tastatura la fiecare conversație și ar acoperi jumătate din
       ea. Câmp ascuns (DM cu relație blocată) sau alt câmp deja în lucru ⇒ nimic.
       EN: focus the composer on open, pointer-fine devices only. */
    function focusComposer(el) {
        try {
            if (!el || el.disabled || !el.getClientRects().length) return;
            if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) return;
            var a = document.activeElement;
            if (a && a !== el && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return;
            setTimeout(function () {
                try { if (el.getClientRects().length) el.focus({ preventScroll: true }); } catch (e) { /* noop */ }
            }, 60);
        } catch (e) { logErr('TO_CHAT.focus', e); }
    }
    TO_CHAT.focusComposer = focusComposer;
    var clubShownPrev = false;
    function syncClubLayers() {
        try {
            var card = document.getElementById('chatCard'), tab = document.getElementById('tab-chat');
            var full = isFs(card), shown = full || !!(tab && tab.classList.contains('active'));
            if (shown && !clubShownPrev) focusComposer(document.getElementById('chatInput'));
            clubShownPrev = shown;
            if (full) backOpen('chat-fs', exitFs); else backDone('chat-fs');
            var s = searchEls();
            if (shown && s.inp && searchIsOpen(s.inp)) backOpen('chat-search', closeClubSearch); else backDone('chat-search');
            /* Plecat din chat cu căutarea deschisă ⇒ se închide (altfel, la întoarcere, ar fi
               cerut o intrare nouă în istoric fără atingere — vezi mai sus de ce nu se poate). */
            if (!shown && s.inp && searchIsOpen(s.inp)) closeClubSearch();
            /* 8.3: la fel foaia de emoji a clubului. */
            if (!shown && window.TO_EMOJI && window.TO_EMOJI.isOpen && window.TO_EMOJI.isOpen('club')) window.TO_EMOJI.close();
        } catch (e) { logErr('TO_CHAT.back', e); }
    }
    function bindClubLayers() {
        var card = document.getElementById('chatCard'), tab = document.getElementById('tab-chat');
        if (!card || card.__toLayers || typeof MutationObserver !== 'function') return;
        card.__toLayers = true;
        try {
            var mo = new MutationObserver(syncClubLayers);
            mo.observe(card, { attributes: true, attributeFilter: ['class'] });
            if (tab) mo.observe(tab, { attributes: true, attributeFilter: ['class'] });
        } catch (e) { logErr('TO_CHAT.back', e); }
    }
    function bindClub() {
        try {
            cprefEarly();                                             /* 8.8-b: aspectul de la început */
            cprefWarm(3000);                                          /* 8.3-c: citit devreme, în timp liber */
            rxWarm(6000);                                             /* 29 oct.: reacțiile, preîncărcate în timp liber */
            bindClubHead();
            bindClubLayers();                                         /* 8.1-d */
            bindOverlayLayers();                                      /* 8.1-d: poza mărită, profil… */
            bindClubSearch();
            bindClubFill();
            bindClubUnread();
            bindClubProfiles(document.getElementById('chatMessages'));
            bindClubRowActions(document.getElementById('chatMessages'));   /* 8.13-1 */
            bindClubOlder(document.getElementById('chatMessages'));   /* 8.1-c3a */
            bindClubRetry(document.getElementById('chatMessages'));   /* 8.1-s */
            attach(document.getElementById('chatInput'), {
                send: function () { if (typeof window.sendMessage === 'function') window.sendMessage(); },
                list: document.getElementById('chatMessages'),
                mentions: true                                       /* 8.1-f: lista @ */
            });
            bindMore(document.getElementById('chatMessages'));
        } catch (e) { logErr('TO_CHAT.bindClub', e); }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindClub);
    else bindClub();
})();
